// Mock / fake API. In-memory, no network, no backend. Every method returns a
// Promise with a little simulated latency so screens exercise real loading
// states. A live ticker drives the power-flow dashboard so the app feels alive.
//
// This is the ONLY place that fabricates data. To go live, implement the same
// ApiClient surface (see ./index.ts) against the real cloud REST/MQTT API and
// swap the export — no screen changes required.
import type {
  ApiClient, LoginResult,
} from './index'
import type {
  Adm, AdmDecision, AdmReview, AdminAccountSummary, AdminAuditEvent, AdminDeviceSummary, AdminSiteSummary, Authority, BackstopStatus, BackstopWindow, Battery, BatteryPack, Behaviour, CommandsStatus, CommissioningStep, ComplianceRecord, Consent, ConsentLogEntry, DerStatusReport, DerStatusStatus, Device, DeviceKind,
  CommandAuthority, CommandHistoryEntry, CommandReason, CommandStatus, CommandValue, CommandView, ControlTier,
  DeviceCommandRequest, DeviceCommandSubmission, DeviceControl, EconomicRevisionView, EconomicSettings, EconomicSettingsPatch, HomeownerControl,
  DiagnosticRegister, DocContent, DocMeta, EnergyPeriod, EnergySummary, FirmwareCampaignAbort, FirmwareCampaignView, FirmwareInfo, FleetAssociation, FleetSite, GridJurisdiction, HandoverResult, HwsDeclaration, JurisdictionProfile,
  RegionProfileView, UpdateJobView, UpdatePlan, UpdateState,
  DemandCharge, InstallerApplication, InstallerOrg, InstallerOrganisation, InstallerOrganisationView, Inverter, InventoryKind, InventoryUnit, InventoryValidation, CorrectionResult, DeclarationResult, Membership, Mode, AppNotification, NotificationPrefs, OrgAuditEvent, OrgMembership, OrgRole, Ownership, PairingStatus, PowerFlow, RecordedEnergyDay, SetpointCommand, Site, SupportOverview, Tariff, TariffPeriod, TariffTier, TariffType, TelemetryFrame, TelemetryStatus, TouSlot, UiPrefs, User, Warranty, Weather,
  VppCohort, VppDispatchRequest, VppEvent, VppFleetSite, VppOutcome, VppPartnerView, VppReasonCode, VppSiteResult, VppSiteStatus, VppTokenResponse, VppWebhookDelivery,
} from './types'
import { getDocument as libGetDocument, listDocuments as libListDocuments } from './documents'
import type { BleClient } from './bleClient'
import { createBleClient } from './bleClient'
import { createMockDongle } from './mockDongle'
import { timeZoneOffsetMinutes, zonedDayBounds } from '../lib/timeZone'
import {
  batteryPacksFromBattery, seedAdmDecisions, seedAuthority, seedBattery, seedCommissioning, seedCompliance, seedConsentLog, seedConsents,
  seedDevices, seedDiagnostics, seedFirmware, seedInverter, seedModes,
  seedNotifications, seedNotificationPrefs, seedOwnership, seedResidenceMemberships, seedSite, seedSiteRecord, seedTariff, seedTou, seedUiPrefs, seedUser, seedWarranty, seedWeather, seedZoneDemoResidences,
} from './mockData'
import type { MockResidenceRecord } from './mockData'

// The fault list the mock's synthesised device frame reports — ONE source for
// getTelemetry (frame.status.faults) and supportOverview's live∪seeded union, so
// the two can never disagree. The sim device is healthy: empty by design.
const SIM_FRAME_FAULTS: readonly string[] = []

const latency = () => 120 + Math.random() * 260
const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(clone(value)), latency()))
const clone = <T>(v: T): T => (typeof v === 'object' && v !== null ? structuredClone(v) : v)

// ---------------------------------------------------------------------------
// THE MOCK'S CLOCK — injectable, so a demo can keep the REAL timings
// ---------------------------------------------------------------------------
//
// The device-command TTL used to be four seconds in this file against five
// minutes in `cloud/src/deviceCommands.ts`, purely so an E2E could watch a
// command expire without waiting. That is a shortened value a homeowner SEES:
// the demo told them "your device did not come online before this change timed
// out" four seconds after they pressed the switch, which is not what the product
// does and is not a claim the app should ever make early.
//
// So the constant now matches the cloud and the CLOCK is what moves. Every
// lifecycle read/write asks `mockNow()` rather than `Date.now()`, and a test can
// push that forward. Nothing else in the app can: the handle is installed only in
// a REVIEW build (the same `VITE_SHOW_SCREEN_INDEX` gate the router uses for its
// prototype surfaces), so a normal build has no way to skew the mock's time.
let mockClockSkewMs = 0
/** The mock's "now". Wall clock plus whatever a test has fast-forwarded. */
const mockNow = (): number => Date.now() + mockClockSkewMs
/** Move the mock's clock FORWARD by `ms` (never backwards — a command that has
 *  already expired cannot un-expire). Returns the new mock time. */
export function advanceMockClock(ms: number): number {
  mockClockSkewMs += Math.max(0, ms)
  return mockNow()
}
/** Put the clock back on the wall clock. */
export function resetMockClock(): void {
  mockClockSkewMs = 0
}

// ---------------------------------------------------------------------------
// THE MOCK'S SITE CLOCK — the RESIDENCE's zone, never the phone's
// ---------------------------------------------------------------------------
//
// Everything in this file that turns an INSTANT into an hour-of-day — the
// effective mode, the DNSP slot ladder, the live power-flow ticker — used to ask
// `new Date().getHours()`, which is the HOST's timezone: whatever the phone, the
// laptop or the CI container happens to be set to. That is the same defect class
// as the fixed +10 that used to default inside `shared/dispatch-model.ts`, only
// with a different wrong answer: a 15:00–21:00 discharge window is stated in the
// RESIDENCE's wall clock (INV-MOD-02), so reading it against a browser in London
// or a CI box on UTC opens it ten hours out. The demo would then show a battery
// discharging at 4 am.
//
// This mock is not zone-less, so there is nothing to apologise for: the seeded
// Residence STATES its zone (`seedSite.timeZone`, 'Australia/Sydney' — the same
// field the real cloud resolves through `siteClock.siteClockForSite`), and
// `GET /energy/recorded-day` in this very file already buckets by it. The two
// clocks in one mock were the inconsistency. Every hour-of-day below now comes
// from here, so the offline demo behaves identically wherever it is opened.
//
// The INSTANT is still the host's (`Date.now()`) — that is correct and not an
// assumption: an epoch-ms instant is absolute and zone-free. It is only the
// INTERPRETATION of that instant as a wall clock that needs a zone, and this is
// the one place the mock states which.
const MOCK_SITE_TIME_ZONE: string = seedSite.timeZone

// ---------------------------------------------------------------------------
// ZONE RESOLUTION AND ITS PROVENANCE — mirror of cloud/src/siteClock.ts
// ---------------------------------------------------------------------------
//
// The cloud never defaults a Residence zone silently: it resolves one from the
// STORED record and reports how (`Site.timeZoneSource`, `GET /economic-settings`).
// The mock hard-coded `timeZoneSource: 'residence'` on one fixture and sent none
// at all on economic settings, so three of the four provenances could not exist
// here — and a screen that renders a guessed zone as a stated one would pass every
// hermetic test. This is the resolution, reproduced rather than approximated.
//
// SIMPLIFIED IN ONE PLACE ONLY: the cloud's address derivation also arbitrates
// postcode ranges and single-postcode zones (Lord Howe, Broken Hill). The mock
// scans the state token, which is all its fixtures need; if a fixture ever needs
// a postcode case, port `timeZoneFromAddressOrNull` rather than widening this.
const MOCK_FALLBACK_TIME_ZONE = 'Australia/Sydney'
const MOCK_STATE_ZONES: Record<string, string> = {
  NSW: 'Australia/Sydney', ACT: 'Australia/Sydney', VIC: 'Australia/Melbourne',
  TAS: 'Australia/Hobart', QLD: 'Australia/Brisbane', SA: 'Australia/Adelaide',
  NT: 'Australia/Darwin', WA: 'Australia/Perth',
}
const MOCK_STATE_TOKEN = /\b(NSW|ACT|VIC|TAS|QLD|SA|NT|WA)\b/i
/** `Region/City`, and never the sign-inverted `Etc/` namespace — `Etc/GMT+10` is
 *  UTC MINUS ten, so accepting it would run a Queensland home twenty hours out. */
const MOCK_IANA_SHAPE = /^[A-Za-z][A-Za-z_-]*(?:\/[A-Za-z0-9_+-]+){1,2}$/

function mockKnownTimeZone(timeZone: string | null | undefined): boolean {
  if (!timeZone || !MOCK_IANA_SHAPE.test(timeZone) || /^etc\//i.test(timeZone)) return false
  try {
    new Intl.DateTimeFormat('en-AU', { timeZone })
    return true
  } catch {
    return false
  }
}

function mockZoneFromAddress(address: string): string | null {
  const token = MOCK_STATE_TOKEN.exec(address ?? '')
  return token ? MOCK_STATE_ZONES[token[1].toUpperCase()] ?? null : null
}

/** The resolution, WITH the source that produced it. Order and outcomes match
 *  `resolveSiteTimeZone`: stated-and-known → `residence`; stated-but-unusable →
 *  `invalid` (the address is still better evidence than nothing, but the record's
 *  own defect is what gets reported); derived → `address`; nothing → `fallback`. */
function resolveMockSiteTimeZone(record: { statedTimeZone?: string; address?: string }): {
  timeZone: string
  source: NonNullable<Site['timeZoneSource']>
} {
  const stated = record.statedTimeZone?.trim() || null
  if (stated && mockKnownTimeZone(stated)) return { timeZone: stated, source: 'residence' }
  const fromAddress = mockZoneFromAddress(record.address ?? '')
  if (stated) return { timeZone: fromAddress ?? MOCK_FALLBACK_TIME_ZONE, source: 'invalid' }
  if (fromAddress) return { timeZone: fromAddress, source: 'address' }
  return { timeZone: MOCK_FALLBACK_TIME_ZONE, source: 'fallback' }
}

/** The Residence record the SIGNED-IN identity's session resolves to — the mock's
 *  stand-in for the cloud reading the session's residenceId. `null` is a real
 *  answer (an account with no home paired yet), not an absent fixture, and every
 *  caller must treat it as one rather than substituting the demo Residence. */
function currentMockResidence(): MockResidenceRecord | null {
  if (mockSessionSiteId === MOCK_HOLIDAY_SITE_ID) return mockHolidaySiteRecord
  return currentEmail in seedZoneDemoResidences ? seedZoneDemoResidences[currentEmail] : seedSiteRecord
}

/** Second demo home for multi-Residence shell (parity with cloud ensureDemoMultiResidence). */
const MOCK_HOLIDAY_SITE_ID = 'site-multi-b'
const mockHolidaySiteRecord: MockResidenceRecord = {
  id: MOCK_HOLIDAY_SITE_ID,
  name: 'Holiday house',
  nmi: '4001 9876 543',
  address: '2 Lawson St, Byron Bay NSW 2481',
  dnsp: 'Essential Energy',
  statedTimeZone: 'Australia/Sydney',
}

/** `GET /site` for the current session, resolved exactly as the cloud does. */
function currentMockSite(): Site | null {
  const record = currentMockResidence()
  if (!record) return null
  const zone = resolveMockSiteTimeZone(record)
  return {
    ...seedSite,
    id: record.id,
    name: record.name,
    nmi: record.nmi,
    address: record.address,
    dnsp: record.dnsp,
    timeZone: zone.timeZone,
    timeZoneSource: zone.source,
  }
}

/** Hour-of-day in [0,24) at the RESIDENCE for an epoch-ms instant. Mirrors
 *  `hourOfDay(nowMs, clock)` in `shared/dispatch-model.ts`, resolved through the
 *  Residence's IANA zone (so it follows daylight saving, and a half-hour zone
 *  works) rather than through a fixed offset. */
function siteHourOfDay(nowMs: number = Date.now()): number {
  const shifted = nowMs + timeZoneOffsetMinutes(nowMs, MOCK_SITE_TIME_ZONE) * 60_000
  return (((shifted % 86_400_000) + 86_400_000) % 86_400_000) / 3_600_000
}

/** The UTC instant of the Residence-local midnight opening the local day that
 *  contains `nowMs` — the mock's equivalent of `localDayStartMs`. Resolved twice
 *  because a DST transition inside the day means the offset at midnight is not
 *  the offset now. */
/** An instant rendered as a wall-clock time AT THE RESIDENCE. The one formatter
 *  for every "6:12 pm" this mock puts in front of a homeowner. */
function clockAtSite(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-AU', { timeZone: MOCK_SITE_TIME_ZONE, hour: 'numeric', minute: '2-digit' })
}

function siteDayStartMs(nowMs: number = Date.now()): number {
  const offsetMs = timeZoneOffsetMinutes(nowMs, MOCK_SITE_TIME_ZONE) * 60_000
  const localMidnight = Math.floor((nowMs + offsetMs) / 86_400_000) * 86_400_000
  const utc = localMidnight - offsetMs
  const offsetThenMs = timeZoneOffsetMinutes(utc, MOCK_SITE_TIME_ZONE) * 60_000
  return offsetThenMs === offsetMs ? utc : localMidnight - offsetThenMs
}
if (typeof window !== 'undefined' && (import.meta.env.DEV || import.meta.env.VITE_SHOW_SCREEN_INDEX === 'true')) {
  ;(window as unknown as { __riseMockClock?: unknown }).__riseMockClock = {
    advance: advanceMockClock,
    reset: resetMockClock,
    now: mockNow,
  }
}

// ---- mutable in-memory state (so setters "stick" for the session) ----
let currentUser: User | null = null
// Set by signOut(); suppresses the lazy default identity in getCurrentUser until
// the next explicit login (mirrors the HTTP client's signed-out suppression).
let signedOut = false
const battery: Battery = structuredClone(seedBattery)
const modes: Mode[] = structuredClone(seedModes)
const tariff: Tariff = structuredClone(seedTariff)
let schedule: TouSlot[] = structuredClone(seedTou)
// Behaviour toggles (APP-HOM-20) — default OFF, mirroring the cloud seed so the
// baseline simulation is identical until the user turns them on.
const behaviour: Behaviour = { zeroExport: false, peakShave: false, batteryHold: false }
// Notification prefs (APP-HOM-08) — mutable so toggles stick for the session.
let notificationPrefs: NotificationPrefs = structuredClone(seedNotificationPrefs)
// Account-synced appearance/Home prefs (APP-PLT-08 / APP-HOM-23). The mock keeps
// a local copy across a full browser reload so it mirrors the live cloud rather
// than snapping the selected theme back during navigation-heavy E2E checks.
const MOCK_UI_PREFS_KEY = 'risen_mock_ui_prefs'
const storedUiPrefs = (): UiPrefs => {
  try { return JSON.parse(localStorage.getItem(MOCK_UI_PREFS_KEY) ?? '{}') as UiPrefs }
  catch { return {} }
}
let uiPrefs: UiPrefs = { ...structuredClone(seedUiPrefs), ...storedUiPrefs() }
// Ownership (APP-OWN-05/09/10/11) — mutable so the demo state machine sticks.
let ownership: Ownership = structuredClone(seedOwnership)
let pairing: PairingStatus = {
  residenceId: seedSite.id,
  deviceId: `rsn-pcu-${seedSite.id}`,
  state: 'homeowner_paired',
  primaryAccount: { id: seedUser.id, name: seedUser.name, role: 'homeowner' },
  deviceRevision: 1,
  cloudKnownRevision: 1,
  source: 'local_ble_pair',
  updatedAt: Date.now(),
  energyVisibleFrom: Date.now(),
  members: [],
}
// Phase-13: consents/warranty/consent-log are mutable so the APP-PAIR-07 transfer
// side-effects (reset + re-register) are observable; HWS declaration (APP-PAIR-16).
let consents: Consent[] = structuredClone(seedConsents)
let warranty: Warranty = structuredClone(seedWarranty)
let consentLog: ConsentLogEntry[] = structuredClone(seedConsentLog)
const CONSENT_LOG_CAP = 50
let hwsDeclaration: HwsDeclaration | null = null
// Phase-2 stateful flows (devices, commissioning, firmware, notifications) — were
// served straight from the seed; now mutable so pairing/advancing/updating/read stick.
let devices: Device[] = structuredClone(seedDevices)
let commissioning: CommissioningStep[] = structuredClone(seedCommissioning)
let compliance: ComplianceRecord = structuredClone(seedCompliance)
let firmware: FirmwareInfo[] = structuredClone(seedFirmware)
let notifications: AppNotification[] = structuredClone(seedNotifications)
/** APP-HOM-12 demo enquiries (in-memory). */
let supportEnquiries: import('./types').SupportEnquiry[] = []
// ADM human-review cases (APP-HOM-22) — mutable so a requested review sticks.
// Mirrors the cloud store: retained decision records + a review-case map, merged.
const admReviews: Record<string, AdmReview> = {}
// Phase-11: a demo-triggerable emergency backstop window (GRI-BST-01/02/03) + the
// LIVE surfaces it drives — an ADM record (prepended to the retained ones) and a
// DERStatus evidence log (GRI-CSP-02 return). Mirror of the cloud backstop.ts.
let backstopWindow: BackstopWindow | null = null
let liveAdm: AdmDecision[] = []
let derStatusLog: DerStatusReport[] = []
function admCaseRef(id: string): string {
  let h = 0
  for (const c of id) h = (Math.imul(h, 31) + c.charCodeAt(0)) >>> 0
  return `TS-${100000 + (h % 900000)}`
}
function admPayload(): Adm {
  // Live decisions (e.g. a backstop curtailment, GRI-BST-03) prepend the retained
  // seed records so the newest real event features first.
  return {
    decisions: [...liveAdm, ...seedAdmDecisions].map((d) => (admReviews[d.id] ? { ...d, review: admReviews[d.id] } : d)),
  }
}

// ---- Installer Residence memberships, keyed by e-mail ----
// The mock is single-process but login(email) runs per session, so a tiny
// multi-identity world demonstrates the whole flow in ONE browser session: log in
// as the installer → create/transfer → log out → log in as the buyer e-mail → see
// the claim → accept. Mirrors cloud/src/residences.ts + the membership store.
let mockMemberships: Record<string, Membership[]> = {}
let mockResidences: Record<string, {
  label: string; address: string; nmi?: string; suburb?: string
  statusHint?: 'healthy' | 'attention' | 'fault'
  syncStatus?: 'provisional' | 'synced' | 'conflict'
  /** Which outstanding firmware update this Residence carries — the fixture that
   *  makes the per-Residence commissioning gate (APP-INS-05A/05B) observable. */
  outstandingUpdate?: 'commissioning-blocking' | 'post-commissioning' | 'none'
}> = {}
let currentEmail = (seedUser.email || '').toLowerCase().trim()
/** Active Residence for the mock session (Stage 4.1 multi-res switcher). */
let mockSessionSiteId = seedSite.id
let residenceSeq = 0
// Emails that have opted into 2FA (APP-ACC-07) — so mock login knows to challenge.
const twoFactorEmails = new Set<string>()
const normEmail = (e: string) => (e || '').toLowerCase().trim()
/** The signed-in identity's userId (mirror of the cloud session userId). */
const uid = (): string => `u-${currentEmail}`

// ---- installer identity (APP-ACC-09 / CLD-PRT-09), keyed by e-mail ----
// Mirrors the cloud: roles are server-owned; homeowner@example.com and
// installer@example.com are the canonical customer personas, ops@example.com is
// the back-office operator, and everyone else starts as a homeowner. The queue
// survives sign-out within the session so one browser can play applicant → admin
// → applicant and watch the role change.
const INSTALLER_EMAIL = 'installer@example.com'
const ADMIN_EMAIL = 'ops@example.com'
const ADMIN_APPROVER_EMAIL = 'approver@example.com'
const SUPPORT_EMAIL = 'support@example.com'
const RETIRED_DEMO_EMAILS = new Set(['you@example.com', 'admin@example.com'])
const installerApps: Record<string, InstallerApplication> = {}
const adminAccountOverrides: Record<string, Partial<User>> = {}
let adminAudit: AdminAuditEvent[] = []

/** The mock's identity overlay — the single place a role is decided. */
const withMockWorkspace = (user: User): User => user.role === 'installer'
  ? { ...user, workspaces: ['installer'], defaultWorkspace: 'installer' }
  : user.role === 'homeowner'
    ? { ...user, workspaces: ['homeowner'], defaultWorkspace: 'homeowner' }
    : { ...user, workspaces: [], defaultWorkspace: undefined }

function userFor(addr: string, name?: string): User {
  const norm = normEmail(addr)
  const base: User = { ...seedUser, email: addr, ...(name ? { name } : {}) }
  if (!norm || norm === normEmail(seedUser.email)) return withMockWorkspace({ ...base, role: 'homeowner' })
  if (norm === INSTALLER_EMAIL)
    return withMockWorkspace({
      ...base, id: 'u-installer', name: name ?? 'Sam Installer', role: 'installer', installerStatus: 'approved', workspaces: ['installer'], defaultWorkspace: 'installer',
      installerOrg: { company: 'ABC Electrical', abn: '12 345 678 901', accreditation: 'SAA-A1023456' },
      ...adminAccountOverrides[norm],
    })
  if (norm === ADMIN_EMAIL) return withMockWorkspace({ ...base, id: 'u-admin', role: 'admin', ...(name ? {} : { name: 'Risen Support Operations' }) })
  if (norm === ADMIN_APPROVER_EMAIL) return withMockWorkspace({ ...base, id: 'u-admin-approver', role: 'admin', ...(name ? {} : { name: 'Risen Ownership Approver' }) })
  if (norm === SUPPORT_EMAIL) return withMockWorkspace({ ...base, id: 'u-support', role: 'support', ...(name ? {} : { name: 'Risen Customer Support' }) })
  const app = installerApps[norm]
  if (app?.status === 'approved')
    return withMockWorkspace({ ...base, role: 'installer', installerStatus: 'approved', installerOrg: app.org, ...adminAccountOverrides[norm] })
  if (app) return withMockWorkspace({ ...base, role: 'homeowner', installerStatus: app.status, installerOrg: app.org, ...adminAccountOverrides[norm] })
  return withMockWorkspace({ ...base, role: 'homeowner', ...adminAccountOverrides[norm] })
}
/** Account-level installer gate — mirror of the cloud's requireInstallerAccount
 *  choke point (handler.ts). Enforced in the mock too so Tier A e2e can catch a
 *  regression that drops the server gate (a homeowner reaching installer data). */
function requireInstallerRole(): void {
  const role = (currentUser ?? userFor(seedUser.email)).role
  if (role !== 'installer')
    throw new Error('403 Installer access has not been granted for this account')
}
/** Mirror of the cloud handler's requireRole choke point (roles are server-owned,
 *  SYS-SW-07) — the ONE gate behind the mock's admin + support endpoints. */
function requireRole(role: 'admin' | 'support', msg: string): void {
  if ((currentUser ?? userFor(seedUser.email)).role !== role) throw new Error(`403 ${msg}`)
}

// ---------------------------------------------------------------------------
// THE ACCOUNT-WORKSPACE CHOKE POINT (APP-ACC-10) — mirrored, not approximated
// ---------------------------------------------------------------------------
//
// `cloud/src/handler.ts` runs `canAccessAppPath(profile, path)` BEFORE any route
// handler, so a professional token cannot inherit the customer API. The mock had
// role gates (requireInstallerRole/requireRole) but no path gate, which meant a
// whole class of call — an installer reading `/updates`, `/devices` or the 2FA
// endpoints — succeeded in Tier A and 403s in Tier S/B. That is the exact failure
// mode this file's header claims not to have: a screen can pass here on semantics
// the cloud refuses.
//
// Kept as a LITERAL port of cloud/src/workspace.ts. If that file changes, this one
// changes with it; anything else is the divergence coming back.
// `/security` is an ACCOUNT surface, not a workspace one — two-step verification is
// enrolled on the signed-in identity, and staff MFA is mandatory — so every role
// reaches it. `/updates` and `/devices` are commissioning surfaces the installer
// half of the firmware/commissioning flow depends on; the real gate on them is the
// per-site one the route runs (`requireMembership` / `requireInstallerFull`), which
// is why they are addressed with `?site=` rather than refused here.
const SHARED_WORKSPACE_PATHS = ['/auth/me', '/documents', '/notifications', '/notification-prefs', '/ui-prefs', '/security']
const hasPathPrefix = (path: string, prefix: string): boolean => path === prefix || path.startsWith(`${prefix}/`)

function canAccessAppPath(role: User['role'], path: string): boolean {
  if (role === 'homeowner') return true
  if (SHARED_WORKSPACE_PATHS.some((prefix) => hasPathPrefix(path, prefix))) return true
  if (role === 'installer')
    return hasPathPrefix(path, '/installer') || hasPathPrefix(path, '/pairing')
      || hasPathPrefix(path, '/updates') || hasPathPrefix(path, '/devices')
  if (role === 'admin') return hasPathPrefix(path, '/admin') || path === '/pairing' || path === '/pairing/force-detach'
  return role === 'support' && (hasPathPrefix(path, '/support') || path === '/pairing' || path === '/pairing/force-detach')
}

/** The mock's stand-in for the cloud handler's pre-route workspace check. Each
 *  call site names the REAL path the HTTP adapter would use (see httpApi.ts), so
 *  the two adapters answer the same question about the same string. */
function assertWorkspace(path: string): void {
  const role = (currentUser ?? userFor(seedUser.email)).role
  if (!canAccessAppPath(role, path))
    throw new Error('403 This endpoint is outside your account workspace')
}

/** The ownership approver is an admin who may DECIDE ownership transfers and may
 *  NOT change account capabilities — mirror of the cloud's requireAccountOperator
 *  (handler.ts), which is a second gate behind requireAdmin, not the same one. */
function requireAccountOperator(msg: string): void {
  requireRole('admin', msg)
  if ((currentUser ?? userFor(seedUser.email)).id === 'u-admin-approver')
    throw new Error('403 Ownership approvers cannot change account capabilities')
}
const newMockSiteId = () => `s-r${(++residenceSeq).toString(36).padStart(6, '0')}`

function mockAdminAccounts(query = ''): AdminAccountSummary[] {
  const emails = new Set([normEmail(seedUser.email), ...Object.keys(installerApps)])
  const q = query.trim().toLowerCase()
  return [...emails].map((email) => {
    const u = { ...userFor(email), ...adminAccountOverrides[email] }
    return {
      id: `u-${email}`, name: u.name, email,
      accountStatus: u.accountStatus ?? 'active',
      installerStatus: u.installerStatus ?? (u.role === 'installer' ? 'approved' : 'none'),
      ...(u.installerOrg ? { installerOrg: u.installerOrg } : {}),
      siteCount: (mockMemberships[email] ?? []).length,
    }
  }).filter((a) => q.length < 2 || [a.id, a.name, a.email, a.installerOrg?.company].some((v) => v?.toLowerCase().includes(q)))
}

function pushAdminAudit(row: Omit<AdminAuditEvent, 'id' | 'at'>): AdminAuditEvent {
  const event: AdminAuditEvent = { ...row, id: `audit-${Date.now()}-${adminAudit.length}`, at: Date.now() }
  adminAudit = [event, ...adminAudit].slice(0, 100)
  return event
}

// Mirror of cloud/src/residences.ts — keep in sync.
const resNewMembership = (
  userId: string, siteId: string, role: Membership['role'], access: Membership['access'],
  label: string, since: string, suburb?: string, statusHint?: Membership['statusHint'],
): Membership => ({ siteId, userId, role, access, label, suburb, statusHint, since })
const resFleetRow = (m: Membership, socPct?: number): FleetSite => {
  const full = m.role !== 'member' && m.access === 'full'
  return {
    id: m.siteId, name: m.label, suburb: m.suburb ?? '—', status: m.statusHint ?? 'healthy',
    socPct: full ? socPct : undefined, role: full ? 'owner' : 'health-only', lastSeen: '2 s ago',
  }
}
const resStatusOnly = (online: boolean): DiagnosticRegister[] => [
  { group: 'Status', rows: [{ label: 'Device', value: online ? 'Online' : 'Fault', tone: online ? 'ok' : 'fault' }] },
]
/** The current identity's installer memberships, lazily seeding a demo fleet of
 *  real residences on first view (so every row drives unpair/transfer). */
function installerMemberships(): Membership[] {
  const mine = mockMemberships[currentEmail] ?? (mockMemberships[currentEmail] = [])
  if (!mine.some((m) => m.role === 'installer')) {
    for (const r of seedResidenceMemberships) {
      const siteId = newMockSiteId()
      mockResidences[siteId] = { label: r.label, address: r.address, suburb: r.suburb, statusHint: r.statusHint, outstandingUpdate: r.outstandingUpdate }
      mine.push(resNewMembership(`u-${currentEmail}`, siteId, 'installer', r.access, r.label, '03/07/2026', r.suburb, r.statusHint))
    }
  }
  return mine.filter((m) => m.role === 'installer')
}
/** The fleet a practitioner sees: the union of what the PERSON holds (their
 *  Residence memberships) and what their ORGANISATION retains (APP-OWN-06B) —
 *  mirror of the cloud's `fleetRows`. The person's row wins where both exist,
 *  because only it can carry commissioning detail; an organisation-only row is
 *  basic status by construction. Deduped by Residence. */
function mockFleet(): FleetSite[] {
  const soc = ticker.snapshot().soc
  const own = installerMemberships().map((m) => ({
    ...resFleetRow(m, m.access === 'full' ? soc : undefined),
    syncStatus: mockResidences[m.siteId]?.syncStatus ?? 'synced',
  }))
  const held = new Set(own.map((r) => r.id))
  const retained = retainedFleetForCurrent().filter((a) => !held.has(a.residenceId))
  return [
    ...own,
    ...retained.map((a): FleetSite => ({
      id: a.residenceId, name: a.label, suburb: a.suburb, status: 'healthy', role: 'health-only', lastSeen: '2 s ago',
    })),
  ]
}

// ---- Installer Organisation, shipment inventory and retained fleet ----
// (APP-ACC-08–08L, APP-OWN-18/18A). Mirror of cloud/src/installerOrgs.ts and the
// organisation half of cloud/src/store.ts — reproduced, not approximated, so a UI
// built against the mock cannot pass on semantics the cloud refuses:
//   • a serial belongs to at most ONE organisation; entering one held elsewhere
//     changes nothing and names the holder + the re-assignment path (APP-ACC-08D);
//   • correction and withdrawal are MANAGER-only (APP-ACC-08J), and a withdrawn
//     row leaves the ACTIVE inventory but is retained as history (APP-ACC-08K);
//   • a practitioner in more than one organisation must name the acting one —
//     the mock refuses with the cloud's 400 rather than guessing (APP-ACC-08L);
//   • revoking a practitioner ends the PERSON's access and leaves the
//     ORGANISATION's inventory intact (APP-ACC-08I).

/** `[x]` The product serial format is a PDR-open value; this is the cloud's
 *  current predicate verbatim (cloud/src/installerOrgs.ts SERIAL_FORMAT). */
const SERIAL_FORMAT = /^[A-Z]{2,4}(-[A-Z0-9]{1,6}){2,4}$/
const normalizeSerial = (raw: string): string => (raw ?? '').trim().toUpperCase().replace(/\s+/g, '')
const serialFormatValid = (serial: string): boolean => SERIAL_FORMAT.test(normalizeSerial(serial))
function classifySerial(serial: string): InventoryKind | null {
  const s = normalizeSerial(serial)
  if (/^RSN-B-/.test(s)) return 'battery'
  if (/^INV-/.test(s)) return 'inverter'
  if (/^(GW|DNG)-/.test(s)) return 'dongle'
  return null
}
/** The re-assignment path named in every `assigned-elsewhere` reply — the literal
 *  text the cloud returns (CLD-PRT-01E/01E1); kept verbatim so the screen copy is
 *  the server's words, not the client's paraphrase. */
const REASSIGNMENT_PATH =
  'This serial is assigned to another Installer Organisation. It moves only through an authorized Support or Admin re-assignment with a recorded reason, or the holding organisation withdrawing it.'
/** Deterministic organisation id from the company name — the cloud's legacy
 *  bridge, so an account and its colleagues converge on the SAME organisation. */
function organisationIdFor(org: InstallerOrg | undefined, fallbackUserId: string): string {
  const slug = (org?.company ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  return slug ? `org-${slug.slice(0, 40)}` : `org-u-${fallbackUserId}`
}

interface MockOrgFleetAssociation {
  organisationId: string
  residenceId: string
  state: 'active' | 'ended'
  origin: FleetAssociation['origin']
  establishedBy: string
  label: string
  suburb: string
  sinceMs: number
}

const RIVAL_ORG: InstallerOrg = { company: 'Southside Solar Pty Ltd', abn: '98 765 432 109', accreditation: 'SAA-A3311220' }
/** The organisation that installed the demo homeowner's system — the same company
 *  the seeded ownership record names, so the homeowner's "who can see my system?"
 *  view and their installer record agree. */
const OWNER_INSTALLER_ORG: InstallerOrg = { company: seedOwnership.installer.org, abn: '55 111 222 333', accreditation: 'SAA-A7788990' }
const ABC_ORG_ID = organisationIdFor({ company: 'ABC Electrical' }, 'seed')
const RIVAL_ORG_ID = organisationIdFor(RIVAL_ORG, 'seed')
const OWNER_INSTALLER_ORG_ID = organisationIdFor(OWNER_INSTALLER_ORG, 'seed')
const DAY_MS = 86_400_000

let mockOrganisations: Record<string, InstallerOrganisation> = {}
let mockOrgMemberships: OrgMembership[] = []
/** The serial REGISTRY: one row per serial, and the arbiter of who holds it. */
let mockInventory: Record<string, InventoryUnit> = {}
let mockOrgAudit: OrgAuditEvent[] = []
let mockOrgFleet: MockOrgFleetAssociation[] = []
/** The acting organisation this client has chosen (APP-ACC-08L). Client-side, like
 *  the HTTP adapter's localStorage entry — never a server resource. */
let actingOrganisation: string | null = null
let orgAuditSeq = 0

function seedOrgWorld(): void {
  const now = Date.now()
  mockOrganisations = {
    [ABC_ORG_ID]: { organisationId: ABC_ORG_ID, company: 'ABC Electrical', abn: '12 345 678 901', accreditation: 'SAA-A1023456', createdAtMs: now - 400 * DAY_MS },
    [RIVAL_ORG_ID]: { organisationId: RIVAL_ORG_ID, company: RIVAL_ORG.company, abn: RIVAL_ORG.abn ?? null, accreditation: RIVAL_ORG.accreditation ?? null, createdAtMs: now - 300 * DAY_MS },
    [OWNER_INSTALLER_ORG_ID]: { organisationId: OWNER_INSTALLER_ORG_ID, company: OWNER_INSTALLER_ORG.company, abn: OWNER_INSTALLER_ORG.abn ?? null, accreditation: OWNER_INSTALLER_ORG.accreditation ?? null, createdAtMs: now - 500 * DAY_MS },
  }
  // One colleague already on the roster, so a manager's first view is not empty.
  // The signed-in installer's own membership is materialised on first touch.
  mockOrgMemberships = [
    { organisationId: ABC_ORG_ID, userId: 'u-jordan@abc-electrical.example', role: 'practitioner', state: 'active', sinceMs: now - 120 * DAY_MS, revokedAtMs: null },
  ]
  mockInventory = {}
  for (const [serial, organisationId] of [
    ['GW-2026-880011', ABC_ORG_ID],
    ['RSN-B-4402-0071', ABC_ORG_ID],
    ['GW-2026-771204', RIVAL_ORG_ID], // held elsewhere — the APP-ACC-08D conflict case
  ] as const) {
    mockInventory[serial] = {
      serial, kind: classifySerial(serial) ?? 'dongle', organisationId, state: 'assigned',
      declaredBy: 'u-seed', declaredByRole: 'member', assignedAtMs: now - 30 * DAY_MS, withdrawnAtMs: null,
      deviceReportedSerial: null, discrepancy: false, reassignedFrom: null, reassignReason: null,
    }
  }
  mockOrgAudit = []
  orgAuditSeq = 0
  // TWO organisations hold a retained basic-status association with the demo
  // homeowner's Residence at once — concurrency is normal (SYS-FUN-17T), and this
  // is what APP-OWN-18 shows the homeowner. Neither is the installer persona's own
  // organisation: a retained row is the INSTALLING organisation's, and the demo
  // home was installed by the company the seeded ownership record names.
  mockOrgFleet = [
    { organisationId: OWNER_INSTALLER_ORG_ID, residenceId: seedSite.id, state: 'active', origin: 'handover', establishedBy: 'u-seed', label: seedSite.name, suburb: seedSite.address, sinceMs: now - 60 * DAY_MS },
    { organisationId: RIVAL_ORG_ID, residenceId: seedSite.id, state: 'active', origin: 'commissioning', establishedBy: 'u-seed', label: seedSite.name, suburb: seedSite.address, sinceMs: now - 200 * DAY_MS },
  ]
  actingOrganisation = null
}
seedOrgWorld()

function pushOrgAudit(row: Omit<OrgAuditEvent, 'id' | 'at'>): OrgAuditEvent {
  const event: OrgAuditEvent = { ...row, id: `org-audit-${++orgAuditSeq}`, at: Date.now() }
  mockOrgAudit = [event, ...mockOrgAudit].slice(0, 200)
  return event
}

/** Materialise the signed-in installer's organisation from the company on their
 *  profile (the cloud's legacy bridge) and return their membership. The first
 *  account to materialise an organisation manages it. Idempotent. */
function ensureMockOrganisation(): OrgMembership {
  const user = currentUser ?? userFor(seedUser.email)
  const organisationId = organisationIdFor(user.installerOrg, uid())
  if (!mockOrganisations[organisationId])
    mockOrganisations[organisationId] = {
      organisationId,
      company: user.installerOrg?.company?.trim() || 'Installer organisation',
      abn: user.installerOrg?.abn?.trim() || null,
      accreditation: user.installerOrg?.accreditation?.trim() || null,
      createdAtMs: Date.now(),
    }
  const existing = mockOrgMemberships.find((m) => m.organisationId === organisationId && m.userId === uid())
  if (existing?.state === 'active') return existing
  const role: OrgRole = mockOrgMemberships.some((m) => m.organisationId === organisationId && m.role === 'manager' && m.state === 'active')
    ? 'practitioner' : 'manager'
  const membership: OrgMembership = { organisationId, userId: uid(), role, state: 'active', sinceMs: Date.now(), revokedAtMs: null }
  mockOrgMemberships = [...mockOrgMemberships.filter((m) => !(m.organisationId === organisationId && m.userId === uid())), membership]
  return membership
}

const activeOrgMembershipsOf = (userId: string): OrgMembership[] =>
  mockOrgMemberships.filter((m) => m.userId === userId && m.state === 'active')

/** Resolve the organisation the caller is acting for — mirror of the cloud's
 *  `actingOrganisation`. An explicit id wins and must be one the caller belongs
 *  to; a single membership needs no choice; a multi-organisation practitioner who
 *  has named none is REFUSED (APP-ACC-08L), never guessed at. */
function mockActingOrg(requested?: string | null): string {
  requireInstallerRole()
  let memberships = activeOrgMembershipsOf(uid())
  if (memberships.length === 0) memberships = [ensureMockOrganisation()]
  const named = (requested ?? actingOrganisation ?? '').trim()
  if (named) {
    const match = memberships.find((m) => m.organisationId === named)
    if (!match) throw new Error('403 You are not a member of that Installer Organisation')
    return match.organisationId
  }
  if (memberships.length > 1)
    throw new Error('400 organisationId is required: this account is a member of more than one Installer Organisation')
  return memberships[0].organisationId
}

const isMockOrgManager = (organisationId: string): boolean =>
  activeOrgMembershipsOf(uid()).some((m) => m.organisationId === organisationId && m.role === 'manager')

/** Every unit visible to the signed-in account: the union over its ACTIVE
 *  memberships. There is no other path to an inventory row (APP-ACC-08I). */
function inventoryVisibleToCurrent(): InventoryUnit[] {
  const orgs = new Set(activeOrgMembershipsOf(uid()).map((m) => m.organisationId))
  return Object.values(mockInventory).filter((u) => orgs.has(u.organisationId))
}

/** The retained associations the caller's organisations hold (APP-OWN-06B). */
function retainedFleetForCurrent(): MockOrgFleetAssociation[] {
  const orgs = new Set(activeOrgMembershipsOf(uid()).map((m) => m.organisationId))
  return mockOrgFleet.filter((a) => a.state === 'active' && orgs.has(a.organisationId))
}

/** Record (or refresh) the organisation's retained basic-status association — the
 *  one call BOTH release paths make (APP-OWN-06B / SYS-FUN-17B). Idempotent for an
 *  already-active row. An earlier ended row may be re-activated when the org
 *  releases the Residence again (e.g. after homeowner removal then re-commission). */
function retainOrgFleetForCurrent(residenceId: string, origin: FleetAssociation['origin']): void {
  const memberships = activeOrgMembershipsOf(uid())
  const chosen = actingOrganisation
    ? memberships.find((m) => m.organisationId === actingOrganisation)
    : memberships.length === 1 ? memberships[0] : undefined
  if (!chosen) return
  if (mockOrgFleet.some((a) => a.organisationId === chosen.organisationId && a.residenceId === residenceId && a.state === 'active')) return
  const meta = mockResidences[residenceId]
  const ended = mockOrgFleet.find((a) => a.organisationId === chosen.organisationId && a.residenceId === residenceId && a.state === 'ended')
  if (ended) {
    mockOrgFleet = mockOrgFleet.map((a) => (a === ended
      ? { ...a, state: 'active' as const, origin, establishedBy: uid(), sinceMs: Date.now(),
          label: meta?.label ?? a.label, suburb: meta?.suburb ?? meta?.address ?? a.suburb }
      : a))
  } else {
    mockOrgFleet = [...mockOrgFleet, {
      organisationId: chosen.organisationId, residenceId, state: 'active', origin, establishedBy: uid(),
      label: meta?.label ?? residenceId, suburb: meta?.suburb ?? meta?.address ?? '—', sinceMs: Date.now(),
    }]
  }
  pushOrgAudit({
    actorId: uid(), actorRole: 'member', organisationId: chosen.organisationId, action: 'fleet.association.retained',
    targetType: 'fleet_association', targetId: residenceId, serials: [], reason: origin, result: 'accepted',
    detail: 'Residence identity, synchronization state and basic online/attention/fault status only',
  })
}

/** End ONE organisation's association, by the organisation itself or by the
 *  current homeowner. Touches no pairing, no Residence record, no inventory and no
 *  other organisation's row — none of those are named here (APP-OWN-18A). */
function endOrgFleet(organisationId: string, residenceId: string, byRole: 'organisation' | 'homeowner'): boolean {
  const current = mockOrgFleet.find((a) => a.organisationId === organisationId && a.residenceId === residenceId && a.state === 'active')
  if (!current) return false
  mockOrgFleet = mockOrgFleet.map((a) => (a === current ? { ...a, state: 'ended' as const } : a))
  pushOrgAudit({
    actorId: uid(), actorRole: byRole === 'homeowner' ? 'homeowner' : 'member', organisationId,
    action: 'fleet.association.removed', targetType: 'fleet_association', targetId: residenceId,
    serials: [], reason: `removed by ${byRole}`, result: 'accepted',
    detail: 'pairing, Residence record and shipment inventory unchanged',
  })
  return true
}

/** Resolve what a manager typed into the account identifier the mock stores. An
 *  account e-mail becomes `u-<email>` — the identifier every mock account already
 *  has — and an identifier it minted itself is passed through unchanged. */
const mockUserIdFor = (entered: string): string =>
  entered.trim().startsWith('u-') || !entered.includes('@') ? entered.trim() : `u-${normEmail(entered)}`

const mockOrgCompany = (organisationId: string): string => mockOrganisations[organisationId]?.company ?? organisationId

/** What the homeowner is shown: every organisation still holding an association
 *  with their Residence, and the basic-status scope it provides (APP-OWN-18). */
const fleetAssociationsForSite = (residenceId: string): FleetAssociation[] =>
  mockOrgFleet.filter((a) => a.residenceId === residenceId && a.state === 'active').map((a) => ({
    organisationId: a.organisationId,
    company: mockOrgCompany(a.organisationId),
    since: a.sinceMs,
    origin: a.origin,
    // The scope this association provides, stated to the homeowner verbatim.
    scope: 'Residence identity, synchronization state and basic online/attention/fault status only',
  }))

/** Submit a declaration: per serial, the same decision the cloud makes. A serial
 *  held elsewhere leaves everything untouched and is reported with the holder and
 *  the re-assignment path (APP-ACC-08D). */
function mockDeclare(organisationId: string, serials: string[], kind: InventoryKind | undefined, actorId: string): DeclarationResult {
  const result: DeclarationResult = { organisationId, accepted: [], alreadyRecorded: [], rejected: [] }
  const seen = new Set<string>()
  for (const raw of serials) {
    const serial = normalizeSerial(raw)
    if (!serial || seen.has(serial)) continue
    seen.add(serial)
    if (!serialFormatValid(serial)) {
      result.rejected.push({ serial, reason: 'malformed', heldBy: null })
      continue
    }
    const existing = mockInventory[serial]
    if (existing && existing.state === 'assigned') {
      if (existing.organisationId === organisationId) result.alreadyRecorded.push(serial)
      else result.rejected.push({ serial, reason: 'assigned-elsewhere', heldBy: existing.organisationId, path: REASSIGNMENT_PATH })
      continue
    }
    const row: InventoryUnit = {
      serial, kind: kind ?? classifySerial(serial) ?? 'dongle', organisationId, state: 'assigned',
      declaredBy: actorId, declaredByRole: 'member', assignedAtMs: Date.now(), withdrawnAtMs: null,
      deviceReportedSerial: null, discrepancy: false, reassignedFrom: null, reassignReason: null,
    }
    mockInventory[serial] = row
    result.accepted.push(row)
  }
  for (const row of result.accepted)
    pushOrgAudit({
      actorId, actorRole: 'member', organisationId, action: 'inventory.declared', targetType: 'inventory',
      targetId: row.serial, serials: [row.serial], reason: '', result: 'accepted', detail: `kind ${row.kind}`,
    })
  for (const r of result.rejected)
    pushOrgAudit({
      actorId, actorRole: 'member', organisationId, action: 'inventory.rejected', targetType: 'inventory',
      targetId: r.serial, serials: [r.serial], reason: r.reason, result: 'rejected',
      detail: r.heldBy ? `held by ${mockOrgCompany(r.heldBy)}` : 'failed serial-format validation',
    })
  return result
}

/**
 * The audited Support/Admin move (SYS-FUN-17J1 / CLD-PRT-01E1) — mirror of
 * `cloud/src/store.reassignSerial` plus the two routes' shared body validation.
 *
 * IT IS THE ONLY WAY A HELD SERIAL MOVES WITHOUT ITS HOLDER, which is what stops a
 * defunct organisation stranding a unit forever, and it is why every guard below
 * is a refusal rather than a best effort:
 *   • an unheld serial is a 404 — there is no assignment to move, and inventing
 *     one here would be a declaration wearing a re-assignment's name;
 *   • the reason is mandatory at three characters, because it is the record of WHY
 *     a unit was taken from an organisation that did not agree to give it up.
 * A WITHDRAWN row is a legitimate target: the move re-assigns it (state back to
 * `assigned`, `withdrawnAtMs` cleared), exactly as the cloud's `reassignAssignment`
 * does — the registry row, not its current state, is what the decision is about.
 *
 * BOTH organisations' logs get the event, the losing one and the gaining one,
 * because each of them has to be able to explain the change from its own records.
 * When a serial is "moved" to the organisation already holding it the two collapse
 * to one row rather than two identical ones.
 */
function mockReassignSerial(
  serial: string, toOrganisationId: string, reason: string, actorScope: 'support' | 'admin',
): InventoryUnit {
  if (!(serial ?? '').trim()) throw new Error('400 serial is required')
  const to = (toOrganisationId ?? '').trim()
  if (!to) throw new Error('400 organisationId is required')
  const recorded = (reason ?? '').trim()
  if (recorded.length < 3)
    throw new Error('400 reason is required and is recorded with the re-assignment')
  const key = normalizeSerial(serial)
  const current = mockInventory[key]
  if (!current) throw new Error('404 No inventory record exists for that serial')
  const from = current.organisationId
  const next: InventoryUnit = {
    ...current,
    organisationId: to,
    state: 'assigned',
    withdrawnAtMs: null,
    assignedAtMs: Date.now(),
    declaredBy: uid(),
    // The cloud writes `admin` on the ROW for both scopes (`reassignAssignment`)
    // and distinguishes the two on the AUDIT EVENT below. Kept, rather than
    // "improved" to `actorScope`, so the mock and the cloud return the same row.
    declaredByRole: 'admin',
    reassignedFrom: from,
    reassignReason: recorded,
  }
  mockInventory[key] = next
  for (const organisationId of new Set([from, to]))
    pushOrgAudit({
      actorId: uid(), actorRole: actorScope, organisationId, action: 'inventory.reassigned',
      targetType: 'inventory', targetId: key, serials: [key], reason: recorded, result: 'accepted',
      detail: `${from} → ${to}`,
    })
  return next
}

/**
 * The staff campaign abort (CLD-OTA-02) — mirror of the shared body behind
 * `POST /admin|support/firmware/campaigns/abort` over `firmwareUpdates.abortCampaign`.
 *
 * THE THREE OUTCOMES PER JOB ARE MODELLED, not just the happy one:
 *   • ahead of the write  → stopped, and its id is returned in `aborted`;
 *   • `applying`          → REFUSED and named, with the cloud's own sentence.
 *     An abort cannot un-flash a partition, so the device's A/B rollback owns
 *     the outcome and the operator is told rather than shown a clean success;
 *   • already resolved    → neither list. Nothing was stopped, nothing refused.
 *
 * AN UNKNOWN CAMPAIGN IS A 404, FULL STOP. It used to reach the caller as a 403
 * "outside your fleet", because the only role that could get here was an installer
 * and the cloud resolved an installer's authority by cohort — a campaign with no
 * jobs failed that test before the lookup. Staff are not cohort-scoped: there is no
 * fleet to be outside of, so an id nobody holds is simply unknown, which is what
 * the handler's own last line says.
 *
 * IT TAKES NO SCOPE ARGUMENT. Unlike `mockReassignSerial`, nothing here is stamped
 * with the acting role — the campaign record carries the reason, not the actor — so
 * passing one in would invent a distinction the cloud does not make.
 */
function mockAbortCampaign(campaignId: string, reason: string): FirmwareCampaignAbort {
  const id = (campaignId ?? '').trim()
  if (!id) throw new Error('400 campaignId is required')
  const recorded = (reason ?? '').trim()
  if (!recorded) throw new Error('400 reason is required — an abort without a recorded reason is not auditable')
  const jobs = updatePlanFor(seedSite.id).filter((job) => job.campaignId === id)
  if (jobs.length === 0) throw new Error('404 No such campaign')
  const stopped: string[] = []
  const refused: { jobId: string; reason: string }[] = []
  const at = Date.now()
  // The cloud truncates the recorded reason at 300 characters before it is
  // written to the campaign and to every job's history; a client that showed
  // more than the server stored would be showing something nobody can audit.
  const stored = recorded.slice(0, 300)
  updatePlans[seedSite.id] = updatePlanFor(seedSite.id).map((job) => {
    if (job.campaignId !== id) return job
    if (job.state === 'applying') {
      refused.push({
        jobId: job.jobId,
        reason: 'this device is writing its standby partition — the abort cannot reach it, and the device\'s own A/B rollback owns the outcome',
      })
      return job
    }
    if (TERMINAL_MOCK_UPDATE_STATES.includes(job.state)) return job
    stopped.push(job.jobId)
    return {
      ...job,
      state: 'aborted',
      abortReason: stored,
      history: [...job.history, { atMs: at, state: 'aborted' as const, note: `campaign aborted: ${stored}` }],
    }
  })
  // Written whatever the cohort did — see `abortedCampaigns`.
  abortedCampaigns[id] = { abortedAtMs: at, abortReason: stored }
  return { campaignId: id, status: 'aborted', abortedAtMs: at, abortReason: stored, aborted: stopped, refused }
}

// ---- Phase-2 pure transforms — mirror of cloud/src/ops.ts; keep in sync. ----
/** Mirror of cloud/src/ops.ts pendingFirst — the shared back-office queue ordering
 *  (pending first, then newest-first within each group). Used by the three admin/
 *  installer queues so their sort never drifts from the cloud's. */
const pendingFirst =
  <T extends { status: string }>(timeOf: (x: T) => number) =>
  (a: T, b: T): number =>
    (a.status === 'pending' ? 0 : 1) - (b.status === 'pending' ? 0 : 1) || timeOf(b) - timeOf(a)
const OPS_UPDATE_TARGET = '2.4.3'
const OPS_UPDATE_COMPONENT = 'Inverter / PCU'
const opPairEvse = (list: Device[]): Device[] =>
  list.map((d) => (d.kind === 'evse' && d.status !== 'online' ? { ...d, status: 'online', detail: '7 kW · paired' } : d))
function opAdvance(steps: CommissioningStep[]): CommissioningStep[] {
  const next = steps.map((s) => (s.state === 'now' ? { ...s, state: 'done' as const } : s))
  const idx = next.findIndex((s) => s.state === 'todo')
  if (idx >= 0) next[idx] = { ...next[idx], state: 'now' }
  return next
}
const opStrip = (v: string) => v.replace(/\s*\(active\)/i, '')
/** Mirror of cloud/src/ops.ts isCommissioningComplete. Keep in sync. */
const opIsCommissioningComplete = (steps: CommissioningStep[]): boolean =>
  steps.length > 0 && steps.every((step) => step.state === 'done')
const opApplyUpdate = (list: FirmwareInfo[], component: string, target: string): FirmwareInfo[] =>
  list.map((f) => {
    if (f.component !== component) return f
    const toB = f.active === 'A'
    return {
      ...f,
      slotA: toB ? opStrip(f.slotA) : `${target} (active)`,
      slotB: toB ? `${target} (active)` : opStrip(f.slotB),
      active: toB ? 'B' : 'A',
      rolloutStage: 'Installed · this site',
    }
  })
const opMarkRead = (list: AppNotification[], ids?: string[]): AppNotification[] => {
  const set = ids ? new Set(ids) : null
  return list.map((n) => (!set || set.has(n.id) ? { ...n, read: true } : n))
}
const opRegistersFor = (baseRegs: DiagnosticRegister[], socPct: number, faulted: boolean): DiagnosticRegister[] =>
  baseRegs.map((g) => ({
    ...g,
    rows: g.rows.map((r) =>
      r.label === 'SoC / SoH'
        ? { ...r, value: `${socPct}% / 99%`, tone: 'ok' as const }
        : r.label.startsWith('Surge protection')
          ? faulted
            ? { ...r, value: 'End of life', tone: 'fault' as const }
            : { ...r, value: 'OK', tone: 'ok' as const }
          : r,
    ),
  }))
/** Mirror of cloud/src/ops.ts appendCapped — prepend + cap (newest first), with an
 *  optional dedup-by-DEFINED-key that reports `changed:false` for a redelivery. The
 *  ONE capped-ring helper behind the mock's notifications, consent log, live ADM
 *  and DERStatus rings (was ~8 inline prepend+slice copies). Keep in sync. */
function appendCapped<T>(list: T[], item: T, cap = 50, dedupe?: (x: T) => unknown): { list: T[]; changed: boolean } {
  if (dedupe) {
    const key = dedupe(item)
    if (key !== undefined && list.some((x) => dedupe(x) === key)) return { list, changed: false }
  }
  return { list: [item, ...list].slice(0, cap), changed: true }
}

// ---------------------------------------------------------------------------
// firmware update lifecycle + region profile (phase 4.5)
// ---------------------------------------------------------------------------
//
// A DELIBERATELY SMALL MIRROR of `cloud/src/firmwareUpdates.ts`. The state
// machine there is the normative one and it is walked exhaustively in
// `cloud/test/firmwareUpdates.test.ts` and driven against a real Simulated
// Dongle in `vertical-slice/test/firmwareLifecycle.test.ts`. What this fake owes
// the app is the same STATES, the same refusals and the same honesty about what
// the device has and has not confirmed — so a screen written against the mock
// cannot be written against a shape the real cloud does not produce.
//
// TWO JOBS, and the reason there are two. The cloud's demo world seeds ONE
// approved release (the inverter one), because a permanently outstanding update
// in every demo Residence would be noise. A phone screen, though, has to be able
// to show what a ROLLBACK looks like — the state that matters most and is
// hardest to render honestly — so this fake also carries a comms-module job that
// has already rolled back. It is a display fixture for a state the real
// lifecycle produces, not a second campaign; retrying it runs the same path to
// `applied` that the inverter job runs.
//
// PROGRESSION IS DRIVEN BY READS. There is no device here, so `getUpdates`
// advances anything in flight one phase per call: downloading → downloading →
// applying → applied. That is a simulation and it is confined to `stepUpdates`
// below; nothing else in this file moves an update.

const UPDATE_RELEASE_REASON =
  'Economic-optimisation and telemetry changes only. No grid-protection setting, no metering path and no AS/NZS 4777.2 parameter is altered, so the commissioning tests remain valid on 2.4.1.'
const COMMS_RELEASE_REASON =
  'Connectivity stack fix. The comms module carries no control-domain authority, so nothing it runs affects grid protection or metering.'
const BLOCKING_RELEASE_REASON =
  'Changes an AS/NZS 4777.2 grid-protection setting. The commissioning tests are only valid on the version that will be running, so this must install before commissioning is signed off.'
const UPDATE_WINDOW_MS = 24 * 60 * 60 * 1000
/** Mirror of `cloud/src/firmwareUpdates.TERMINAL_UPDATE_STATES`. Deliberately
 *  SHORT: `failed` and `rolled-back` are not terminal — a job in either is still
 *  awaiting a retry, so an abort has something real to stop. Keep in sync. */
const TERMINAL_MOCK_UPDATE_STATES: ReadonlyArray<UpdateJobView['state']> = ['applied', 'aborted']

const newUpdateJob = (job: Partial<UpdateJobView> & Pick<UpdateJobView, 'jobId' | 'domain' | 'domainLabel' | 'target' | 'current'>): UpdateJobView => ({
  campaignId: `cmp-${job.jobId}`,
  residenceId: seedSite.id,
  deviceId: `${seedSite.id}::pcu`,
  classification: 'post-commissioning',
  classificationReason: UPDATE_RELEASE_REASON,
  state: 'deferred',
  pending: true,
  progressPct: null,
  autoStartDeadlineMs: null,
  deadlineStatus: 'none',
  attempts: 0,
  startedBy: null,
  rolledBackToVersion: null,
  reason: null,
  lastRefusal: null,
  slots: null,
  unconfirmed: [],
  abortReason: null,
  canStartNow: true,
  explanation: '',
  history: [],
  ...job,
} as UpdateJobView)

// THE UPDATE PLAN IS A FACT ABOUT A RESIDENCE, NOT ABOUT THE SIGNED-IN INSTALLER.
//
// The cloud keys it that way — `GET /updates?site=` runs `requireMembership` on the
// addressed Residence and answers with THAT Residence's jobs and commissioning gate
// (cloud/src/handler.ts). The mock held ONE global list, so an installer opening the
// commissioning wizard on a customer's Residence was shown the gate of their OWN
// site: a blocking update on the home in front of them was invisible, and one
// somewhere else could block a sign-off it has nothing to do with. Keyed per
// Residence here so a screen that forgets to address one fails in Tier A too,
// instead of only against the real cloud.
const updatePlans: Record<string, UpdateJobView[]> = {}

/** Mirror of the cloud's addressing rule: no `site` is the session Residence
 *  (multi-res switcher); an addressed one must be a Residence the caller holds a
 *  membership of, else 403 (`requireMembership`). Never silently falls back to a
 *  different home than the session — that is the multi-res defect. */
function updatePlanSite(site?: string): string {
  if (!site) return mockSessionSiteId || seedSite.id
  if (site === seedSite.id) return seedSite.id
  const mine = mockMemberships[currentEmail] ?? []
  if (!mine.some((m) => m.siteId === site)) throw new Error('403 Forbidden')
  return site
}

/** The addressed Residence's plan, seeded on first read like the rest of this file. */
function updatePlanFor(siteId: string): UpdateJobView[] {
  if (!updatePlans[siteId]) updatePlans[siteId] = seedUpdateJobs(siteId)
  return updatePlans[siteId]
}

function seedUpdateJobs(siteId: string = seedSite.id): UpdateJobView[] {
  const at = (id: string) => ({
    jobId: siteId === seedSite.id ? id : `${id}@${siteId}`,
    residenceId: siteId,
    deviceId: `${siteId}::pcu`,
  })
  // A Residence the installer is commissioning may carry a COMMISSIONING-BLOCKING
  // update — the fixture that makes APP-INS-05A/05B's amber path reachable at all
  // (the seed world only ever had post-commissioning ones, so the wizard's blocking
  // notice and the Firmware screen's "Commissioning blocked" badge were unreachable
  // in Tier A). Which Residence carries which is declared in the fixture, not here.
  if (siteId !== seedSite.id && mockResidences[siteId]?.outstandingUpdate === 'commissioning-blocking')
    return [
      newUpdateJob({
        ...at('job-inv-2.5.0'),
        domain: 'inverter',
        domainLabel: 'Inverter / PCU',
        classification: 'commissioning-blocking',
        classificationReason: BLOCKING_RELEASE_REASON,
        current: { version: '2.4.1', confirmed: true },
        target: { version: '2.5.0', signature: 'simulated', rolloutStage: 'staged' },
        state: 'eligible',
        history: [{ atMs: Date.now() - 3600_000, state: 'eligible', note: 'offered (commissioning-blocking classification)' }],
      }),
    ]
  if (siteId !== seedSite.id && mockResidences[siteId]?.outstandingUpdate === 'none') return []
  return [
    newUpdateJob({
      ...at('job-inv-2.4.3'),
      domain: 'inverter',
      domainLabel: 'Inverter / PCU',
      current: { version: '2.4.1', confirmed: true },
      target: { version: '2.4.3', signature: 'simulated', rolloutStage: 'staged' },
      state: 'deferred',
      history: [{ atMs: Date.now() - 3 * 3600_000, state: 'deferred', note: 'held until commissioning completes (post-commissioning classification)' }],
    }),
    newUpdateJob({
      ...at('job-comms-3.2.0'),
      domain: 'comms',
      domainLabel: 'Comms module',
      classificationReason: COMMS_RELEASE_REASON,
      current: { version: '3.1.0', confirmed: true },
      target: { version: '3.2.0', signature: 'simulated', rolloutStage: 'staged' },
      state: 'rolled-back',
      attempts: 1,
      startedBy: 'automatic',
      rolledBackToVersion: '3.1.0',
      reason: 'health_check_failed',
      deadlineStatus: 'met',
      history: [
        { atMs: Date.now() - 2 * 3600_000, state: 'downloading', note: 'dispatched (automatic, attempt 1)' },
        { atMs: Date.now() - 2 * 3600_000 + 60_000, state: 'applying', note: 'device reported applying' },
        { atMs: Date.now() - 2 * 3600_000 + 120_000, state: 'rolled-back', note: 'device reported rolled-back' },
      ],
    }),
  ]
}

/** The mirror of `cloud/src/firmwareUpdates.explainUpdate`. Keep in sync. */
function explainUpdate(job: UpdateJobView): string {
  switch (job.state) {
    case 'eligible':
      return job.classification === 'commissioning-blocking'
        ? 'This update must be installed before commissioning can be completed.'
        : 'This update is available and has not been queued yet.'
    case 'deferred':
      return 'Queued until commissioning is finished. Nothing starts before then.'
    case 'scheduled':
      return 'Queued. It starts automatically once the device is online and ready — or now, if you ask for it.'
    case 'downloading':
      return `Downloading${job.progressPct === null ? '' : ` — ${job.progressPct}%`}. Nothing has been written to the device yet.`
    case 'applying':
      return job.domain === 'inverter' || job.domain === 'bms' || job.domain === 'ems'
        ? 'Installing to the spare partition. The running version is untouched until the new one boots and passes its check.'
        : 'Installing from the image staged on the inverter. It can be retried from that image if it fails.'
    case 'applied':
      return `Installed. The device reports it is running ${job.current.version ?? job.target.version}.`
    case 'rolled-back':
      return `The update did not pass its check and the device rolled itself back. It is running ${job.rolledBackToVersion ?? 'its previous version'} — nothing was lost.`
    case 'failed':
      return `The update did not install (${job.reason ?? 'no reason reported'}). The device is still running ${job.current.version ?? 'its previous version'}.`
    case 'aborted':
      return `The rollout was stopped${job.abortReason ? ` (${job.abortReason})` : ''}. This update will not be delivered.`
  }
}

const projectUpdate = (job: UpdateJobView): UpdateJobView => ({
  ...job,
  pending: job.state !== 'applied' && job.state !== 'aborted',
  canStartNow: job.state === 'scheduled' || job.state === 'deferred' || job.state === 'failed' || job.state === 'rolled-back',
  explanation: explainUpdate(job),
})

/** Advance anything in flight by one phase, and reconcile the deferral gate with
 *  the commissioning wizard. The ONE mover of update state in this file. Scoped to
 *  the addressed Residence's plan — nothing steps a Residence nobody is looking at. */
function stepUpdates(siteId: string = seedSite.id): UpdateJobView[] {
  const complete = opIsCommissioningComplete(commissioning)
  updatePlans[siteId] = updatePlanFor(siteId).map((job) => {
    // The commissioning gate, both directions — a deferred job becomes an
    // automatic one the moment the wizard is done (CLD-OTA-07A/07B).
    if (job.state === 'deferred' && complete)
      return {
        ...job,
        state: 'scheduled',
        autoStartDeadlineMs: Date.now() + UPDATE_WINDOW_MS,
        deadlineStatus: 'within-window',
        history: [...job.history, { atMs: Date.now(), state: 'scheduled' as const, note: 'commissioning complete — queued for automatic execution' }],
      }
    if (job.state === 'downloading') {
      const next = (job.progressPct ?? 0) + 45
      if (next < 100) return { ...job, progressPct: next }
      return {
        ...job,
        state: 'applying',
        progressPct: 100,
        slots: { active: 'A', activeVersion: job.current.version ?? '', standbyVersion: job.target.version, pendingSlot: 'B' },
        history: [...job.history, { atMs: Date.now(), state: 'applying' as const, note: 'device reported applying' }],
      }
    }
    if (job.state === 'applying') {
      // The device booted the new slot and read the version back. The version
      // shown is the one the DEVICE reported, never the release's target copied
      // across — the same rule the real lifecycle holds.
      const applied = job.target.version
      // The installed-versions list and the "firmware installed" notification are
      // the CALLER'S OWN site's, so only that site's plan writes to them.
      if (siteId === seedSite.id) {
        firmware = job.domain !== 'inverter'
          ? opApplyUpdate(firmware, job.domainLabel, applied)
          : opApplyUpdate(firmware, OPS_UPDATE_COMPONENT, applied)
        pushFirmwareNotif()
      }
      return {
        ...job,
        state: 'applied',
        progressPct: 100,
        current: { version: applied, confirmed: true },
        slots: { active: 'B', activeVersion: applied, standbyVersion: job.current.version ?? '', pendingSlot: null },
        deadlineStatus: job.deadlineStatus === 'within-window' ? 'met' : job.deadlineStatus,
        history: [...job.history, { atMs: Date.now(), state: 'applied' as const, note: 'device reported applied' }],
      }
    }
    return job
  })
  return updatePlans[siteId]
}

/** The region-profile picture. Separate from every firmware version above, and
 *  honestly gated: while MOD-TBR-07 is open the inverter cannot report a profile
 *  VERSION, so CLD-OTA-03C's read-back is incomplete and nothing may be shown as
 *  applied (`cloud/src/regionProfiles.ts` holds the normative rule). */
const mockRegionProfile = (): RegionProfileView[] => [
  {
    applicationId: 'rgn-aus-a-2026.1::pcu',
    revisionId: 'rgn-aus-a-2026.1',
    deviceId: `${seedSite.id}::pcu`,
    requested: { profileId: 'Australia A', version: '2026.1' },
    state: 'unconfirmed',
    active: { profileId: 'Australia A', version: null, confirmedAtMs: null },
    retention: 'unknown',
    gates: [
      { gate: 'MOD-TBR-07', reason: 'V1.20 defines no active-profile-version or last-result read-back register.' },
      { gate: 'MOD-TBR-08', reason: 'V1.20 defines no atomic apply/validate/rollback for a multi-register profile revision.' },
    ],
    confirmed: false,
    explanation:
      'The inverter reports Australia A, but cannot report a profile version. It is not recorded as applied. The previous profile record is unchanged.',
  },
]

/**
 * Campaigns this world has aborted (CLD-OTA-02), by campaign id.
 *
 * It lives BESIDE the plan rather than inside a job because a campaign and a job
 * are different records with different lifetimes: the cloud writes the campaign's
 * `status`/`abortedAtMs`/`abortReason` unconditionally and then walks the cohort,
 * so a campaign whose every job had already resolved is still recorded as
 * aborted. Folding the fact into the jobs would have lost exactly that case.
 */
const abortedCampaigns: Record<string, { abortedAtMs: number; abortReason: string }> = {}

const mockCampaigns = (siteId: string = seedSite.id): FirmwareCampaignView[] =>
  updatePlanFor(siteId).map((job) => {
    const counts = {
      eligible: 0, deferred: 0, scheduled: 0, downloading: 0, applying: 0,
      applied: 0, 'rolled-back': 0, failed: 0, aborted: 0,
    } as Record<UpdateState, number>
    counts[job.state] += 1
    return {
      campaignId: job.campaignId,
      releaseId: job.campaignId.replace('cmp-job-', 'rel-'),
      deviceClass: seedInverter.model,
      domain: job.domain,
      status: abortedCampaigns[job.campaignId] ? ('aborted' as const) : ('running' as const),
      rolloutStage: job.target.rolloutStage,
      classification: job.classification,
      targetVersion: job.target.version,
      total: 1,
      counts,
      resolved: job.state === 'applied' ? 1 : 0,
      inFlight: job.state === 'downloading' || job.state === 'applying' ? 1 : 0,
      rollbackRate: job.state === 'rolled-back' ? 1 : job.state === 'applied' ? 0 : null,
      attempts: job.attempts,
      haltRecommended: false,
      haltReason: null,
      deadline: { none: 0, 'within-window': 0, met: 0, 'lapsed-while-ineligible': 0, 'lapsed-unexplained': 0, [job.deadlineStatus]: 1 } as Record<UpdateJobView['deadlineStatus'], number>,
    }
  })

function pushFirmwareNotif(): void {
  pushNotif(
    `Firmware ${OPS_UPDATE_TARGET} installed`,
    `${OPS_UPDATE_COMPONENT}: signed A/B install complete — automatic rollback armed, then released after verification.`,
    { category: 'announcement' },
  )
}
/** Prepend a notification (newest first, capped), DEDUPED BY ID like the cloud feed
 *  (store.raiseBackstopSurfaces): the backstop path passes a STABLE id, so a re-raise of
 *  the same event is an idempotent no-op instead of a duplicate React key. Defaults to a
 *  'system'/'info' event whose id is unique per push (a counter, not just the clock — two
 *  notifications inside one millisecond must never collide and drop one). */
let notifSeq = 0
function pushNotif(
  title: string,
  body: string,
  opts?: { category?: AppNotification['category']; severity?: AppNotification['severity']; id?: string },
): void {
  const item: AppNotification = {
    id: opts?.id ?? `n-${Date.now()}-${notifSeq++}`,
    category: opts?.category ?? 'system',
    severity: opts?.severity ?? 'info',
    title, body, time: 'Just now', read: false,
  }
  notifications = appendCapped(notifications, item, 50, (x) => x.id).list
}

// Rendered in the RESIDENCE's zone: "today" for a consent-log entry is the
// site's today, and near midnight the host's date is a different day.
const todayLong = () =>
  new Date().toLocaleDateString('en-AU', { timeZone: MOCK_SITE_TIME_ZONE, day: 'numeric', month: 'short', year: 'numeric' })
const opNormalizeHws = (relayConnected: boolean, hotByHour?: number): HwsDeclaration => {
  if (!relayConnected) return { relayConnected: false }
  const h = typeof hotByHour === 'number' && Number.isInteger(hotByHour) && hotByHour >= 0 && hotByHour <= 23 ? hotByHour : undefined
  return { relayConnected: true, ...(h !== undefined ? { hotByHour: h } : {}) }
}
// ---- TOU dispatch windows derived from the editable schedule (mirror of the
// cloud simulator: honours enable + times + power + target SoC; day-of-week not
// applied). ----
interface Win {
  from: number
  to: number
  powerKw: number
  targetSocPct: number
}
function parseHHMM(t: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(t)
  if (!m) return null
  const h = +m[1]
  const mm = +m[2]
  return h >= 0 && h <= 24 && mm >= 0 && mm < 60 ? h + mm / 60 : null
}
/** Sort a schedule by start time-of-day (APP-HOM-14). Slots with an unparseable
 *  start sort last but are preserved. */
function sortByStart(slots: TouSlot[]): TouSlot[] {
  return [...slots].sort((a, b) => (parseHHMM(a.start) ?? 99) - (parseHHMM(b.start) ?? 99))
}
/** True if two windows' time ranges intersect on the 24 h clock (each split into
 *  1–2 non-wrapping [from,to) intervals). Mirror of the cloud's windowsOverlap and
 *  the app editor's — no window may overlap any other, charge or discharge alike. */
function slotsOverlap(a: TouSlot, b: TouSlot): boolean {
  const iv = (s: string, e: string): Array<[number, number]> => {
    const f = parseHHMM(s) ?? 0
    const t = parseHHMM(e) ?? 0
    return f < t ? [[f, t]] : [[f, 24], [0, t]]
  }
  const A = iv(a.start, a.end)
  const B = iv(b.start, b.end)
  return A.some(([s1, e1]) => B.some(([s2, e2]) => s1 < e2 && s2 < e1))
}
/** Reject an overlapping schedule (mirror of the cloud's validateSchedule) so the
 *  Tier-A e2e can catch a violation offline too. */
function assertNoOverlap(slots: TouSlot[]): void {
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      if (slotsOverlap(slots[i], slots[j])) throw new Error("Schedule windows can't overlap in time")
    }
  }
}
function windows(): { charge: Win[]; discharge: Win[] } {
  const charge: Win[] = []
  const discharge: Win[] = []
  for (const s of schedule) {
    if (!s.enabled) continue
    const from = parseHHMM(s.start)
    const to = parseHHMM(s.end)
    if (from == null || to == null || from === to) continue
    ;(s.action === 'charge' ? charge : discharge).push({ from, to, powerKw: s.powerKw, targetSocPct: s.targetSocPct })
  }
  return { charge, discharge }
}
const inWin = (h: number, w: Win) => (w.from <= w.to ? h >= w.from && h < w.to : h >= w.from || h < w.to)
const matchWin = (h: number, ws: Win[]): Win | undefined => ws.find((w) => inWin(h, w))
const modeOn = (id: string) => modes.find((m) => m.id === id)?.enabled ?? false

/** Peak-shave demand window from the stored tariff (mirror of the cloud's
 *  demandWindowFor) — null when there's no active demand charge. */
function demandWin(): Win | null {
  const d = tariff.demand
  if (!d || !d.enabled) return null
  const { fromHour: from, toHour: to } = d
  if (typeof from !== 'number' || typeof to !== 'number' || from === to) return null
  return { from, to, powerKw: MAX_KW, targetSocPct: 0 }
}

// ---- mode precedence: backup › Hold / TOU (inside its windows) › self ----
function effectiveModeAt(hour: number): string | null {
  const w = windows()
  if (modeOn('backup')) return 'backup'
  if (behaviour.batteryHold) return null
  if (modeOn('tou') && (matchWin(hour, w.charge) || matchWin(hour, w.discharge))) return 'tou'
  return 'self'
}

function effectiveModeId(): string | null {
  // The RESIDENCE's hour, not the host's — a TOU window is stated in the site's
  // wall clock, so which mode is effective cannot depend on where the phone is.
  return effectiveModeAt(siteHourOfDay())
}

/** Recompute the `active` flags — only the effective mode carries one. */
function refreshActive(): void {
  const eff = effectiveModeId()
  modes.forEach((m) => (m.active = m.id === eff))
}

// ---- mock DNSP + aggregator (Phase 10) — the OFFLINE analog of the cloud CSIP-Aus
//      slice. The offline mock has no device and no MQTT, so this SIMULATES the
//      cloud aggregator: a DNSP dynamic export limit that steps through the day
//      (deterministic on the client clock, like the rest of the mock) and lapses
//      overnight. Surfaced honestly on the Device-link screen as "simulated". The
//      real thing (mock DNSP → aggregator → cmd/ topic → dongle ack) runs cloud-side.
const DNSP_SLOTS: { startH: number; endH: number; limitW: number }[] = [
  { startH: 6, endH: 10, limitW: 1500 }, // morning ramp
  { startH: 10, endH: 15, limitW: 1000 }, // solar-peak pinch (minimum demand)
  { startH: 15, endH: 21, limitW: 1500 }, // evening
]
function dnspCommandFor(slot: { startH: number; endH: number; limitW: number }, dayStartMs: number, day: number): SetpointCommand {
  const derControlId = `derc-${seedSite.id}-${day}-${slot.startH}`
  return {
    v: 1,
    commandId: `cmd-${derControlId}`,
    seq: DNSP_SLOTS.indexOf(slot) + 1,
    siteId: seedSite.id,
    issuedAt: dayStartMs + slot.startH * 3_600_000,
    expiresAt: dayStartMs + slot.endH * 3_600_000,
    priority: 'dnsp',
    setpoint: { kind: 'exportLimit', exportLimitW: slot.limitW },
    source: 'dnsp',
    reason: `Ausgrid (mock) dynamic export limit ${(slot.limitW / 1000).toFixed(1)} kW`,
    derControlId,
  }
}
// ---- Phase-11: per-DNSP jurisdiction config + emergency backstop (GRI-CSP-05,
//      GRI-BST-01/02/03) — the OFFLINE mirror of the cloud jurisdictions.ts +
//      backstop.ts. Config-driven (no per-DNSP branches); the demo trigger defaults
//      to the SA/SAPN flagship (GRI-BST-01). `defaultExportLimitW` is the GRI-DEV-02
//      revert target the device holds when no command is in force.
const JURISDICTIONS: JurisdictionProfile[] = [
  { id: 'sapn', dnsp: 'SA Power Networks', displayName: 'SA Power Networks', jurisdiction: 'South Australia', utilityServerUrl: 'https://der.sapn.example/dcap', pollSeconds: 60, defaultExportLimitW: 1500, backstopAdapter: 'sa-relevant-agent', backstopScheme: 'SA emergency backstop (Relevant Agent)' },
  { id: 'energex', dnsp: 'Energex', displayName: 'Energex', jurisdiction: 'Queensland', utilityServerUrl: 'https://der.energex.example/dcap', pollSeconds: 300, defaultExportLimitW: 1500, backstopAdapter: 'qld-dynamic-connection', backstopScheme: 'QLD emergency solar management' },
  { id: 'ausgrid', dnsp: 'Ausgrid', displayName: 'Ausgrid', jurisdiction: 'New South Wales', utilityServerUrl: 'https://der.ausgrid.example/dcap', pollSeconds: 300, defaultExportLimitW: 1500, backstopAdapter: 'nsw-flexible-export', backstopScheme: 'NSW flexible-export emergency limit' },
  { id: 'citipower', dnsp: 'CitiPower', displayName: 'CitiPower', jurisdiction: 'Victoria', utilityServerUrl: 'https://der.citipower.example/dcap', pollSeconds: 300, defaultExportLimitW: 1500, backstopAdapter: 'vic-flexible-export', backstopScheme: 'VIC flexible-export emergency limit' },
]
const profileForSiteDnsp = (): JurisdictionProfile =>
  JURISDICTIONS.find((p) => p.dnsp.toLowerCase() === seedSite.dnsp.toLowerCase()) ?? JURISDICTIONS[2]
const backstopProfileById = (id?: string): JurisdictionProfile =>
  JURISDICTIONS.find((p) => p.id === id) ?? JURISDICTIONS[0] // default: SA/SAPN flagship (GRI-BST-01)

const backstopActive = (nowMs = Date.now()): boolean =>
  !!backstopWindow && backstopWindow.active && nowMs >= backstopWindow.startMs && nowMs < backstopWindow.endMs

/** The 'backstop'-priority setpoint command an active backstop window issues (mirror
 *  of the cloud aggregator's derControlToCommand for an emergency control). */
function backstopCommandFor(w: BackstopWindow): SetpointCommand {
  return {
    v: 1,
    commandId: `cmd-backstop-${w.jurisdiction}-${w.startMs}`,
    seq: 999,
    siteId: seedSite.id,
    issuedAt: w.startMs,
    expiresAt: w.endMs,
    priority: 'backstop',
    setpoint: { kind: 'exportLimit', exportLimitW: w.limitW },
    source: 'dnsp', // a backstop is issued BY a DNSP; the priority flag carries the tier
    reason: w.reason,
    derControlId: `backstop-${w.jurisdiction}-${w.startMs}`,
  }
}

// ---- Phase-12: the 'vpp' tier (CLD-VPP-*, GRI-VPP-*, ICD-API-02) — the OFFLINE
//      mirror of cloud/src/vpp.ts + the generalized resolver in cloud/src/command.ts.
//      Keep in sync. The precedence VALUES are invariant #5 and must not change.
const PRIORITY_RANK: Record<SetpointCommand['priority'], number> = {
  safety: 5, backstop: 4, dnsp: 3, backup: 2, vpp: 1, economic: 0,
}
/** Mirror of cloud/src/command.ts `outranks` — precedence, ties to the newer seq. */
const outranks = (a: SetpointCommand, b: SetpointCommand): boolean =>
  PRIORITY_RANK[a.priority] > PRIORITY_RANK[b.priority] ||
  (PRIORITY_RANK[a.priority] === PRIORITY_RANK[b.priority] && a.seq > b.seq)
/** Mirror of cloud/src/command.ts `activeCommands` — rank a candidate SET (N-way, not
 *  a two-way special case), dropping anything past its TTL. */
const activeCommands = (candidates: (SetpointCommand | null)[], nowMs: number): SetpointCommand[] =>
  candidates
    .filter((c): c is SetpointCommand => !!c && nowMs >= c.issuedAt && nowMs < c.expiresAt)
    .sort((a, b) => (outranks(a, b) ? -1 : outranks(b, a) ? 1 : 0))

/** A live VPP dispatch on THIS site (the mock's VPP_COMMAND slot). */
let vppCommand: SetpointCommand | null = null
/** The per-site VPP records + the partner's event/webhook history (mirrors the cloud's
 *  SITE#/VPP_LOG and PARTNER#/EVENT_LOG + WEBHOOK_LOG rings). */
let vppRecords: VppSiteResult[] = []
let vppEvents: VppEvent[] = []
let vppWebhooks: VppWebhookDelivery[] = []
/** Whether a partner grant has been minted this session (the mock's OAuth2 analog). */
let partnerGranted = false

const VPP_PARTNER = {
  id: 'origin-loop',
  name: 'Origin Loop VPP', // must match seedConsents c-2 — that consent governs dispatch
  clientId: 'origin-loop',
  clientSecret: 'demo-partner-secret',
  scopes: ['fleet:read', 'dispatch:write', 'settlement:read'],
  webhookUrl: 'https://partner.origin-loop.example/webhooks/risen',
  program: 'evening-peak',
}
/** The sandbox fleet (CLD-VPP-06) — mirror of cloud/src/vpp.ts VPP_SIM_FLEET, mixed so
 *  ONE cohort dispatch exercises every CLD-VPP-04 partial-failure branch. */
const VPP_SIM_FLEET: {
  siteId: string; label: string; postcode: string; dnspZone: string; program: string
  online: boolean; consentActive: boolean; socPct: number
  gridLimitW: number; gridLimitSource: 'dnsp' | 'backstop' | 'default'; netLoadKw: number
}[] = [
  { siteId: 'sim-2320', label: 'Maitland · 2320', postcode: '2320', dnspZone: 'Ausgrid — Hunter', program: 'evening-peak', online: true, consentActive: true, socPct: 78, gridLimitW: 1500, gridLimitSource: 'dnsp', netLoadKw: 2.2 },
  { siteId: 'sim-2290', label: 'Charlestown · 2290', postcode: '2290', dnspZone: 'Ausgrid — Hunter', program: 'evening-peak', online: true, consentActive: true, socPct: 64, gridLimitW: 1000, gridLimitSource: 'dnsp', netLoadKw: 0 },
  { siteId: 'sim-2285', label: 'Cardiff · 2285', postcode: '2285', dnspZone: 'Ausgrid — Hunter', program: 'evening-peak', online: false, consentActive: true, socPct: 55, gridLimitW: 1500, gridLimitSource: 'dnsp', netLoadKw: 1.4 },
  { siteId: 'sim-5000', label: 'Adelaide · 5000', postcode: '5000', dnspZone: 'SAPN — Adelaide', program: 'evening-peak', online: true, consentActive: true, socPct: 71, gridLimitW: 0, gridLimitSource: 'backstop', netLoadKw: 0 },
  { siteId: 'sim-2300', label: 'Newcastle · 2300', postcode: '2300', dnspZone: 'Ausgrid — Hunter', program: 'evening-peak', online: true, consentActive: false, socPct: 82, gridLimitW: 1500, gridLimitSource: 'dnsp', netLoadKw: 1.9 },
  { siteId: 'sim-2325', label: 'Cessnock · 2325', postcode: '2325', dnspZone: 'Ausgrid — Hunter', program: 'evening-peak', online: true, consentActive: true, socPct: 20, gridLimitW: 1500, gridLimitSource: 'dnsp', netLoadKw: 1.1 },
]
const MIN_DISPATCH_KW = 0.1
/** CLD-DAT-03 — the DATA-LAYER consent gate: a non-consenting site is ABSENT from the
 *  partner's rows, not redacted. What was withheld is a COUNT only. */
function consentedSimFleet(): { rows: VppFleetSite[]; withheld: number } {
  const ok = VPP_SIM_FLEET.filter((s) => s.consentActive)
  return {
    rows: ok.map((s) => ({
      siteId: s.siteId, label: s.label, postcode: s.postcode, dnspZone: s.dnspZone, program: s.program,
      online: s.online, socPct: s.socPct, gridLimitW: s.gridLimitW, gridLimitSource: s.gridLimitSource, simulated: true,
    })),
    withheld: VPP_SIM_FLEET.length - ok.length,
  }
}
const inCohort = (s: VppFleetSite, c: VppCohort): boolean =>
  !(c.siteIds?.length && !c.siteIds.includes(s.siteId)) &&
  !(c.postcode && s.postcode !== c.postcode) &&
  !(c.dnspZone && s.dnspZone !== c.dnspZone) &&
  !(c.program && s.program !== c.program)

/** GRI-VPP-01 — the export headroom a DISCHARGE has, in battery-power kW. The house
 *  takes its net draw first and only the remainder reaches the grid, so
 *  batteryKw ≤ capKw + max(0, netLoadKw). A zero-export backstop therefore still permits
 *  discharge INTO THE HOUSE — it caps export, not self-consumption. */
const exportHeadroomKw = (gridLimitW: number, netLoadKw: number): number =>
  Math.max(0, gridLimitW) / 1000 + Math.max(0, netLoadKw)

/** GRI-VPP-01 + CLD-VPP-03 — the screen. Mirror of cloud/src/vpp.ts screenDispatch:
 *  refuse, clamp, or accept — NEVER widen (invariant #1: every clamp is toward zero). */
function screenDispatch(i: {
  action: 'discharge' | 'charge'; targetKw: number; socPct: number; reserveFloorPct: number
  chargeCeilingPct: number; online: boolean; gridLimitW: number
  gridLimitSource: 'dnsp' | 'backstop' | 'default'; netLoadKw: number
  higherTier?: SetpointCommand['priority'] | null
}): { outcome: VppOutcome; dispatchedKw: number; reasonCode?: VppReasonCode; reason: string } {
  const label = i.gridLimitSource === 'backstop' ? 'emergency backstop'
    : i.gridLimitSource === 'dnsp' ? 'DNSP export limit' : 'DNSP default export limit'
  const kwLabel = (w: number) => (w === 0 ? '0 kW' : `${(w / 1000).toFixed(w % 1000 === 0 ? 0 : 1)} kW`)
  if (!i.online) return { outcome: 'refused', dispatchedKw: 0, reasonCode: 'site_offline', reason: 'Site is offline — no dispatch was issued' }
  if (i.higherTier)
    return { outcome: 'refused', dispatchedKw: 0, reasonCode: 'higher_precedence', reason: `A higher-precedence mode (${i.higherTier}) is in force — VPP dispatch is subordinate to it (invariant #5)` }
  if (i.action === 'charge') {
    // Charging ABSORBS power — it can never breach an export limit; only the BMS-safe
    // ceiling bites (a simulation clamp, never a safety limit — invariant #1).
    if (i.socPct >= i.chargeCeilingPct)
      return { outcome: 'refused', dispatchedKw: 0, reasonCode: 'soc_ceiling', reason: `Battery is at its charge ceiling (${i.chargeCeilingPct}%) — nothing to absorb` }
    const kw = Math.min(i.targetKw, MAX_KW)
    return kw < i.targetKw - 1e-9
      ? { outcome: 'clamped', dispatchedKw: -round(kw), reason: `Charge clamped to the ${MAX_KW} kW inverter rating` }
      : { outcome: 'accepted', dispatchedKw: -round(kw), reason: `Charging at ${round(kw)} kW` }
  }
  if (i.socPct <= i.reserveFloorPct)
    return { outcome: 'refused', dispatchedKw: 0, reasonCode: 'reserve_floor', reason: `Battery is at the backup reserve floor (${i.reserveFloorPct}%) — the reserve is held back from VPP dispatch` }
  const headroom = exportHeadroomKw(i.gridLimitW, i.netLoadKw)
  if (headroom < MIN_DISPATCH_KW)
    return { outcome: 'refused', dispatchedKw: 0, reasonCode: 'grid_limit', reason: `The ${label} in force (${kwLabel(i.gridLimitW)}) leaves no export headroom — dispatch refused rather than breach it` }
  const bound = Math.min(headroom, MAX_KW)
  const kw = Math.min(i.targetKw, bound)
  if (kw < i.targetKw - 1e-9) {
    const by = headroom <= MAX_KW ? `the ${label} in force (${kwLabel(i.gridLimitW)})` : `the ${MAX_KW} kW inverter rating`
    return { outcome: 'clamped', dispatchedKw: round(kw), reasonCode: headroom <= MAX_KW ? 'grid_limit' : undefined, reason: `Clamped from ${round(i.targetKw)} kW to ${round(kw)} kW by ${by}` }
  }
  return { outcome: 'accepted', dispatchedKw: round(kw), reason: `Discharging at ${round(kw)} kW within the ${label} (${kwLabel(i.gridLimitW)})` }
}

/** The command currently in force + every ranked candidate + the day's command log
 *  (newest first). GRI-BST-03 / GRI-VPP-01: an active EMERGENCY backstop outranks the
 *  routine dynamic export limit, and BOTH outrank a VPP dispatch (invariant #5).
 *  SYNC NOTE: this is the mock's dialect of the cloud's single resolver
 *  (cloud/src/command.ts activeCommands) — same semantics (the backstop is derived from
 *  the WINDOW at read time; every tier is ranked as a candidate SET, not pairwise), and
 *  it too is genuinely N-way. Synthesized rather than stored because the mock's routine
 *  log is generated from the day's slots. Keep in sync. */
function mockCommands(nowMs: number = Date.now()): CommandsStatus {
  // A DNSP slot ladder is stated in the site's LOCAL hours (the solar-peak pinch
  // is a local-noon phenomenon), and the day it belongs to rolls over at the
  // site's midnight — not the host's. Both used to come from the browser's zone.
  const hour = siteHourOfDay(nowMs)
  const dayStartMs = siteDayStartMs(nowMs)
  const day = Math.floor(dayStartMs / 86_400_000)
  const activeSlot = DNSP_SLOTS.find((s) => hour >= s.startH && hour < s.endH)
  const routineLog = DNSP_SLOTS.filter((s) => hour >= s.startH).map((s) => dnspCommandFor(s, dayStartMs, day)).reverse()
  const bs = backstopActive(nowMs) ? backstopCommandFor(backstopWindow!) : null
  const candidates = activeCommands(
    [activeSlot ? dnspCommandFor(activeSlot, dayStartMs, day) : null, bs, vppCommand],
    nowMs,
  )
  const log = [
    ...(vppCommand ? [vppCommand] : []),
    ...(bs ? [bs] : []),
    ...routineLog,
  ]
  return { active: candidates[0] ?? null, candidates, log }
}

/** The EXPORT CAP in force: the highest-ranked ACTIVE command that carries one. Filters
 *  the candidate set by DIMENSION, not by rank — mirror of the cloud's
 *  resolveExportControl. Load-bearing: a VPP power target may outrank nothing else and
 *  top the ladder, but it carries no cap and must never displace a live DNSP limit. */
function exportControlNow(nowMs = Date.now()): { capW: number; source: 'dnsp' | 'backstop' | 'default'; commandId: string } {
  const cap = mockCommands(nowMs).candidates.find((c) => c.setpoint.kind === 'exportLimit')
  if (cap && cap.setpoint.kind === 'exportLimit')
    return { capW: cap.setpoint.exportLimitW, source: cap.priority === 'backstop' ? 'backstop' : 'dnsp', commandId: cap.commandId }
  return { capW: profileForSiteDnsp().defaultExportLimitW, source: 'default', commandId: 'default' }
}

/** The VPP battery-power target the device is EXECUTING (kW, + discharge / − charge), or
 *  null. Mirror of the cloud's command.resolveVppTargetKw — deliberately NOT gated on
 *  'vpp' topping the ladder: an export CAP is a different DIMENSION (it bounds what may
 *  LEAVE the site; it does not command the battery), so it CONSTRAINS this target rather
 *  than cancelling it. Gating on rank would mean a dispatch was logged, acked and
 *  baselined while the battery quietly did nothing for the whole daytime window a routine
 *  DNSP limit is in force — a dispatch that claims delivery and delivers none. The cap is
 *  applied where both halves are known: the execution-time re-screen in batteryPowerAt. */
function vppTargetNow(nowMs = Date.now()): number | null {
  const vpp = mockCommands(nowMs).candidates.find((c) => c.priority === 'vpp')
  return vpp && vpp.setpoint.kind === 'power' ? vpp.setpoint.targetKw : null
}

/** The export cap the VPP dispatch is RE-SCREENED against at execution (GRI-VPP-01) — so
 *  a cap that TIGHTENS mid-event (a DNSP slot change, or an emergency backstop arriving)
 *  clamps it immediately instead of trusting the issue-time screen to still hold. */
const vppCapNow = (nowMs = Date.now()): number => exportControlNow(nowMs).capW

/** The partner's scope gate (ICD-API-02). A partner grant must have been minted, and
 *  must carry the scope. Mirrors the cloud's requirePartner — the scope gates the ROUTE;
 *  the homeowner's consent gates the ROWS (mockPartnerFleet). Both must hold. */
function requirePartnerScope(scope: string): void {
  if (!partnerGranted) throw new Error('403 This endpoint requires a VPP partner credential')
  if (!VPP_PARTNER.scopes.includes(scope)) throw new Error(`403 The partner grant lacks the "${scope}" scope`)
}

/** The partner's fleet, CONSENT-GATED AT THE DATA LAYER (CLD-DAT-03) — mirror of the
 *  cloud's store.partnerFleet. The live site is included ONLY while its VPP consent is
 *  active AND granted to THIS partner; a withheld site is ABSENT, never a redacted row,
 *  and shows only in the count. */
function mockPartnerFleet(nowMs = Date.now()): { rows: VppFleetSite[]; withheld: number; live: VppFleetSite | null } {
  const sandbox = consentedSimFleet()
  const c = consents.find((x) => x.party === 'VPP')
  const grantedToUs = c?.status === 'active' && c.name === VPP_PARTNER.name
  if (!grantedToUs) return { rows: sandbox.rows, withheld: sandbox.withheld + 1, live: null }
  const ctrl = exportControlNow(nowMs)
  const pf = ticker.snapshot()
  const live: VppFleetSite = {
    siteId: seedSite.id,
    label: seedSite.name,
    postcode: /\b(\d{4})\b/.exec(seedSite.address ?? '')?.[1] ?? '—',
    dnspZone: `${seedSite.dnsp} — live`,
    program: VPP_PARTNER.program,
    online: true,
    socPct: Math.round(pf.soc),
    gridLimitW: ctrl.capW,
    gridLimitSource: ctrl.source,
    simulated: false, // the one REAL (store-backed) site — the rest are sandbox
  }
  return { rows: [live, ...sandbox.rows], withheld: sandbox.withheld, live }
}

/** THE mock dispatch — the offline mirror of the cloud's store.dispatchVppEvent:
 *  cohort (through the consent gate) → screen (GRI-VPP-01) → issue a 'vpp'-priority
 *  command to the REAL site → homeowner surfaces → baseline → event + webhook record. */
function runMockDispatch(req: VppDispatchRequest, nowMs: number): VppEvent {
  const fleet = mockPartnerFleet(nowMs)
  const cohort = fleet.rows.filter((s) => inCohort(s, req.cohort))
  const startMs = nowMs
  const endMs = nowMs + req.durationMinutes * 60_000
  const eventId = `vppe-${VPP_PARTNER.id}-${nowMs}`
  const head = {
    eventId, partnerName: VPP_PARTNER.name, action: req.action, startMs, endMs,
    requestedKw: round(req.targetKw),
  }
  const results: VppSiteResult[] = []

  // A site the partner named EXPLICITLY that the consent gate did not return: refused
  // with the reason code (CLD-VPP-03), disclosing nothing beyond the id it supplied.
  for (const id of req.cohort.siteIds ?? []) {
    if (!cohort.some((s) => s.siteId === id)) {
      results.push({
        ...head, siteId: id, label: id, outcome: 'refused', dispatchedKw: 0,
        gridLimitW: 0, gridLimitSource: 'default', reasonCode: 'consent_revoked',
        reason: 'The site has no active VPP consent — it is not in your cohort',
      })
    }
  }

  const pf = ticker.snapshot()
  for (const site of cohort) {
    const isLive = !site.simulated
    const sim = VPP_SIM_FLEET.find((x) => x.siteId === site.siteId)
    const floor = Math.max(battery.dischargeFloorPct, battery.backupReservePct)
    const screen = screenDispatch({
      action: req.action,
      targetKw: req.targetKw,
      socPct: site.socPct,
      reserveFloorPct: isLive ? floor : 20,
      chargeCeilingPct: isLive ? battery.chargeCeilingPct : 100,
      online: site.online,
      gridLimitW: site.gridLimitW,
      gridLimitSource: site.gridLimitSource,
      netLoadKw: isLive ? round(pf.loadKw - pf.solarKw) : (sim?.netLoadKw ?? 0),
      // Invariant #5 from the ECONOMIC-mode side: backup/islanding outranks VPP.
      higherTier: isLive && modeOn('backup') ? 'backup' : null,
    })
    const r: VppSiteResult = {
      ...head,
      siteId: site.siteId,
      label: site.label,
      outcome: screen.outcome,
      dispatchedKw: screen.dispatchedKw,
      gridLimitW: site.gridLimitW,
      gridLimitSource: site.gridLimitSource,
      reasonCode: screen.reasonCode,
      reason: screen.reason,
    }
    if (isLive) {
      // GRI-VPP-02 — the settlement baseline. The offline mock has no telemetry ring at
      // all, so the baseline is the battery power right before the event, and the note
      // says so. Overstating this as settlement-grade evidence would be a lie.
      r.baselineKw = round(pf.batteryKw)
      r.baselineSamples = 1
      r.deliveredKwh = 0
      r.baselineNote =
        'Offline mock: baseline = the battery power in the instant before the event (1 sample). The cloud demo averages a 10-min pre-event window from its 20-frame telemetry ring; settlement-grade baselining needs interval meter data.'
      if (screen.outcome !== 'refused') {
        // Issue the 'vpp'-priority command — the SAME slot every reader ranks (invariant
        // #5), so a grid limit arriving mid-event instantly out-ranks it.
        r.commandId = `cmd-vpp-${eventId}-${site.siteId}`
        vppCommand = {
          v: 1,
          commandId: r.commandId,
          seq: (vppCommand?.seq ?? 0) + 1,
          siteId: site.siteId,
          issuedAt: startMs,
          expiresAt: endMs,
          priority: 'vpp',
          setpoint: { kind: 'power', targetKw: screen.dispatchedKw },
          source: 'vpp',
          reason: `${VPP_PARTNER.name} dispatch — ${screen.reason}`,
          partner: VPP_PARTNER.name, // provenance — lets the banner NAME the partner
        }
      }
      raiseMockVppSurfaces(r)
    } else {
      r.baselineNote = 'Sandbox site — simulated fleet, no telemetry.'
    }
    results.push(r)
  }

  const ev: VppEvent = {
    id: eventId,
    partnerId: VPP_PARTNER.id,
    partnerName: VPP_PARTNER.name,
    cohort: req.cohort,
    action: req.action,
    targetKw: round(req.targetKw),
    startMs,
    endMs,
    reason: req.reason?.trim() || `${VPP_PARTNER.name} ${req.action} dispatch — ${round(req.targetKw)} kW for ${req.durationMinutes} min`,
    results,
    createdAt: nowMs,
  }
  vppEvents = appendCapped(vppEvents, ev, 20, (e) => e.id).list
  vppWebhooks = appendCapped(vppWebhooks, {
    id: `whk-${ev.id}`,
    eventId: ev.id,
    url: VPP_PARTNER.webhookUrl,
    deliveredAt: nowMs,
    status: 'recorded' as const, // honest: no partner server to POST to
    payload: {
      type: 'dispatch.completed' as const,
      eventId: ev.id,
      partnerId: VPP_PARTNER.id,
      summary: {
        sites: results.length,
        accepted: results.filter((x) => x.outcome === 'accepted').length,
        clamped: results.filter((x) => x.outcome === 'clamped').length,
        refused: results.filter((x) => x.outcome === 'refused').length,
        dispatchedKw: round(results.reduce((sum, x) => sum + Math.abs(x.dispatchedKw), 0)),
      },
      results,
    },
  }, 20, (w) => w.id).list
  return ev
}

/** The homeowner surfaces a VPP dispatch drives (mirror of store.raiseVppSurfaces): a
 *  notification naming the partner + its subordination + the opt-out route, an ADM record
 *  (category 'vpp') linked to the REVOCABLE VPP consent, and the per-site record. Ids key
 *  on (event, site) so a re-raise is idempotent. */
function raiseMockVppSurfaces(r: VppSiteResult): void {
  const key = `${r.eventId}-${r.siteId}`
  const kw = Math.abs(r.dispatchedKw)
  const verb = r.action === 'charge' ? 'charging' : 'discharging'
  // Rendered in the RESIDENCE's zone, like every other wall clock in this mock —
  // an event that ends at 6:50 pm at the site must not read as 8:50 am because
  // the browser is elsewhere.
  const endLabel = clockAtSite(r.endMs)
  const body =
    r.outcome === 'refused'
      ? `${r.partnerName} called a dispatch event, but it was not applied to your system: ${r.reason}. Your battery keeps running as normal.`
      : `${r.partnerName} is ${verb} your battery at ${kw} kW until ${endLabel} as part of a grid-services event.${r.outcome === 'clamped' ? ` The request was reduced to fit your grid limits — ${r.reason.toLowerCase()}.` : ''} A VPP dispatch is subordinate to your DNSP export limit and any emergency backstop — those always take precedence. You can opt out at any time by revoking the VPP consent in the consent centre.`
  pushNotif(
    r.outcome === 'refused' ? `VPP dispatch declined — ${r.partnerName}` : `VPP dispatch — ${r.partnerName}`,
    body,
    { category: 'grid', severity: 'info', id: `n-vpp-${key}` },
  )
  const consentId = consents.find((c) => c.party === 'VPP')?.id ?? 'c-2'
  const startLabel = clockAtSite(r.startMs)
  const adm: AdmDecision = {
    id: `adm-vpp-${key}`,
    title: r.outcome === 'refused' ? 'VPP dispatch declined' : 'Battery dispatched for a VPP event',
    category: 'vpp',
    whenLabel: 'Just now',
    what:
      r.outcome === 'refused'
        ? `Dispatch declined — ${r.reason}`
        : `${r.action === 'charge' ? 'Charged' : 'Discharged'} the battery at ${kw} kW for the dispatch window`,
    timeRange: `${startLabel} – ${endLabel}`,
    // The labels above are a rendering; these three are the record (see AdmDecision).
    startMs: r.startMs,
    endMs: r.endMs,
    timeZone: MOCK_SITE_TIME_ZONE,
    why: `${r.partnerName} called a grid-services dispatch; the cloud screened it against your ${r.gridLimitSource === 'backstop' ? 'emergency backstop' : 'DNSP export limit'} before issuing it`,
    dataUsed: 'SoC, backup reserve, export limit, dispatch signal',
    consentId,
  }
  liveAdm = appendCapped(liveAdm, adm, 10, (d) => d.id).list
  vppRecords = appendCapped(vppRecords, r, 10, (x) => `${x.eventId}-${x.siteId}`).list
}

/** The controlling-authority banner an active setpoint command imposes (mirrors the
 *  cloud's command.authorityFromCommand). A backstop reads red + names the issuing
 *  authority (GRI-BST-03); a routine dynamic export limit reads blue; a VPP dispatch
 *  reads green and states its SUBORDINATION plus the one control that ends it. */
function commandAuthority(cmd: SetpointCommand): Authority {
  if (cmd.setpoint.kind === 'power') {
    const kw = Math.abs(cmd.setpoint.targetKw)
    const verb = cmd.setpoint.targetKw >= 0 ? 'discharging' : 'charging'
    return {
      authority: cmd.priority,
      title: 'VPP dispatch',
      detail: `${cmd.reason} — ${verb} at ${kw} kW. Subordinate to your grid export limit and any emergency backstop; opt out by revoking the VPP consent.`,
      tone: 'green',
    }
  }
  const w = cmd.setpoint.exportLimitW
  const limitKw = (w / 1000).toFixed(w % 1000 === 0 ? 0 : 1)
  const detail =
    w === 0
      ? `${cmd.reason} — zero-export in force; surplus solar is curtailed`
      : `${cmd.reason} — exports are capped at ${limitKw} kW; surplus solar is curtailed`
  const backstop = cmd.priority === 'backstop'
  return { authority: cmd.priority, title: backstop ? 'Emergency backstop' : 'Grid export limit', detail, tone: backstop ? 'red' : 'blue' }
}

/** THE authority banner, resolved from the whole RANKED set (mirror of the cloud's
 *  command.authorityFromCommands). The winner IS the controlling authority (invariant
 *  #5), so it sets the title and tone — when a DNSP limit and a VPP dispatch are both in
 *  force the banner says DNSP, because that is what is really in charge. But a dispatch
 *  the homeowner's battery is EXECUTING still concerns them even when out-ranked, so it
 *  is NAMED with its subordination stated. Neither hidden, nor masquerading as the
 *  authority: invariant #5 made legible rather than merely true. */
function authorityFromCommands(candidates: SetpointCommand[]): Authority {
  const top = candidates[0]
  const banner = commandAuthority(top)
  const vpp = candidates.find((c) => c.priority === 'vpp' && c.commandId !== top.commandId)
  if (!vpp || vpp.setpoint.kind !== 'power') return banner
  const kw = Math.abs(vpp.setpoint.targetKw)
  const verb = vpp.setpoint.targetKw >= 0 ? 'discharge' : 'charge'
  const who = vpp.partner ?? 'Your VPP partner'
  return {
    ...banner,
    detail: `${banner.detail}. ${who} also has a ${kw} kW ${verb} dispatch in force — it is SUBORDINATE to this limit and can never push your export past it.`,
  }
}

/** A DERStatus the cloud would relay to the DNSP (GRI-CSP-02 return), reconciled from
 *  the live sim telemetry + the control in force. The command it names is the GRID one
 *  (the highest-ranked ACTIVE export-cap command), never simply the effective command:
 *  from Phase 12 the effective command may be a 'vpp' power target, and a DNSP wants to
 *  hear about the export limit IT issued, not about a VPP dispatch it has no part in. */
function buildMockDerStatus(nowMs = Date.now()): DerStatusReport {
  const grid = mockCommands(nowMs).candidates.find((c) => c.setpoint.kind === 'exportLimit') ?? null
  const ctrl = exportControlNow(nowMs)
  const pf = ticker.snapshot()
  const appliedKw = Math.min(Math.max(0, -pf.gridKw), ctrl.capW / 1000)
  return {
    reportedAt: nowMs,
    siteId: seedSite.id,
    commandId: grid?.commandId ?? null,
    derControlId: grid?.derControlId,
    exportLimitW: ctrl.capW,
    appliedW: Math.round(appliedKw * 1000),
    online: true,
    opState: ctrl.source === 'default' ? 'default' : 'curtailed',
    source: ctrl.source,
  }
}

// ---- daily power profile (deterministic shape, used for charts) ----
function solarAt(hour: number): number {
  if (hour < 6 || hour > 19.5) return 0
  const x = (hour - 12.75) / 4.2
  return Math.max(0, 5.1 * Math.exp(-x * x))
}
function loadAt(hour: number): number {
  let l = 0.35
  l += 1.4 * Math.exp(-(((hour - 7.5) / 1.1) ** 2)) // morning peak
  l += 2.2 * Math.exp(-(((hour - 19) / 1.6) ** 2)) // evening peak
  l += 0.25 * Math.exp(-(((hour - 13) / 2) ** 2)) // midday
  return l
}

const MAX_KW = 3.5
/** Battery power (+ discharge / − charge, kW) at an instant under the effective
 *  mode + schedule windows. Mirrors the cloud simulator's batteryPowerAt, including the
 *  Phase-12 VPP tier. `vppDispatchKw` defaults to the dispatch in force RIGHT NOW (the
 *  live path); the day profile passes `null` explicitly — see dayProfile. */
function batteryPowerAt(hour: number, s: number, vppDispatchKw: number | null = vppTargetNow()): number {
  const w = windows()
  const mode = effectiveModeAt(hour)
  const solar = solarAt(hour)
  const load = loadAt(hour)
  let bat = 0
  if (mode === 'backup') {
    bat = -Math.min(MAX_KW, Math.max(0, (solar - load) * 0.85))
  } else if (mode === 'tou') {
    const cw = matchWin(hour, w.charge)
    if (cw) {
      // Charge at the window's power up to its target SoC, never above the safe
      // ceiling (invariant #1: a window is only ever more conservative).
      const target = Math.min(cw.targetSocPct, battery.chargeCeilingPct)
      bat = s >= target ? 0 : -Math.min(cw.powerKw, MAX_KW)
    } else {
      // A TOU discharge window is a forced kW setpoint, not merely a request to
      // cover household shortfall. Continue discharging when solar covers the
      // home, until the target SoC; the device/grid layer constrains export.
      const dw = matchWin(hour, w.discharge)
      const effFloor = Math.max(battery.dischargeFloorPct, battery.backupReservePct)
      const floor = Math.max(dw?.targetSocPct ?? effFloor, effFloor)
      const cap = Math.min(dw?.powerKw ?? MAX_KW, MAX_KW)
      bat = s <= floor ? 0 : cap
    }
  } else if (mode === 'self') {
    bat = Math.max(-MAX_KW, Math.min(MAX_KW, (load - solar) * 0.85))
  }

  // ---- VPP dispatch (Phase 12) — invariant #5 realised IN the dispatch stack (mirror
  // of the cloud simulator's batteryPowerAt). A dispatched VPP setpoint OUTRANKS every
  // economic mode AND the two behaviour toggles below (which is why they are gated on
  // it), but is itself OUTRANKED by 'backup' — islanding holds the reserve for an
  // outage. Precedence high→low: safety › backstop › dnsp › backup › VPP › economic.
  // The dispatch has ALREADY been screened against the export limit in force
  // (GRI-VPP-01, screenDispatch), so executing it can never breach the cap.
  const vppKw = vppDispatchKw ?? 0
  const vppOn = vppKw !== 0 && mode !== 'backup'
  if (vppOn) {
    let kw = vppKw > 0 ? Math.min(vppKw, MAX_KW) : Math.max(vppKw, -MAX_KW)
    if (kw > 0) {
      // RE-SCREEN against the cap in force RIGHT NOW (GRI-VPP-01, defence in depth).
      // Headroom = the export cap + whatever the house is drawing: the house takes its
      // net draw first and only the remainder reaches the grid. So a zero-export backstop
      // still permits powering the HOME — it caps export, not self-consumption.
      const headroomKw = Math.max(0, vppCapNow()) / 1000 + Math.max(0, load - solar)
      kw = Math.min(kw, headroomKw)
    }
    bat = kw
  }

  const floor = Math.max(battery.dischargeFloorPct, battery.backupReservePct)
  // Behaviour toggles (APP-HOM-20) — mirror of the cloud simulator. Peak shaving:
  // inside the demand window, discharge to cover the net grid draw. Zero export:
  // absorb surplus into the battery (curtailed at the flow calc when full). Both are
  // ECONOMIC behaviours, so a live VPP dispatch (a higher tier) suppresses them.
  const dw = demandWin()
  if (!behaviour.batteryHold && !vppOn && behaviour.peakShave && dw && inWin(hour, dw) && s > floor) {
    const grid = load - solar - bat
    if (grid > 0) bat = Math.min(MAX_KW, bat + grid)
  }
  if (!behaviour.batteryHold && !vppOn && behaviour.zeroExport && s < battery.chargeCeilingPct) {
    const grid = load - solar - bat
    if (grid < 0) bat = Math.max(-MAX_KW, bat + grid)
  }
  // SoC clamps — simulation inputs only, never safety limits (invariant #1). They bound
  // a VPP dispatch too: an event never discharges below the reserve nor charges above
  // the ceiling, whatever the partner asked for.
  if (bat > 0 && s <= floor) bat = 0
  if (bat < 0 && s >= battery.chargeCeilingPct) bat = 0
  return bat
}

/** Synthesise the day's 5-min battery power (charge -, discharge +) and SoC %.
 *  A VPP dispatch is deliberately EXCLUDED (vppDispatchKw = null), exactly as in the
 *  cloud simulator: the day profile is a SHAPE over all 24 h, while a dispatch is an
 *  instantaneous, minutes-long event — applying it to every sample would smear one
 *  30-minute event across the whole day and misreport the day's energy. The LIVE ticker
 *  honours it, which is where a dispatch belongs. */
function dayProfile(samples = 96): { power: number[]; soc: number[] } {
  const power: number[] = []
  const soc: number[] = []
  let s = 32 // start-of-day SoC
  const usable = battery.usableKwh
  for (let i = 0; i < samples; i++) {
    const hour = (i / samples) * 24
    const bat = batteryPowerAt(hour, s, null)
    const dtHours = 24 / samples
    s = Math.max(0, Math.min(100, s - (bat * dtHours) / usable * 100))
    power.push(Math.round(bat * 100) / 100)
    soc.push(Math.round(s * 10) / 10)
  }
  return { power, soc }
}

/** The $/kWh a discharged kWh displaces (mirror of the cloud marginalRate). */
function marginalRate(): number {
  if (tariff.type === 'flat') return tariff.flatRateAud ?? 0.3
  if (tariff.type === 'tiered') {
    const t = tariff.tiers ?? []
    return t.length ? t[t.length - 1].rateAud : 0.3
  }
  return tariff.periods.reduce((m, p) => Math.max(m, p.rateAud), 0)
}

function energyFor(period: EnergyPeriod): EnergySummary {
  const { power, soc } = dayProfile()
  // integrate the day profile into kWh totals
  let charged = 0
  let discharged = 0
  const dt = 24 / power.length
  for (const p of power) (p < 0 ? (charged -= p * dt) : (discharged += p * dt))
  const scale = period === 'day' ? 1 : period === 'week' ? 7 : period === 'month' ? 30.4 : 365
  const solarKwh = round(8.74 * scale)
  const loadKwh = round(9.6 * scale)
  const exportKwh = round(4.44 * scale)
  const importKwh = round(0.31 * scale)
  const selfConsumed = round(solarKwh - exportKwh)
  const selfSuff = Math.round((1 - importKwh / loadKwh) * 100)
  const savings = round((discharged * marginalRate() + (exportKwh / scale) * (tariff.feedInAud ?? 0.05)) * scale)

  const n = period === 'day' ? 24 : period === 'week' ? 7 : period === 'month' ? 30 : 12
  const buckets = Array.from({ length: n }, (_, i) => {
    const base = period === 'day' ? Math.max(0, solarAt(i)) : 1
    const wobble = 0.6 + 0.8 * Math.abs(Math.sin(i * 1.7))
    return {
      label: bucketLabel(period, i),
      selfKwh: round(base * 0.9 * wobble + 0.4),
      importKwh: round(0.2 * wobble),
      exportKwh: round(base * 0.6 * wobble),
    }
  })

  return {
    period,
    solarKwh, loadKwh, importKwh, exportKwh,
    chargedKwh: round(charged * scale), dischargedKwh: round(discharged * scale),
    selfConsumedKwh: selfConsumed, selfSufficiencyPct: selfSuff,
    savingsAud: savings, buckets, socLine: soc, powerSamples: power,
  }
}

function bucketLabel(period: EnergyPeriod, i: number): string {
  if (period === 'day') return `${String(i).padStart(2, '0')}:00`
  if (period === 'week') return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i] ?? ''
  if (period === 'month') return String(i + 1)
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i] ?? ''
}
const round = (n: number) => Math.round(n * 100) / 100

// ---- live power-flow ticker ----
class LiveTicker {
  private subs = new Set<(pf: PowerFlow) => void>()
  private timer: ReturnType<typeof setInterval> | null = null
  private last = Date.now()
  private soc = battery.soc

  private compute(): PowerFlow {
    // The RESIDENCE's hour drives the solar/load curves and the dispatch — the
    // host's zone used to, so the same demo showed midday solar at midnight on a
    // laptop set to another continent.
    const hour = siteHourOfDay()
    const jitter = (a: number) => a * (0.92 + Math.random() * 0.16)
    let solar = round(jitter(solarAt(hour)))
    const load = round(jitter(loadAt(hour)))
    // Dispatch through the same mode+schedule logic as the day profile and the
    // cloud simulator, so live SoC/power reflect the enabled TOU windows too.
    let bat = batteryPowerAt(hour, this.soc)
    const requestedBat = bat
    let grid = round(load - solar - bat)
    // Mirror the device export governor: preserve a forced request by curtailing PV
    // first, then clamp discharge if PV has reached zero and export is still high.
    const exportControl = exportControlNow()
    const exportCapKw = Math.max(0, exportControl.capW) / 1000
    const overshootKw = Math.max(0, -grid - exportCapKw)
    if (overshootKw > 0) {
      const solarCurtailKw = Math.min(Math.max(0, solar), overshootKw)
      solar = round(Math.max(0, solar - solarCurtailKw))
      const remainingKw = Math.max(0, overshootKw - solarCurtailKw)
      if (bat > 0) bat = round(Math.max(0, bat - remainingKw))
      grid = round(load - solar - bat)
    }
    const constrained = Math.abs(requestedBat - bat) > 0.01
    const dtH = (Date.now() - this.last) / 3.6e6
    this.last = Date.now()
    this.soc = Math.max(0, Math.min(100, this.soc - (bat * dtH) / battery.usableKwh * 100))
    battery.soc = Math.round(this.soc)
    battery.socKwh = round((this.soc / 100) * battery.usableKwh)
    // Zero export (APP-HOM-20): curtail surplus the battery couldn't absorb so the
    // live flow shows no grid export (mirror of the cloud simulator).
    if (behaviour.zeroExport && grid < 0) {
      solar = round(solar + grid)
      grid = 0
    }
    return {
      ts: Date.now(),
      solarKw: solar,
      loadKw: load,
      batteryKw: round(bat),
      batteryRequestedKw: round(requestedBat),
      batteryControlResult: constrained ? 'clamped' : 'applied',
      batteryConstraintReasons: constrained ? ['grid_export_limit'] : [],
      exportLimitW: exportControl.capW,
      exportLimitSource: exportControl.source,
      gridKw: grid,
      soc: Math.round(this.soc),
      socKwh: round((this.soc / 100) * battery.usableKwh),
      updatedSecondsAgo: 1,
    }
  }

  subscribe(cb: (pf: PowerFlow) => void): () => void {
    cb(this.compute())
    this.subs.add(cb)
    if (!this.timer) {
      this.timer = setInterval(() => {
        const pf = this.compute()
        this.subs.forEach((s) => s(pf))
      }, 1500)
    }
    return () => {
      this.subs.delete(cb)
      if (this.subs.size === 0 && this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    }
  }

  snapshot(): PowerFlow {
    return this.compute()
  }
}
const ticker = new LiveTicker()

// ===========================================================================
// Stage 4.4 — the device-command lifecycle, mirrored (Stage 3.3 taxonomy)
// ===========================================================================
//
// The mock does not "fake success". It reproduces the STATE MACHINE of
// `cloud/src/deviceCommands.ts` closely enough that a screen built against it
// cannot pass on semantics the cloud refuses — the same rule the Installer
// Organisation mirror already follows in this file.
//
// The four rules that survive the port, because they are the ones a UI can lie
// about:
//   1. REQUESTED IS NOT APPLIED. `appliedValue` is null until a simulated device
//      answer sets it, and the only place it is set is `answerCommand`.
//   2. QUEUED IS NOT DELIVERED (APP-SEC-03). An OFFLINE device is never handed
//      the command, so the record stays `requested` and eventually `expired` —
//      it never passes through `delivered`, and the app can tell the difference.
//   3. THE CLOUD MAY DECIDE `superseded`/`expired`; everything else is the
//      device's word. A newer command for the same device + control retires an
//      earlier UNDELIVERED one and nothing else.
//   4. A CLAMP NAMES THE TIER THAT WON. A homeowner SoC request outside the
//      BMS safe-operating area comes back `clamped` with `constraintTier:
//      'safety'` — invariant #1 seen from the app.
//
// DETERMINISTIC TIMING. Resolution is a pure function of elapsed wall-clock ms,
// evaluated lazily whenever the log is read: submit → (+DELIVER) delivered →
// (+ANSWER) terminal, and an undeliverable command expires at +TTL. No timers,
// no randomness, so an E2E can assert `pending` immediately after the submit and
// then wait for exactly one transition rather than racing a scheduler.

/** Handed to a (simulated) authenticated dongle this long after submission. */
export const MOCK_COMMAND_DELIVER_MS = 350
/** The device answers this long after submission. */
export const MOCK_COMMAND_ANSWER_MS = 1400
/** TTL. An offline device's command expires here rather than waiting forever.
 *  THE CLOUD'S VALUE (`cloud/src/deviceCommands.ts` COMMAND_TTL_MS = 5 min), not a
 *  demo-shortened one: "your device did not come online before this change timed
 *  out" is a sentence a homeowner reads, and saying it four seconds after they
 *  pressed a switch would be the mock inventing an outcome. A test that needs the
 *  expiry moves `advanceMockClock` instead of waiting. */
export const MOCK_COMMAND_TTL_MS = 5 * 60_000
/** How long an `unverified` answer waits before the same command is re-armed for
 *  another delivery attempt (the mock's stand-in for the cloud's downlink retry). */
export const MOCK_COMMAND_RETRY_MS = 1200
/** Mirror of the cloud's MAX_UNVERIFIED_RETRIES. When the budget is gone the mock
 *  stops re-arming and the record STAYS `unverified` — the honest end state — with
 *  `abandonedAtMs` recording that the retrying, not the uncertainty, is what ended. */
export const MOCK_MAX_UNVERIFIED_RETRIES = 1
/** Flaky online inverter for `unverified` honesty (economic delivery target). */
export const MOCK_UNVERIFIED_DEVICE_ID = 'd-2-flaky'
/** Primary inverter with open downlink for supersession/divergence fixtures. */
export const MOCK_DIVERGENT_DEVICE_ID = 'd-2'
/** The BMS safe-operating-area bounds the device clamps a homeowner request to.
 *  Preferences live INSIDE these; they are not remotely writable (invariant #1). */
export const MOCK_DEVICE_CHARGE_CEILING_PCT = 95
export const MOCK_DEVICE_DISCHARGE_FLOOR_PCT = 5

/** The control axes a homeowner's economic tier may issue, with their domains.
 *  `export_limit` is deliberately ABSENT: it is a grid constraint on another
 *  tier, and the mock refuses it exactly as the cloud registry does. */
const MOCK_HOMEOWNER_CONTROLS: Record<HomeownerControl, { kind: 'number' | 'enum'; min?: number; max?: number; values?: string[]; unit?: string }> = {
  working_mode: { kind: 'enum', values: ['self', 'backup', 'tou', 'grid-feeding', 'off-grid'] },
  battery_min_soc: { kind: 'number', min: 0, max: 100, unit: '%' },
  battery_max_soc: { kind: 'number', min: 0, max: 100, unit: '%' },
}

interface MockCommandRecord {
  commandId: string
  seq: number
  deviceId: string
  residenceId: string
  control: DeviceControl
  requestedValue: CommandValue
  appliedValue: CommandValue | null
  issuedAtMs: number
  expiresAtMs: number
  status: CommandStatus
  reason: CommandReason | null
  constraintTier: ControlTier | null
  supersededBy: string | null
  attempts: number
  /** How many times an `unverified` answer has re-armed this command (cloud
   *  `retries`). Bounded by MOCK_MAX_UNVERIFIED_RETRIES. */
  retries: number
  /** When the mock stopped re-arming, having spent the retry budget. The record is
   *  still `unverified`: what ended is the retrying, not the uncertainty. */
  abandonedAtMs: number | null
  /** When the device acted on a record the cloud had already retired. */
  divergence: string | null
  /** The mock's model of the downlink: true once the (simulated) dongle has the
   *  frame, which is what makes a post-supersession device answer possible. */
  downlinkTaken: boolean
  authority: CommandAuthority
  history: CommandHistoryEntry[]
  resolvedAtMs: number | null
  /** When the next delivery attempt becomes due — re-armed after `unverified`. */
  deliverAtMs: number
  /** When the device's answer to the CURRENT attempt lands. */
  answerAtMs: number
}

let deviceCommandLog: MockCommandRecord[] = []
let deviceCommandSeq = 0

const TERMINAL: CommandStatus[] = ['applied', 'clamped', 'rejected', 'superseded', 'expired']

const commandTransition = (record: MockCommandRecord, to: CommandStatus, atMs: number, note: string): void => {
  record.status = to
  record.history.push({ atMs, status: to, note })
  if (TERMINAL.includes(to)) record.resolvedAtMs = atMs
}

/** Is the target device reachable RIGHT NOW? A command for an unreachable device
 *  is never delivered — that is the whole point of keeping `requested` and
 *  `delivered` apart (APP-SEC-03) and is what makes the expiry case honest. */
function deviceReachable(deviceId: string): boolean {
  const device = devices.find((d) => d.id === deviceId)
  if (!device) return false
  return device.status === 'online' || device.status === 'standby'
}

/**
 * What the (simulated) device does about one command. Pure with respect to the
 * record: it reads the world (backstop window, SOA bounds) and returns the
 * device's word. Every branch names a CLASSIFIED reason — "denied" is not a
 * diagnosis, and a clamp that cannot say which tier won is not much better.
 */
function mockDeviceAnswer(record: MockCommandRecord, nowMs: number): {
  result: Extract<CommandStatus, 'applied' | 'clamped' | 'rejected' | 'unverified'>
  appliedValue: CommandValue | null
  reason: CommandReason | null
  constraintTier: ControlTier | null
} {
  // Precedence first (invariant #5). An emergency backstop window out-ranks every
  // economic command — including a flaky bus: under backstop the device refuses
  // rather than half-applying or answering "unverified".
  if (backstopWindow && backstopWindow.active && backstopWindow.endMs > nowMs)
    return { result: 'rejected', appliedValue: null, reason: 'precedence', constraintTier: 'backstop' }

  // SOA clamp is a definite device answer and outranks flaky-bus uncertainty for
  // SoC preferences (invariant #1).
  if (record.control === 'battery_max_soc' && Number(record.requestedValue) > MOCK_DEVICE_CHARGE_CEILING_PCT)
    return { result: 'clamped', appliedValue: MOCK_DEVICE_CHARGE_CEILING_PCT, reason: 'precedence', constraintTier: 'safety' }
  if (record.control === 'battery_min_soc' && Number(record.requestedValue) < MOCK_DEVICE_DISCHARGE_FLOOR_PCT)
    return { result: 'clamped', appliedValue: MOCK_DEVICE_DISCHARGE_FLOOR_PCT, reason: 'precedence', constraintTier: 'safety' }

  // THE FLAKY BUS. The write went out and the read-back did not come back, so the
  // device cannot say what it holds. `unverified` is that, named — and it is NOT
  // an apply result: nothing is reported as applied (rule 1).
  if (record.deviceId === MOCK_UNVERIFIED_DEVICE_ID)
    return { result: 'unverified', appliedValue: null, reason: 'bus_unreachable', constraintTier: null }

  return { result: 'applied', appliedValue: record.requestedValue, reason: null, constraintTier: null }
}

/** Advance every non-terminal record to where the clock says it should be. Called
 *  at the top of every read and every submit, so no caller can observe a live
 *  command whose TTL has passed. */
function settleDeviceCommands(nowMs = mockNow()): void {
  // TO A FIXED POINT, not one step per read. The header promises resolution is a
  // pure function of elapsed time, and with retries in the machine a single jump
  // can be worth several transitions (deliver → unverified → re-deliver →
  // unverified → expire). Advancing one step per read would make the answer depend
  // on how often somebody looked, which is the opposite of that promise. The bound
  // is the retry budget plus the fixed rungs of the ladder, so it always converges.
  const maxSteps = 4 * (MOCK_MAX_UNVERIFIED_RETRIES + 2)
  for (let step = 0; step < maxSteps; step += 1) {
    if (!settleDeviceCommandsOnce(nowMs)) return
  }
}

/** One pass. Returns true when it changed something, so the caller can run it
 *  again until the log is quiet. */
function settleDeviceCommandsOnce(nowMs: number): boolean {
  let changed = false
  for (const record of deviceCommandLog) {
    const before = `${record.status}|${record.attempts}|${record.retries}|${record.abandonedAtMs}|${record.divergence}`
    settleOneCommand(record, nowMs)
    if (`${record.status}|${record.attempts}|${record.retries}|${record.abandonedAtMs}|${record.divergence}` !== before) changed = true
  }
  return changed
}

function settleOneCommand(record: MockCommandRecord, nowMs: number): void {
  // RULE 4 (cloud/src/deviceCommands.ts): a record the CLOUD retired can still be
  // acted on by the DEVICE, because the frame had already left the building. The
  // cloud does not overwrite its own story and does not hide the device's — it
  // records BOTH, and the disagreement is stated rather than resolved.
  if (record.status === 'superseded' && record.downlinkTaken && record.appliedValue === null
    && record.divergence === null && nowMs >= record.answerAtMs) {
    const answer = mockDeviceAnswer(record, nowMs)
    if (answer.result === 'applied' || answer.result === 'clamped') {
      record.appliedValue = answer.appliedValue
      record.reason = answer.reason
      record.constraintTier = answer.constraintTier
      record.divergence = `device reported ${answer.result} for a command the cloud had already recorded as superseded`
      commandTransition(record, answer.result, record.answerAtMs, `device reported ${answer.result} after supersession — divergence recorded`)
    }
    return
  }
  if (TERMINAL.includes(record.status)) return
  const reachable = deviceReachable(record.deviceId)
  if (reachable && !record.abandonedAtMs && (record.status === 'requested' || record.status === 'unverified') && nowMs >= record.deliverAtMs) {
    record.attempts += 1
    record.downlinkTaken = true
    record.answerAtMs = record.deliverAtMs + MOCK_COMMAND_ANSWER_MS - MOCK_COMMAND_DELIVER_MS
    commandTransition(record, 'delivered', record.deliverAtMs, `handed to the dongle (attempt ${record.attempts})`)
  }
  if (record.status === 'delivered' && nowMs >= record.answerAtMs) {
    const answer = mockDeviceAnswer(record, nowMs)
    if (answer.result === 'unverified') {
      // The one NON-TERMINAL device answer, and therefore the one that has to do
      // something rather than merely be displayed. Nothing is recorded as applied,
      // the same command is re-armed for another delivery — and the re-arming is
      // BOUNDED, because the alternative is a livelock. When the budget is gone the
      // record stays `unverified` (the truth) and says the retrying stopped.
      record.appliedValue = null
      record.reason = answer.reason
      record.constraintTier = null
      commandTransition(record, 'unverified', record.answerAtMs, `device reported unverified (${answer.reason}) — the read-back did not come back`)
      if (record.retries >= MOCK_MAX_UNVERIFIED_RETRIES) {
        record.abandonedAtMs = record.answerAtMs
        record.history.push({
          atMs: record.answerAtMs,
          status: 'unverified',
          note: `retry budget exhausted after ${record.retries} re-deliveries (${record.reason}) — no more deliveries; the device's state on this control is unknown`,
        })
      } else {
        record.retries += 1
        record.deliverAtMs = record.answerAtMs + MOCK_COMMAND_RETRY_MS
      }
    } else {
      record.appliedValue = answer.appliedValue
      record.reason = answer.reason
      record.constraintTier = answer.constraintTier
      commandTransition(record, answer.result, record.answerAtMs, `device reported ${answer.result}`)
    }
    return
  }
  // Expiry is evaluated LAST and applies to whatever is still open — which, for
  // an offline device, is a command that was never delivered at all.
  if (!TERMINAL.includes(record.status) && nowMs >= record.expiresAtMs) {
    record.reason = 'expired'
    commandTransition(record, 'expired', record.expiresAtMs, `time-to-live passed at ${record.expiresAtMs} with status ${record.status}`)
  }
}

const mockCommandView = (record: MockCommandRecord): CommandView => ({
  commandId: record.commandId,
  deviceId: record.deviceId,
  residenceId: record.residenceId,
  control: record.control,
  status: record.status,
  pending: !TERMINAL.includes(record.status),
  requested: {
    value: record.requestedValue,
    atMs: record.issuedAtMs,
    expiresAtMs: record.expiresAtMs,
    tier: 'economic',
    intent: 'command',
  },
  applied: record.appliedValue === null ? null : { value: record.appliedValue, atMs: record.resolvedAtMs ?? record.issuedAtMs },
  result: TERMINAL.includes(record.status) ? record.status : null,
  reason: record.reason,
  constraintTier: record.constraintTier,
  authority: { ...record.authority },
  attempts: record.attempts,
  supersededBy: record.supersededBy,
  divergence: record.divergence,
  history: record.history.map((entry) => ({ ...entry })),
})

// ---------------------------------------------------------------------------
// Stage 4.4 — the ACCOUNT's economic-settings profile (APP-HOM-32, CLD-CMD-04)
// ---------------------------------------------------------------------------
//
// THE DEFECT THE PROFILE CLOSES, restated for the mock: tariff, TOU windows,
// reserve and SoC limits used to be attributes of the SITE and nothing else, so
// they survived a change of owner and the second homeowner inherited the first
// one's plan without ever having chosen it.
//
// Mirroring `cloud/src/economicProfiles.ts`, the mock keeps the OWNERSHIP
// ENVELOPE around the same items and enforces the barrier on BOTH axes: the
// reader must BE the profile's account AND the profile must still be active.
// Release DEACTIVATES rather than deletes (the account keeps its own history) and
// RESETS the Residence's economic items — without that second half the profile
// would be correct and the behaviour unchanged, which is the actual failure mode.
//
// `accountId: null` is the pre-pairing world this mock boots into: settings that
// belong to whoever is signed in, exactly as before. It becomes a real account id
// the first time a pairing event happens, so the barrier exists from the moment
// there is an owner to have a barrier about.

/** The unset plan. Not a zero-rate plan — the screen must show "not set" rather
 *  than numbers nobody entered, so this is only ever reached through `isDefault`. */
const DEFAULT_TARIFF: Tariff = {
  type: 'flat',
  retailer: '',
  plan: 'Not set',
  supplyChargeAud: 0,
  feedInAud: 0,
  effectiveDate: '',
  periods: [],
  flatRateAud: 0,
  tiers: [],
  // `demand` IS LISTED, and that is the whole point. The release path applies this
  // object over the live tariff with Object.assign, which can overwrite a key but
  // cannot DELETE one — so an optional key omitted here is a key the previous
  // owner's value survives in. That is exactly what happened: the outgoing owner's
  // demand window, rate and recorded peak (4.8 kW, "12 Jun, 6:12 pm") crossed the
  // ownership barrier into the next account and rendered on Work modes and the
  // Demand-charge screen, while the same screen told the new owner that nothing
  // from a previous owner is shown (APP-HOM-32A/32B, CLD-CMD-04A).
  //
  // A zeroed demand, not an absent one: absent is unreachable through Object.assign
  // and the screens already read this shape.
  demand: {
    enabled: false,
    fromLabel: '',
    toLabel: '',
    fromHour: 0,
    toHour: 0,
    days: [false, false, false, false, false, false, false],
    daysLabel: 'No days',
    windowLabel: 'Not set',
    season: 'All year',
    rateAud: 0,
    rateUnit: '/kW·mo',
    peakKw: 0,
    peakAt: '—',
    peakPeriodLabel: 'This month',
  },
}

interface MockEconomicProfile {
  accountId: string | null
  residenceId: string
  targetDeviceIds: string[]
  revision: number
  active: boolean
  lastRevisionAtMs: number | null
  revisions: EconomicRevisionView[]
  /** What the account had configured when it was put away. Account history —
   *  retained, never served to anybody else (CLD-CMD-04A/04B). */
  snapshot: { tariff: Tariff; schedule: TouSlot[] } | null
}

const newMockEconomicProfile = (accountId: string | null, revision: number, residenceId: string = seedSite.id): MockEconomicProfile => ({
  accountId,
  residenceId,
  targetDeviceIds: [],
  revision,
  active: true,
  lastRevisionAtMs: revision > 0 ? Date.now() : null,
  revisions: [],
  snapshot: null,
})

// Per-Residence economic profiles (APP-HOM-32). The seed primary home boots with
// a configured plan (revision 1); other session Residences start at revision 0
// so a holiday house never inherits My Home's tariff or schedule.
const economicBySite: Record<string, MockEconomicProfile> = {
  [seedSite.id]: newMockEconomicProfile(null, 1, seedSite.id),
}

function sessionEconomicSiteId(): string {
  return mockSessionSiteId || seedSite.id
}

function currentEconomic(): MockEconomicProfile {
  const siteId = sessionEconomicSiteId()
  if (!economicBySite[siteId]) {
    economicBySite[siteId] = newMockEconomicProfile(uid(), 0, siteId)
  }
  // Keep the profile stamped to the Residence the session is viewing.
  if (economicBySite[siteId].residenceId !== siteId) economicBySite[siteId].residenceId = siteId
  return economicBySite[siteId]
}

/** Inverter Connected Devices that receive a Residence economic plan (APP-HOM-32C).
 *  Packs are not economic targets — the inverter controls solar and battery modules.
 *  Offline inverters are omitted from a new revision's delivery set; multi-inverter
 *  balance is on-device (master inverter), not App-side. */
const economicTargetDeviceIds = (): string[] => {
  // Secondary multi-res homes have no device twin in the offline mock.
  if (sessionEconomicSiteId() !== seedSite.id) return []
  return devices
    .filter((d) => d.kind === 'inverter')
    .filter((d) => d.status === 'online' || d.status === 'standby')
    .map((d) => d.id)
}

/** THE READ BARRIER, both axes (CLD-CMD-04B). A barrier failure needs two
 *  independent mistakes: a wrong account id AND a missed deactivation. */
const economicReadable = (): boolean => {
  const p = currentEconomic()
  return p.active && (p.accountId === null || p.accountId === uid())
}

const economicIsDefault = (): boolean => !economicReadable() || currentEconomic().revision === 0

/** Secondary Residences have no offline twin for mutable economic state; refuse
 *  writes rather than mutating the primary home's plan under a switched session. */
function assertPrimaryEconomicSite(action: string): void {
  if (sessionEconomicSiteId() !== seedSite.id) {
    throw new Error(`400 ${action} for this Residence is not available in the offline demo`)
  }
}

/** Stamp one accepted economic change as a revision of the SIGNED-IN account's
 *  profile. Called from every economic setter, which is the mock's stand-in for
 *  the cloud router's single choke point. */
function recordMockEconomicRevision(change: string): void {
  assertPrimaryEconomicSite('Economic settings')
  const p = currentEconomic()
  const targets = economicTargetDeviceIds()
  p.revision += 1
  p.active = true
  p.targetDeviceIds = targets
  p.lastRevisionAtMs = Date.now()
  p.snapshot = null
  p.revisions = [
    ...p.revisions,
    {
      revision: p.revision,
      atMs: p.lastRevisionAtMs,
      change,
      accountId: p.accountId ?? uid(),
      residenceId: p.residenceId,
      scope: 'residence' as const,
      targetDeviceIds: targets,
    },
  ].slice(-25)
}

/**
 * RELEASE (CLD-CMD-04A/04C, APP-HOM-32A/32B). Three things happen and the third
 * is the one that is easy to leave out: the profile is deactivated with a
 * snapshot, and the RESIDENCE'S OWN economic items go back to defaults so the
 * next account inherits nothing. Technical, warranty, commissioning, firmware and
 * audit records are deliberately untouched (APP-HOM-32D).
 */
function releaseMockEconomicSettings(): void {
  const siteId = sessionEconomicSiteId()
  const p = currentEconomic()
  economicBySite[siteId] = {
    ...p,
    active: false,
    snapshot: { tariff: structuredClone(tariff), schedule: structuredClone(schedule) },
  }
  // Global twin state only belongs to the primary seed Residence.
  if (siteId === seedSite.id) {
    Object.assign(tariff, structuredClone(DEFAULT_TARIFF))
    schedule = []
    modes.splice(0, modes.length, ...structuredClone(seedModes).map((m) => (m.id === 'self' ? m : { ...m, enabled: false, active: false })))
    Object.assign(behaviour, { zeroExport: false, peakShave: false, batteryHold: false })
    battery.backupReservePct = 20
    battery.chargeCeilingPct = 100
    battery.dischargeFloorPct = 10
    deviceCommandLog = []
    refreshActive()
  }
}

/** A new account has paired: it starts with a profile of its own at revision 0,
 *  which is what makes "default self-consumption" a stated fact rather than an
 *  assumption repeated at each reader (APP-HOM-32B). */
function claimMockEconomicSettings(accountId: string): void {
  const siteId = sessionEconomicSiteId()
  economicBySite[siteId] = newMockEconomicProfile(accountId, 0, siteId)
}

function mockEconomicSettings(): EconomicSettings {
  // No Residence, no Residence-scoped settings — the same 404 `GET /site` gives,
  // rather than the demo home's plan under an account that holds no home.
  const record = currentMockResidence()
  if (!record) throw new Error('404 No Residence is linked to this account yet')
  const economicProfile = currentEconomic()
  const isDefault = economicIsDefault()
  // Non-primary homes: always present as default self-consumption (no shared twin).
  const secondary = sessionEconomicSiteId() !== seedSite.id
  const effectiveDefault = secondary || isDefault
  return {
    profile: {
      accountId: economicProfile.accountId ?? uid(),
      residenceId: economicProfile.residenceId,
      scope: 'residence',
      // A profile that predates this session's first save has no stored target
      // set yet; the Residence's controllable devices are what a save WOULD
      // target, and naming them is the point of APP-HOM-32C.
      targetDeviceIds: !effectiveDefault && economicProfile.revision > 0 && economicProfile.targetDeviceIds.length > 0
        ? economicProfile.targetDeviceIds
        : economicTargetDeviceIds(),
      revision: secondary ? 0 : (economicReadable() ? economicProfile.revision : 0),
      active: !secondary && economicReadable(),
      isDefault: effectiveDefault,
      lastRevisionAtMs: effectiveDefault ? null : economicProfile.lastRevisionAtMs,
      revisions: secondary || !economicReadable() ? [] : economicProfile.revisions.map((r) => ({ ...r })),
    },
    modes: effectiveDefault
      ? { self: true, backup: false, tou: false }
      : {
        self: true,
        backup: modes.find((m) => m.id === 'backup')?.enabled ?? false,
        tou: modes.find((m) => m.id === 'tou')?.enabled ?? false,
      },
    tariff: effectiveDefault ? null : structuredClone(tariff),
    schedule: effectiveDefault ? [] : structuredClone(schedule),
    // The zone AND its provenance, from the same resolution `GET /site` uses — the
    // cloud requires both on this response, and a schedule screen is the only
    // place a homeowner ever reads the zone. Sending the zone alone here is what
    // let a derived one be rendered as the home's own setting.
    ...zoneOf(record),
  }
}

/** The `{ timeZone, timeZoneSource }` pair, never one without the other. */
function zoneOf(record: MockResidenceRecord): { timeZone: string; timeZoneSource: NonNullable<Site['timeZoneSource']> } {
  const zone = resolveMockSiteTimeZone(record)
  return { timeZone: zone.timeZone, timeZoneSource: zone.source }
}

/** Restore every piece of mutable session state to its seed (the mock's analog
 *  of the cloud's POST /site/reset). Const bindings are mutated in place; let
 *  bindings are reassigned. The login identity (currentUser) is deliberately
 *  kept — reset clears data, it does not sign you out. */
function resetState(): void {
  Object.assign(battery, structuredClone(seedBattery))
  modes.splice(0, modes.length, ...structuredClone(seedModes))
  Object.assign(tariff, structuredClone(seedTariff))
  Object.assign(behaviour, { zeroExport: false, peakShave: false, batteryHold: false })
  schedule = structuredClone(seedTou)
  notificationPrefs = structuredClone(seedNotificationPrefs)
  uiPrefs = structuredClone(seedUiPrefs)
  try { localStorage.removeItem(MOCK_UI_PREFS_KEY) } catch { /* non-browser test */ }
  ownership = structuredClone(seedOwnership)
  pairing = {
    residenceId: seedSite.id, deviceId: `rsn-pcu-${seedSite.id}`,
    state: 'homeowner_paired', primaryAccount: { id: seedUser.id, name: seedUser.name, role: 'homeowner' },
    deviceRevision: 1, cloudKnownRevision: 1, source: 'local_ble_pair', updatedAt: Date.now(),
    energyVisibleFrom: Date.now(), members: [],
  }
  devices = structuredClone(seedDevices)
  commissioning = structuredClone(seedCommissioning)
  compliance = structuredClone(seedCompliance)
  firmware = structuredClone(seedFirmware)
  for (const k of Object.keys(updatePlans)) delete updatePlans[k]
  for (const k of Object.keys(abortedCampaigns)) delete abortedCampaigns[k]
  notifications = structuredClone(seedNotifications)
  supportEnquiries = []
  for (const k of Object.keys(admReviews)) delete admReviews[k]
  // Phase-11 backstop state (window + live ADM + DERStatus trail).
  backstopWindow = null
  liveAdm = []
  derStatusLog = []
  // Phase-12 VPP state (the dispatch in force + the per-site + partner records).
  vppCommand = null
  vppRecords = []
  vppEvents = []
  vppWebhooks = []
  partnerGranted = false
  // Consent, warranty, consent-log and HWS state.
  consents = structuredClone(seedConsents)
  warranty = structuredClone(seedWarranty)
  consentLog = structuredClone(seedConsentLog)
  hwsDeclaration = null
  // Multi-identity world (memberships/residences).
  mockMemberships = {}
  mockResidences = {}
  residenceSeq = 0
  mockSessionSiteId = seedSite.id
  // Stage 4.4: the device-command log and the account's economic profile.
  deviceCommandLog = []
  deviceCommandSeq = 0
  for (const k of Object.keys(economicBySite)) delete economicBySite[k]
  economicBySite[seedSite.id] = newMockEconomicProfile(null, 1, seedSite.id)
  // Installer Organisation world: organisations, roster, serial registry, audit
  // and the retained fleet associations the homeowner sees (APP-ACC-08*/APP-OWN-18).
  seedOrgWorld()
  currentEmail = normEmail(currentUser?.email ?? seedUser.email)
  refreshActive()
}

const mockImplementation = {
  async login(email: string, _password: string): Promise<LoginResult> {
    const addr = email || seedUser.email
    if (RETIRED_DEMO_EMAILS.has(normEmail(addr))) throw new Error('401 Invalid email or password')
    if (userFor(addr).accountStatus === 'locked') throw new Error('401 This account is locked. Contact Risen support.')
    // Opt-in 2FA (APP-ACC-07): challenge instead of signing in.
    if (twoFactorEmails.has(normEmail(addr))) return delay({ challengeRequired: true as const, email: addr, challengeToken: `mock:${normEmail(addr)}`, enrollmentRequired: false })
    currentUser = { ...userFor(addr), twoFactorEnabled: false }
    currentEmail = normEmail(addr)
    mockSessionSiteId = seedSite.id
    signedOut = false
    // An approved installer account always HAS an organisation — the company on
    // its profile names one (the cloud's legacy bridge). Materialising it at
    // sign-in rather than on first organisation read keeps a practitioner's set of
    // organisations stable, which is what the APP-ACC-08L selector reads.
    if (currentUser.role === 'installer') ensureMockOrganisation()
    return delay({ user: currentUser })
  },
  async verifyTwoFactor(email: string, _code: string, challengeToken: string): Promise<User> {
    // Demo: any 6-digit code passes (validated at the UI); complete the sign-in.
    const addr = email || seedUser.email
    if (challengeToken !== `mock:${normEmail(addr)}`) throw new Error('401 Sign-in challenge is missing or expired')
    currentUser = { ...userFor(addr), twoFactorEnabled: true }
    currentEmail = normEmail(addr)
    mockSessionSiteId = seedSite.id
    signedOut = false
    return delay(currentUser)
  },
  async getTwoFactorSetup(): Promise<{ secret: string; uri: string }> {
    assertWorkspace('/security/2fa/setup')
    return delay({ secret: 'JBSWY3DPEHPK3PXP', uri: 'otpauth://totp/Risen%20Energy:demo?secret=JBSWY3DPEHPK3PXP&issuer=Risen%20Energy' })
  },
  async setTwoFactor(enabled: boolean, _code?: string): Promise<User> {
    assertWorkspace('/security/2fa')
    currentUser = { ...(currentUser ?? { ...seedUser }), twoFactorEnabled: enabled }
    if (enabled) twoFactorEmails.add(currentEmail)
    else twoFactorEmails.delete(currentEmail)
    return delay(currentUser)
  },
  async signup(name: string, email: string, _password: string): Promise<User> {
    // Self-serve account creation — anyone, any time (APP-ACC-01). Demo-equivalent
    // to login (per-email world) but keeps the entered display name.
    currentUser = userFor(email || seedUser.email, name.trim() || seedUser.name)
    currentEmail = normEmail(currentUser.email)
    signedOut = false
    return delay(currentUser)
  },
  async signOut(): Promise<void> {
    // End the session: drop the identity and suppress the lazy default so the
    // shell drops to Login instead of silently re-establishing the demo account.
    currentUser = null
    signedOut = true
    // The acting organisation belongs to the practitioner, not the client: the
    // next account must choose its own (APP-ACC-08L).
    actingOrganisation = null
  },
  async getCurrentUser(): Promise<User | null> {
    // After an explicit sign-out, stay signed out (no lazy default identity).
    if (signedOut) return delay(null)
    // Lazy default to the canonical demo identity so offline dev / Tier A open on a
    // populated app. This is DELIBERATELY no longer parity with the HTTP client: it
    // dropped its auto-login when the demo personas gained real passwords (see
    // httpApi.ts). Harmless here — the mock has no server, no rate limiter and no
    // password to leak — but it does mean the mock never exercises the signed-out
    // shell that the deployed app shows a first-time visitor.
    if (!currentUser) {
      currentUser = userFor(seedUser.email)
      currentEmail = normEmail(seedUser.email)
    }
    return delay(currentUser)
  },
  // ---- installer identity (APP-ACC-09 / CLD-PRT-09) ----
  async applyInstaller(org: InstallerOrg): Promise<User> {
    const me = currentUser ?? userFor(seedUser.email)
    if (me.role === 'admin') throw new Error('403 Admin accounts cannot request installer access')
    if (me.role === 'installer') return delay(me) // already granted — no-op
    // Re-applying supersedes the prior row; carry a prior DECISION forward as audit.
    const prior = installerApps[currentEmail]
    installerApps[currentEmail] = {
      userId: currentEmail, email: me.email, name: me.name,
      org: { ...org }, status: 'pending', appliedAt: Date.now(),
      ...(prior && prior.status !== 'pending'
        ? { priorStatus: prior.status, priorDecidedAt: prior.decidedAt, priorDecidedBy: prior.decidedBy }
        : {}),
    }
    currentUser = { ...me, installerStatus: 'pending', installerOrg: { ...org } }
    return delay(currentUser)
  },
  async getInstallerApplication(): Promise<InstallerApplication | null> {
    return delay(installerApps[currentEmail] ?? null)
  },
  async adminListInstallerApplications(): Promise<InstallerApplication[]> {
    requireRole('admin', 'Back-office access required')
    return delay(Object.values(installerApps).sort(pendingFirst((x) => x.appliedAt)))
  },
  async adminDecideInstallerApplication(userId: string, approve: boolean): Promise<InstallerApplication> {
    // Roles are server-owned (SYS-SW-07): only this admin decision elevates one,
    // and an ownership approver is not the admin who may do it.
    requireAccountOperator('Back-office access required')
    if (userId === currentEmail) throw new Error('400 You cannot decide your own application')
    const app = installerApps[userId]
    if (!app) throw new Error('404 No application for that user')
    if (app.status !== 'pending') throw new Error('409 Application already decided')
    installerApps[userId] = {
      ...app, status: approve ? 'approved' : 'rejected', decidedAt: Date.now(), decidedBy: ADMIN_EMAIL,
    }
    return delay(installerApps[userId])
  },
  async adminListAccounts(query = ''): Promise<AdminAccountSummary[]> {
    requireRole('admin', 'Back-office access required')
    return delay(mockAdminAccounts(query))
  },
  async adminListSites(query = ''): Promise<AdminSiteSummary[]> {
    requireRole('admin', 'Back-office access required')
    const all: AdminSiteSummary[] = [
      { id: seedSite.id, name: seedSite.name, address: seedSite.address, dnsp: seedSite.dnsp, ownerId: seedUser.id, ownerName: seedUser.name, deviceCount: seedDevices.length, ownershipVersion: ownership.version ?? 1 },
      ...Object.entries(mockResidences).map(([id, s]) => ({ id, name: s.label, address: s.address, dnsp: seedSite.dnsp, deviceCount: seedDevices.length, ownershipVersion: 1 })),
    ]
    const q = query.trim().toLowerCase()
    return delay(all.filter((s) => q.length < 2 || [s.id, s.name, s.address, s.ownerName].some((v) => v?.toLowerCase().includes(q))))
  },
  async adminListDevices(query = ''): Promise<AdminDeviceSummary[]> {
    requireRole('admin', 'Back-office access required')
    const sites = await this.adminListSites('')
    const all = sites.flatMap((s) => seedDevices.map((d) => ({
      id: `${s.id}:${d.id}`, kind: d.kind, name: d.name, serial: d.serial,
      siteId: s.id, siteName: s.name, ownerId: s.ownerId, ownerName: s.ownerName, status: d.status,
    })))
    const q = query.trim().toLowerCase()
    return delay(all.filter((d) => q.length < 2 || [d.id, d.name, d.serial, d.siteName, d.ownerName].some((v) => v?.toLowerCase().includes(q))))
  },
  async adminListAudit(): Promise<AdminAuditEvent[]> {
    requireRole('admin', 'Back-office access required')
    return delay(adminAudit)
  },
  async adminSetInstallerCredential(accountId: string, status: 'approved' | 'suspended' | 'revoked', reason: string, ticket: string): Promise<{ account: AdminAccountSummary; audit: AdminAuditEvent }> {
    requireAccountOperator('Back-office access required')
    if (!reason.trim() || !ticket.trim()) throw new Error('400 A reason and ticket are required')
    const email = accountId.replace(/^u-/, '')
    const before = mockAdminAccounts(email).find((a) => a.id === accountId)
    if (!before) throw new Error('404 Account not found')
    const app = installerApps[email]
    if (app) installerApps[email] = { ...app, status: status === 'approved' ? 'approved' : 'rejected' }
    adminAccountOverrides[email] = { ...adminAccountOverrides[email], role: status === 'approved' ? 'installer' : 'homeowner', installerStatus: status }
    const audit = pushAdminAudit({ actorId: currentEmail, action: `installer.${status}`, targetType: 'installer', targetId: accountId, reason, ticket, result: 'applied', before: before.installerStatus, after: status })
    return delay({ account: mockAdminAccounts(email).find((a) => a.id === accountId)!, audit })
  },
  async adminSetAccountLock(accountId: string, locked: boolean, reason: string, ticket: string): Promise<{ account: AdminAccountSummary; audit: AdminAuditEvent }> {
    requireAccountOperator('Back-office access required')
    if (!reason.trim() || !ticket.trim()) throw new Error('400 A reason and ticket are required')
    const email = accountId.replace(/^u-/, '')
    const before = mockAdminAccounts(email).find((a) => a.id === accountId)
    if (!before) throw new Error('404 Account not found')
    adminAccountOverrides[email] = { ...adminAccountOverrides[email], accountStatus: locked ? 'locked' : 'active' }
    const audit = pushAdminAudit({ actorId: currentEmail, action: locked ? 'account.lock' : 'account.unlock', targetType: 'account', targetId: accountId, reason, ticket, result: 'applied', before: before.accountStatus, after: locked ? 'locked' : 'active' })
    return delay({ account: mockAdminAccounts(email).find((a) => a.id === accountId)!, audit })
  },
  async getSite(): Promise<Site> {
    // The SESSION's Residence, resolved zone and provenance included — not the
    // seed fixture regardless of who is signed in. An account with no home paired
    // yet gets the cloud's 404, which is a state the app has to render rather than
    // paper over with a default.
    const site = currentMockSite()
    if (!site) throw new Error('404 No Residence is linked to this account yet')
    return delay(site)
  },
  async listResidences(): Promise<import('./types').ResidenceList> {
    const site = currentMockSite()
    if (!site) return delay({ activeSiteId: '', residences: [] })
    // Canonical homeowner + multi@ get two homes so the Me switcher is always
    // exercisable offline (parity with cloud ensureDemoMultiResidence).
    const dual = ['homeowner@example.com', 'multi@example.com', seedUser.email.toLowerCase()]
      .includes((currentEmail ?? '').toLowerCase())
    const primaryId = seedSite.id
    const activeId = mockSessionSiteId || primaryId
    const residences = [
      {
        siteId: primaryId,
        label: seedSite.name,
        role: 'owner' as const,
        access: 'full',
        suburb: 'Newcastle' as string | null,
        statusHint: 'healthy' as string | null,
        since: '—',
        active: activeId === primaryId,
      },
      ...(dual
        ? [{
            siteId: MOCK_HOLIDAY_SITE_ID,
            label: 'Holiday house',
            role: 'owner' as const,
            access: 'full',
            suburb: 'Byron Bay' as string | null,
            statusHint: 'healthy' as string | null,
            since: '—',
            active: activeId === MOCK_HOLIDAY_SITE_ID,
          }]
        : []),
    ]
    // Zone-demo personas keep a single home (their fixture id).
    if (!dual && currentEmail in seedZoneDemoResidences) {
      return delay({
        activeSiteId: site.id,
        residences: [{
          siteId: site.id,
          label: site.name,
          role: 'owner' as const,
          access: 'full',
          suburb: null,
          statusHint: 'healthy',
          since: '—',
          active: true,
        }],
      })
    }
    return delay({ activeSiteId: activeId, residences })
  },
  async switchResidence(siteId: string): Promise<import('./types').ResidenceSummary> {
    const list = await this.listResidences()
    const hit = list.residences.find((r) => r.siteId === siteId)
    if (!hit) throw new Error('403 You do not have access to that Residence')
    mockSessionSiteId = siteId
    return delay({ ...hit, active: true })
  },
  async resetSiteData(): Promise<{ reset: boolean; deletedItems: number }> {
    resetState()
    return delay({ reset: true, deletedItems: 0 })
  },
  async getPowerFlow(): Promise<PowerFlow> {
    return delay(ticker.snapshot())
  },
  subscribeLive(cb: (pf: PowerFlow) => void): () => void {
    return ticker.subscribe(cb)
  },
  async getTelemetry(): Promise<TelemetryStatus> {
    // The offline mock has no device link — surface an honest `sim` frame derived
    // from the live ticker so the Device-link screen renders the "Simulated" state
    // (online:false), never a fake "Live · via MQTT". Mirrors the cloud's fallback.
    const pf = ticker.snapshot()
    const frame: TelemetryFrame = {
      v: 1,
      siteId: seedSite.id,
      deviceId: `rsn-pcu-${seedSite.id}`,
      seq: 0,
      ts: pf.ts,
      tel: {
        solarKw: pf.solarKw, loadKw: pf.loadKw, batteryKw: pf.batteryKw,
        batteryRequestedKw: pf.batteryRequestedKw,
        batteryControlResult: pf.batteryControlResult,
        batteryConstraintReasons: pf.batteryConstraintReasons,
        gridKw: pf.gridKw,
        soc: pf.soc, socKwh: pf.socKwh, sohPct: battery.soh, modeId: effectiveModeId() ?? 'idle',
      },
      // 'standby', not 'online': no device is linked in this build, and the envelope
      // already says source:'sim' (parity with the cloud's synthesised sim frame).
      status: { state: 'standby', conn: 'wifi', rssiDbm: -52, spd: 'ok', faults: [...SIM_FRAME_FAULTS] },
      fw: Object.fromEntries(firmware.map((f) => [f.component, opStrip(f.active === 'A' ? f.slotA : f.slotB)])),
    }
    // The (simulated) device enforces the export control in force (GRI-DEV-01/04): an
    // active command's cap, else the per-site DNSP DEFAULT (GRI-DEV-02, never
    // uncapped). Curtail surplus solar so grid export ≤ cap and ack it in `grid`.
    const ctrl = exportControlNow(pf.ts)
    const curtailKw = Math.max(0, Math.max(0, -frame.tel.gridKw) - ctrl.capW / 1000)
    if (curtailKw > 0) {
      const solarCurtailKw = Math.min(Math.max(0, frame.tel.solarKw), curtailKw)
      frame.tel.solarKw = Math.round(Math.max(0, frame.tel.solarKw - solarCurtailKw) * 100) / 100
      const remainingKw = Math.max(0, curtailKw - solarCurtailKw)
      if (frame.tel.batteryKw > 0) frame.tel.batteryKw = Math.round(Math.max(0, frame.tel.batteryKw - remainingKw) * 100) / 100
      frame.tel.gridKw = Math.round((frame.tel.loadKw - frame.tel.solarKw - frame.tel.batteryKw) * 100) / 100
    }
    frame.grid = {
      exportLimitW: ctrl.capW,
      appliedW: Math.round(Math.max(0, -frame.tel.gridKw) * 1000),
      commandId: ctrl.commandId,
      source: ctrl.source,
    }
    return delay({ online: false, source: 'sim', ageSeconds: 0, latest: frame, recent: [] })
  },
  async getBattery(): Promise<Battery> {
    // Secondary multi-res homes do not share the primary twin's live battery.
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id) {
      return delay({
        ...structuredClone(battery),
        soc: 0,
        socKwh: 0,
        powerKw: 0,
        flow: 'idle' as const,
        modules: battery.modules.map((m) => ({ ...m, soc: 0, status: 'ok' as const })),
      })
    }
    return delay(battery)
  },
  async getBatteryPacks(): Promise<BatteryPack[]> {
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id) return delay([])
    return delay(batteryPacksFromBattery(battery, warranty))
  },
  async setBackupReserve(pct: number): Promise<Battery> {
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id) throw new Error('400 Battery settings for this Residence are not available in the offline demo')
    battery.backupReservePct = Math.round(pct)
    recordMockEconomicRevision('PUT /battery/backup-reserve')
    return delay(battery)
  },
  async setSocLimits(ceilingPct: number, floorPct: number): Promise<Battery> {
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id) throw new Error('400 Battery settings for this Residence are not available in the offline demo')
    battery.chargeCeilingPct = Math.round(ceilingPct)
    battery.dischargeFloorPct = Math.round(floorPct)
    recordMockEconomicRevision('PUT /battery/soc-limits')
    return delay(battery)
  },
  async getCommands(): Promise<CommandsStatus> {
    // Simulated cloud aggregator (Phase 10/11): the command in force (backstop when
    // active, else the routine DNSP limit) + the day's command log. Offline-honest —
    // no real device/MQTT (see Device-link screen).
    return delay(mockCommands())
  },
  async getGridJurisdiction(): Promise<GridJurisdiction> {
    // Per-DNSP jurisdiction config bound to this site (GRI-CSP-05), read-only. The
    // profiles are config, not code branches; editable:false is the honest posture.
    return delay<GridJurisdiction>({ dnsp: seedSite.dnsp, profile: profileForSiteDnsp(), all: JURISDICTIONS, editable: false })
  },
  async getDerStatus(): Promise<DerStatusStatus> {
    // The DERStatus the cloud would relay to the DNSP (GRI-CSP-02 return), reconciled
    // from live telemetry, plus the recorded trail from backstop start/stop.
    return delay<DerStatusStatus>({ reports: appendCapped(derStatusLog, buildMockDerStatus(), 20).list })
  },
  async triggerBackstop(
    action: 'start' | 'stop',
    opts?: { jurisdiction?: string; limitW?: number; durationMinutes?: number },
  ): Promise<BackstopStatus> {
    // DEMO: simulate a DNSP-issued EMERGENCY backstop (GRI-BST-01/02) on this site.
    // `start` opens the window (default the SA/SAPN flagship) and drives the LIVE
    // surfaces — the authority banner (via getAuthority), a notification, and an ADM
    // record linked to the non-revocable DNSP consent c-1 (GRI-BST-03); `stop` reverts.
    const now = Date.now()
    if (action === 'stop') {
      backstopWindow = null
      derStatusLog = appendCapped(derStatusLog, buildMockDerStatus(now), 20).list
      return delay<BackstopStatus>({ backstop: null })
    }
    // Reject an invalid explicit limit (undefined → zero-export default) — parity
    // with the cloud's 400. Number.isFinite alone covers the non-number cases.
    if (opts?.limitW !== undefined && (!Number.isFinite(opts.limitW) || opts.limitW < 0))
      throw new Error('400 limitW must be a number ≥ 0')
    const profile = backstopProfileById(opts?.jurisdiction)
    const limitW = typeof opts?.limitW === 'number' ? Math.round(opts.limitW) : 0
    const durationMs =
      typeof opts?.durationMinutes === 'number' && opts.durationMinutes > 0
        ? Math.min(opts.durationMinutes, 24 * 60) * 60_000
        : 2 * 3_600_000
    const capLabel = limitW === 0 ? 'zero export' : `${(limitW / 1000).toFixed(1)} kW export cap`
    backstopWindow = {
      active: true, startMs: now, endMs: now + durationMs, limitW,
      jurisdiction: profile.id, authority: profile.displayName, scheme: profile.backstopScheme,
      reason: `${profile.displayName} emergency backstop — ${capLabel}`,
    }
    // Live surfaces (GRI-BST-03): a non-silenceable notification naming the authority +
    // an ADM record linked to the non-revocable DNSP consent c-1. Both ids key on the
    // window's stable mRID (matching the cloud) so a re-raise would be idempotent.
    const mRID = `backstop-${profile.id}-${now}`
    const cap = limitW === 0 ? 'requiring zero export' : `capping export to ${(limitW / 1000).toFixed(1)} kW`
    pushNotif(
      `Emergency backstop — ${profile.displayName}`,
      `${profile.displayName} has issued an emergency backstop ${cap} under the ${profile.backstopScheme}. This is a mandatory network condition of your connection — it cannot be silenced. No action is needed; your battery keeps powering your home.`,
      { category: 'grid', severity: 'warning', id: `n-bs-${mRID}` },
    )
    const adm: AdmDecision = {
      id: `adm-bs-${mRID}`, title: 'DNSP emergency backstop curtailment', category: 'backstop',
      whenLabel: 'Just now',
      what: limitW === 0 ? 'Export held at zero for the backstop window' : `Export capped to ${(limitW / 1000).toFixed(1)} kW for the backstop window`,
      why: `${profile.displayName} emergency backstop signal (${profile.backstopScheme})`,
      dataUsed: 'Export limit, backstop signal, connection agreement', consentId: 'c-1',
    }
    liveAdm = appendCapped(liveAdm, adm, 10, (d) => d.id).list
    derStatusLog = appendCapped(derStatusLog, buildMockDerStatus(now), 20).list
    return delay<BackstopStatus>({ backstop: backstopWindow })
  },
  // ---- Phase-12: VPP dispatch (ICD-API-02 / CLD-VPP-* / GRI-VPP-*) ----
  // The OFFLINE mirror of the cloud partner API. Same screen, same consent gate, same
  // precedence — synthesized in-memory. Honest: no network, no partner server; the
  // webhook is RECORDED, exactly as the cloud demo records it.

  async getVppStatus(): Promise<VppSiteStatus> {
    const c = consents.find((x) => x.party === 'VPP')
    const top = mockCommands().active
    const liveCmd = top?.priority === 'vpp' ? top : null
    // GRI-VPP-02: settle the delivered energy as the event runs. The mock has no
    // telemetry ring, so the delta is integrated from the LIVE dispatched power over the
    // elapsed window — and `baselineNote` says exactly that rather than implying a
    // settlement-grade meter record.
    const nowMs = Date.now()
    const settled = vppRecords.map((r) => {
      if (r.outcome === 'refused' || r.baselineKw === undefined) return r
      const elapsedH = Math.max(0, Math.min(nowMs, r.endMs) - r.startMs) / 3.6e6
      return { ...r, deliveredKwh: round((Math.abs(r.dispatchedKw) - r.baselineKw) * elapsedH) }
    })
    return delay<VppSiteStatus>({
      enrolled: c?.status === 'active',
      partnerName: c?.name ?? null,
      consentId: c?.id ?? null,
      active: liveCmd ? (settled.find((r) => r.commandId === liveCmd.commandId) ?? null) : null,
      recent: settled,
    })
  },

  async vppPartnerToken(clientId: string, clientSecret: string): Promise<VppTokenResponse> {
    // OAuth2 client-credentials (RFC 6749 §4.4), mocked. A wrong secret is REJECTED —
    // the gate is real code even though the credential is static (ICD-API-02).
    if (clientId !== VPP_PARTNER.clientId || clientSecret !== VPP_PARTNER.clientSecret)
      throw new Error('401 Invalid client credentials')
    partnerGranted = true
    return delay<VppTokenResponse>({
      accessToken: `mock-partner-${VPP_PARTNER.id}`,
      tokenType: 'Bearer',
      expiresIn: 8 * 3600,
      scope: VPP_PARTNER.scopes.join(' '),
    })
  },

  async vppPartnerFleet(): Promise<VppPartnerView> {
    requirePartnerScope('fleet:read')
    const { rows, withheld } = mockPartnerFleet()
    return delay<VppPartnerView>({
      partner: { id: VPP_PARTNER.id, name: VPP_PARTNER.name, scopes: [...VPP_PARTNER.scopes], sandbox: true, webhookUrl: VPP_PARTNER.webhookUrl },
      fleet: rows,
      withheldSites: withheld,
      events: vppEvents,
      webhooks: vppWebhooks,
    })
  },

  async vppDispatch(req: VppDispatchRequest): Promise<VppEvent> {
    requirePartnerScope('dispatch:write')
    if (req.action !== 'discharge' && req.action !== 'charge') throw new Error("400 action must be 'discharge' or 'charge'")
    if (!Number.isFinite(req.targetKw) || req.targetKw <= 0 || req.targetKw > MAX_KW)
      throw new Error(`400 targetKw must be a number in (0, ${MAX_KW}]`)
    if (!Number.isFinite(req.durationMinutes) || req.durationMinutes <= 0 || req.durationMinutes > 1440)
      throw new Error('400 durationMinutes must be a number in (0, 1440]')
    return delay(runMockDispatch(req, Date.now()))
  },

  async vppPartnerEvents(): Promise<VppEvent[]> {
    requirePartnerScope('settlement:read')
    return delay(vppEvents)
  },

  async vppPartnerWebhooks(): Promise<VppWebhookDelivery[]> {
    requirePartnerScope('settlement:read')
    return delay(vppWebhooks)
  },

  async getAuthority(): Promise<Authority> {
    // Invariant #5 precedence: an active DNSP export limit sits on the grid tier,
    // ABOVE every economic mode — so it is the controlling authority when in force.
    const { active, candidates } = mockCommands()
    if (active) return delay<Authority>(authorityFromCommands(candidates))
    const eff = effectiveModeId()
    if (eff === null)
      return delay<Authority>({
        authority: 'economic',
        title: 'Hold',
        detail: 'Battery turned off by homeowner — routine charging and discharging are paused',
        tone: 'orange',
      })
    const detail =
      eff === 'backup'
        ? 'Keeping the battery full and ready for outages'
        : eff === 'tou'
          ? 'Time-of-use window active — shifting energy to cut your bill'
          : seedAuthority.detail
    const title = modes.find((m) => m.id === eff)?.label ?? seedAuthority.title
    return delay<Authority>({ authority: 'economic', title: eff === 'self' ? 'Self-consumption' : title, detail, tone: 'orange' })
  },
  async getModes(): Promise<Mode[]> {
    refreshActive()
    return delay(modes)
  },
  async setModeEnabled(id: string, enabled: boolean): Promise<Mode[]> {
    if (id === 'self') return delay(modes)
    const m = modes.find((x) => x.id === id)
    if (m) m.enabled = enabled
    recordMockEconomicRevision(`PUT /modes/${id}/enabled`)
    refreshActive()
    return delay(modes)
  },
  async getBehaviour(): Promise<Behaviour> {
    return delay(behaviour)
  },
  async setBehaviour(flag: keyof Behaviour, enabled: boolean): Promise<Behaviour> {
    behaviour[flag] = enabled
    recordMockEconomicRevision(`PUT /behaviour/${flag}`)
    return delay(behaviour)
  },
  async getSchedule(): Promise<TouSlot[]> {
    return delay(sortByStart(schedule))
  },
  async setSchedule(slots: TouSlot[]): Promise<TouSlot[]> {
    assertNoOverlap(slots)
    schedule = sortByStart(structuredClone(slots))
    recordMockEconomicRevision('PUT /schedule')
    refreshActive()
    return delay(schedule)
  },
  async getTariff(): Promise<Tariff> {
    if (sessionEconomicSiteId() !== seedSite.id) {
      return delay(structuredClone(DEFAULT_TARIFF))
    }
    return delay(tariff)
  },
  async setTariffType(type: TariffType): Promise<Tariff> {
    assertPrimaryEconomicSite('Tariff settings')
    tariff.type = type
    recordMockEconomicRevision('PUT /tariff/type')
    return delay(tariff)
  },
  async setTariffPeriods(periods: TariffPeriod[]): Promise<Tariff> {
    tariff.periods = structuredClone(periods)
    recordMockEconomicRevision('PUT /tariff/periods')
    return delay(tariff)
  },
  async setFlatRate(rate: number): Promise<Tariff> {
    tariff.flatRateAud = Math.round(rate * 100) / 100
    recordMockEconomicRevision('PUT /tariff/flat-rate')
    return delay(tariff)
  },
  async setTariffTiers(tiers: TariffTier[]): Promise<Tariff> {
    tariff.tiers = structuredClone(tiers)
    recordMockEconomicRevision('PUT /tariff/tiers')
    return delay(tariff)
  },
  async setDemandCharge(demand: DemandCharge): Promise<Tariff> {
    tariff.demand = structuredClone(demand)
    recordMockEconomicRevision('PUT /tariff/demand')
    return delay(tariff)
  },

  // ---- Stage 4.4: the device-command lifecycle ----

  async submitDeviceCommand(request: DeviceCommandRequest): Promise<DeviceCommandSubmission> {
    const now = mockNow()
    settleDeviceCommands(now)
    const commandId = request.commandId ?? crypto.randomUUID()

    // IDEMPOTENT SUBMISSION. The same identifier is the same intent, however many
    // times the caller retried — it is not a second command.
    const existing = deviceCommandLog.find((r) => r.commandId === commandId)
    if (existing) return delay({ command: mockCommandView(existing), duplicate: true, superseded: [] })

    const deviceId = (request.deviceId ?? '').trim()
    if (!deviceId) throw new Error('400 deviceId is required — a Residence identifier is not a broadcast')
    if (!devices.some((d) => d.id === deviceId)) throw new Error('403 That device belongs to a Residence outside your account')
    const spec = MOCK_HOMEOWNER_CONTROLS[request.control]
    // The tier rule, both directions. A homeowner's economic tier has no authority
    // over a grid export limit, so the axis is not merely discouraged here — it is
    // absent from the table and therefore unsubmittable.
    if (!spec)
      throw new Error(`400 ${String(request.control)} may not be issued on the economic tier — a routine grid limit is a constraint, not a homeowner command`)
    if (spec.kind === 'enum') {
      if (typeof request.value !== 'string' || !(spec.values ?? []).includes(request.value))
        throw new Error(`400 ${request.control} takes one of ${(spec.values ?? []).join('/')}`)
    } else {
      const value = Number(request.value)
      if (!Number.isFinite(value)) throw new Error(`400 ${request.control} takes a finite number`)
      if (spec.min !== undefined && value < spec.min) throw new Error(`400 ${value} is below ${spec.min}${spec.unit ?? ''}`)
      if (spec.max !== undefined && value > spec.max) throw new Error(`400 ${value} is above ${spec.max}${spec.unit ?? ''}`)
    }

    const ttlMs = request.ttlMs && request.ttlMs > 0 ? request.ttlMs : MOCK_COMMAND_TTL_MS
    const record: MockCommandRecord = {
      commandId,
      seq: (deviceCommandSeq += 1),
      deviceId,
      residenceId: seedSite.id,
      control: request.control,
      requestedValue: request.value,
      appliedValue: null,
      issuedAtMs: now,
      expiresAtMs: now + ttlMs,
      status: 'requested',
      reason: null,
      constraintTier: null,
      supersededBy: null,
      attempts: 0,
      retries: 0,
      abandonedAtMs: null,
      divergence: null,
      // The divergent device's dongle holds an open downlink, so the frame is taken
      // at once; everything else waits for the delivery window.
      downlinkTaken: deviceId === MOCK_DIVERGENT_DEVICE_ID && deviceReachable(deviceId),
      authority: {
        actorId: (currentUser ?? seedUser).id,
        actorRole: 'homeowner',
        source: 'app',
        reason: request.reason?.trim() || 'homeowner setting',
      },
      history: [{ atMs: now, status: 'requested', note: `submitted by ${(currentUser ?? seedUser).id}` }],
      resolvedAtMs: null,
      deliverAtMs: now + MOCK_COMMAND_DELIVER_MS,
      answerAtMs: now + MOCK_COMMAND_ANSWER_MS,
    }
    deviceCommandLog.push(record)

    // SUPERSESSION, and only of what has not left the building. A command already
    // handed to a dongle may already have been applied, so calling it superseded
    // would be the cloud narrating the device.
    //
    // …EXCEPT that "has not left the building" is a belief, not a fact, and on the
    // divergent device it is wrong: its dongle already holds the frame. The cloud
    // still retires the record, because that is the decision it can make with what
    // it knows — and when the device answers anyway, the disagreement is RECORDED
    // (see settleDeviceCommands) rather than either side being silently preferred.
    const superseded: MockCommandRecord[] = []
    for (const other of deviceCommandLog) {
      if (other.commandId === record.commandId) continue
      if (other.status !== 'requested' && !(other.status === 'delivered' && other.deviceId === MOCK_DIVERGENT_DEVICE_ID)) continue
      if (other.deviceId !== record.deviceId || other.control !== record.control) continue
      if (other.seq >= record.seq) continue
      other.reason = 'superseded'
      other.supersededBy = record.commandId
      commandTransition(other, 'superseded', now, `superseded by ${record.commandId}`)
      superseded.push(other)
    }

    return delay({
      command: mockCommandView(record),
      duplicate: false,
      superseded: superseded.map(mockCommandView),
    })
  },

  async getDeviceCommands(deviceId?: string): Promise<CommandView[]> {
    settleDeviceCommands()
    return delay(
      deviceCommandLog
        .filter((r) => !deviceId || r.deviceId === deviceId)
        .sort((a, b) => b.seq - a.seq)
        .map(mockCommandView),
    )
  },

  // ---- Stage 4.4: the account's economic-settings profile ----

  async getEconomicSettings(): Promise<EconomicSettings> {
    return delay(mockEconomicSettings())
  },

  async updateEconomicSettings(patch: EconomicSettingsPatch): Promise<EconomicSettings> {
    assertPrimaryEconomicSite('Economic settings')
    let changed = ''
    if (patch.supplyChargeAud !== undefined) {
      // APP-HOM-06A: finite and ≥ AUD 0.00 per day. Zero is a legal plan (some
      // embedded networks carry none), so the floor is 0, not greater than 0.
      if (!Number.isFinite(patch.supplyChargeAud) || patch.supplyChargeAud < 0)
        throw new Error('400 supplyChargeAud must be a finite number ≥ 0')
      tariff.supplyChargeAud = Math.round(patch.supplyChargeAud * 100) / 100
      changed = 'PUT /economic-settings (supply charge)'
    }
    if (patch.feedInAud !== undefined) {
      if (!Number.isFinite(patch.feedInAud) || patch.feedInAud < 0) throw new Error('400 feedInAud must be a finite number ≥ 0')
      tariff.feedInAud = Math.round(patch.feedInAud * 100) / 100
      changed = 'PUT /economic-settings (feed-in rate)'
    }
    if (patch.schedule !== undefined) {
      assertNoOverlap(patch.schedule)
      schedule = sortByStart(structuredClone(patch.schedule))
      changed = 'PUT /economic-settings (schedule)'
      refreshActive()
    }
    if (patch.targetDeviceIds !== undefined) changed ||= 'PUT /economic-settings (target devices)'
    if (!changed) throw new Error('400 nothing to change')
    recordMockEconomicRevision(changed)
    if (patch.targetDeviceIds !== undefined) currentEconomic().targetDeviceIds = [...patch.targetDeviceIds]
    return delay(mockEconomicSettings())
  },
  async getWeather(): Promise<Weather> {
    return delay(seedWeather)
  },
  async getEnergy(period: EnergyPeriod): Promise<EnergySummary> {
    return delay(energyFor(period))
  },
  async getRecordedEnergyDay(date: string): Promise<RecordedEnergyDay> {
    // Offline has no device/cloud recorder. Keep its useful visual preview but
    // label it truthfully so it can never be mistaken for metered history.
    const { dayStartTs, dayEndTs } = zonedDayBounds(date, seedSite.timeZone)
    const slotCount = Math.round((dayEndTs - dayStartTs) / 300_000)
    const { power, soc } = dayProfile(slotCount)
    const samples = power.map((batteryKw, i) => {
      const ts = dayStartTs + i * 300_000
      const local = new Intl.DateTimeFormat('en-AU', {
        timeZone: seedSite.timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(ts)
      const localParts = Object.fromEntries(local.map((part) => [part.type, part.value]))
      const minuteOfDay = Number(localParts.hour) * 60 + Number(localParts.minute)
      const hour = minuteOfDay / 60
      const solarKw = round(solarAt(hour))
      const loadKw = round(loadAt(hour))
      return {
        minuteOfDay,
        utcOffsetMinutes: timeZoneOffsetMinutes(ts, seedSite.timeZone),
        ts,
        solarKw,
        loadKw,
        batteryKw,
        gridKw: round(loadKw - solarKw - batteryKw),
        soc: soc[i],
        provenance: 'simulator' as const,
      }
    })
    return delay({ date, timeZone: seedSite.timeZone, dayStartTs, dayEndTs, source: 'simulation', provenance: 'simulator', samples })
  },
  async getNotifications(): Promise<AppNotification[]> {
    return delay(notifications)
  },
  async markNotificationsRead(ids?: string[]): Promise<AppNotification[]> {
    notifications = opMarkRead(notifications, ids)
    return delay(notifications)
  },
  async getNotificationPrefs(): Promise<NotificationPrefs> {
    return delay(notificationPrefs)
  },
  async setNotificationPrefs(prefs: NotificationPrefs): Promise<NotificationPrefs> {
    // Mirror the cloud: force the safety category on (non-silenceable, APP-HOM-08).
    notificationPrefs = {
      channels: { ...prefs.channels },
      categories: { ...prefs.categories, safety: true },
    }
    return delay(notificationPrefs)
  },
  async getConsents(): Promise<Consent[]> {
    return delay(consents)
  },
  async setConsent(id: string, status: 'active' | 'inactive'): Promise<Consent[]> {
    // The homeowner's opt-out control (APP-HOM-07). Revoking the VPP consent drops the
    // site from every partner cohort (CLD-VPP-03) — enforced in mockPartnerFleet, the
    // DATA layer, not here (CLD-DAT-03). A NON-revocable consent (the DNSP connection
    // condition) is REFUSED, not silently ignored: a mandatory network condition is not
    // a preference. Mirror of the cloud's store.setConsentStatus (403 + consent log).
    const c = consents.find((x) => x.id === id)
    if (!c) throw new Error('404 No such consent')
    if (!c.revocable)
      throw new Error(`403 "${c.name}" is a mandatory condition of your grid connection and cannot be revoked in-app`)
    if (c.status === status) return delay(consents) // idempotent — no log churn
    consents = consents.map((x) => (x.id === id ? { ...x, status } : x))
    consentLog = appendCapped(
      consentLog,
      {
        at: todayLong(),
        text: status === 'active' ? `${c.name} consent granted` : `${c.name} consent revoked`,
        tone: status === 'active' ? ('green' as const) : ('orange' as const),
      },
      CONSENT_LOG_CAP,
    ).list
    // Revoking VPP consent mid-event ENDS the dispatch in force: opting out has to be
    // immediate, not "after this event finishes". The command is dropped, so the shared
    // resolver stops returning it and the banner/telemetry revert on the next read.
    if (c.party === 'VPP' && status === 'inactive') vppCommand = null
    return delay(consents)
  },
  async getConsentLog(): Promise<ConsentLogEntry[]> {
    return delay(consentLog)
  },
  async getWarranty(): Promise<Warranty> {
    return delay(warranty)
  },
  async getUiPrefs(): Promise<UiPrefs> {
    return delay(uiPrefs)
  },
  async setUiPrefs(prefs: UiPrefs): Promise<UiPrefs> {
    // Mirror the cloud: merge over the seed defaults so unknown/missing keys fall back.
    uiPrefs = { ...seedUiPrefs, ...prefs }
    try { localStorage.setItem(MOCK_UI_PREFS_KEY, JSON.stringify(uiPrefs)) } catch { /* non-browser test */ }
    return delay(uiPrefs)
  },
  async getAdm(): Promise<Adm> {
    return delay(admPayload())
  },
  async requestAdmReview(decisionId: string): Promise<Adm> {
    // Idempotent: opening review on a decision that already has a case is a no-op.
    // Validate against the LIVE set (seed + live backstop `adm-bs-…`), not just the seed,
    // so a review can be opened on a live decision too.
    if ([...liveAdm, ...seedAdmDecisions].some((d) => d.id === decisionId) && !admReviews[decisionId]) {
      admReviews[decisionId] = { status: 'requested', caseRef: admCaseRef(decisionId), requestedLabel: 'just now' }
      pushNotif(
        `Human review case ${admReviews[decisionId].caseRef} opened`,
        'Your request to have an automated decision reviewed by a person has been logged.',
      )
    }
    return delay(admPayload())
  },
  async getInverter(): Promise<Inverter> {
    return delay(seedInverter)
  },
  async getDevices(): Promise<Device[]> {
    assertWorkspace('/devices')
    // Multi-res honesty: only the seed primary home has the offline device twin.
    // A second Residence must not show another home's hardware.
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id) return delay([])
    const packById = new Map(batteryPacksFromBattery(battery, warranty).map((p) => [p.id, p]))
    return delay(devices.map((d) => {
      const p = packById.get(d.id)
      return p ? { ...d, status: p.status, serial: p.serial, socPct: p.soc, ratedKwh: p.ratedKwh } : d
    }))
  },
  async pairDevice(kind: DeviceKind, _site?: string): Promise<Device[]> {
    assertWorkspace('/devices/pair')
    // The mock keeps a single shared device list; `_site` (a residence) is accepted
    // for signature parity but the offline demo pairs against the shared state.
    if (kind === 'evse') devices = opPairEvse(devices)
    return delay(devices)
  },
  async getPairing(_deviceId?: string): Promise<PairingStatus> {
    // Multi-res honesty: the offline twin is only the primary seed Residence.
    // Do not fabricate a "factory unpaired" surface that invites Pair CTAs that
    // then 400 — refuse like other secondary-home mutators.
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id && pairing.residenceId === seedSite.id) {
      throw new Error('400 Pairing for this Residence is not available in the offline demo')
    }
    return delay(pairing)
  },
  async recordLocalPairing(action: 'pair' | 'unpair', expectedRevision: number, _deviceId: string, _residenceId?: string): Promise<PairingStatus> {
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id && pairing.residenceId === seedSite.id) {
      throw new Error('400 Pairing for this Residence is not available in the offline demo')
    }
    if (action === 'unpair') {
      const user = currentUser ?? seedUser
      if (pairing.deviceRevision !== expectedRevision) throw new Error('409 Pairing changed; read the device state and try again')
      if (pairing.primaryAccount?.id !== user.id) throw new Error('403 Only the primary paired account can unpair locally')
      if (pairing.state === 'unpaired') return delay(pairing)
      const revision = pairing.deviceRevision + 1
      pairing = { ...pairing, state: 'unpaired', primaryAccount: undefined, members: [], deviceRevision: revision,
        cloudKnownRevision: revision, source: 'local_app_unpair', updatedAt: Date.now(), energyVisibleFrom: undefined, remoteUnpair: undefined }
      // The released account's economic settings stop being active for this
      // Residence and the Residence returns to defaults (APP-HOM-32A/32B). The
      // asset-scoped records around them are untouched (APP-HOM-32D).
      releaseMockEconomicSettings()
      return delay(pairing)
    }
    if (pairing.deviceRevision !== expectedRevision) throw new Error('409 Pairing changed; read the device state and try again')
    if (pairing.state !== 'unpaired') throw new Error('403 This device is already paired')
    const user = currentUser ?? seedUser
    const role = user.role === 'installer' ? 'installer' as const : 'homeowner' as const
    const revision = pairing.deviceRevision + 1
    pairing = {
      ...pairing, state: role === 'installer' ? 'installer_paired' : 'homeowner_paired',
      primaryAccount: { id: user.id, name: user.name, role }, deviceRevision: revision,
      cloudKnownRevision: revision, source: 'local_ble_pair', updatedAt: Date.now(), members: [],
      energyVisibleFrom: role === 'homeowner' ? Date.now() : undefined, remoteUnpair: undefined,
    }
    // The pairing account starts with a profile of its own at revision 0 — it has
    // chosen nothing, and the app says so instead of assuming (APP-HOM-32B).
    if (role === 'homeowner') claimMockEconomicSettings(user.id)
    return delay(pairing)
  },
  async remoteUnpairDevice(expectedRevision: number, _deviceId?: string): Promise<PairingStatus> {
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id && pairing.residenceId === seedSite.id) {
      throw new Error('400 Remote unpair for this Residence is not available in the offline demo')
    }
    const user = currentUser ?? seedUser
    if (user.role !== 'homeowner' || pairing.primaryAccount?.id !== user.id) throw new Error('403 Only the primary homeowner can remotely unpair')
    if (pairing.deviceRevision !== expectedRevision) throw new Error('409 Remote unpair is stale')
    if (pairing.remoteUnpair?.status === 'pending')
      throw new Error('409 Another detach command is already pending for this dongle')
    pairing = { ...pairing,
      remoteUnpair: { commandId: crypto.randomUUID(), status: 'pending', expectedRevision, kind: 'owner_unpair', requestedBy: user.id } }
    return delay(pairing)
  },
  async cancelRemoteUnpair(_deviceId?: string): Promise<PairingStatus> {
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id && pairing.residenceId === seedSite.id) {
      throw new Error('400 Remote unpair for this Residence is not available in the offline demo')
    }
    const user = currentUser ?? seedUser
    if (user.role !== 'homeowner' || pairing.primaryAccount?.id !== user.id) {
      throw new Error('403 Only the primary homeowner can cancel remote unpair')
    }
    const pending = pairing.remoteUnpair
    if (!pending) return delay(pairing)
    if (pending.status === 'applied') throw new Error('409 Remote unpair already applied on the dongle; it cannot be cancelled')
    if (pending.kind !== 'owner_unpair')
      throw new Error('403 Only an owner remote-unpair request can be cancelled from this account')
    if (pending.status === 'pending') {
      pairing = { ...pairing, remoteUnpair: { ...pending, status: 'cancelled' } }
    }
    return delay(pairing)
  },
  async forceDetachPairing(_deviceId: string, expectedRevision: number, caseId: string, reason: string): Promise<PairingStatus> {
    const user = currentUser ?? seedUser
    if (user.role !== 'admin' && user.role !== 'support') throw new Error('403 Support or Admin account required')
    if (!caseId.trim()) throw new Error('400 An open support/service case reference is required')
    if (!reason.trim()) throw new Error('400 A recorded reason is required')
    if (devices.find((d) => d.kind === 'gateway')?.status !== 'online')
      throw new Error('409 Broken-button remote unpair requires an online dongle; offline dongles need field service or replacement')
    if (pairing.deviceRevision !== expectedRevision) throw new Error('409 Force detach is stale')
    pairing = { ...pairing,
      remoteUnpair: { commandId: crypto.randomUUID(), status: 'pending', expectedRevision, kind: 'staff_force_detach', requestedBy: user.id, caseId: caseId.trim(), reason: reason.trim() } }
    return delay(pairing)
  },
  async getHelpFaq(): Promise<import('./types').HelpFaqItem[]> {
    return delay([
      { id: 'wifi', title: 'Changed Wi-Fi password', body: 'Use Devices → Reconnect to Wi-Fi. Stand at the unit, wake Bluetooth, enter the new network. No installer visit is needed for a wrong password.' },
      { id: 'unpair', title: 'Moving house or changing owner', body: 'The current owner unpairs in the app, or someone at the unit long-holds the dongle button (~10 s). The next person pairs over Bluetooth while signed in.' },
      { id: 'account', title: 'Do I need an account?', body: 'Browsing help is fine without an account. Connecting, monitoring or controlling a system, warranty and system-specific support require signing in.' },
    ])
  },
  async listSupportEnquiries(): Promise<import('./types').SupportEnquiry[]> {
    // Residence-scoped like the cloud (session siteId), never a global inbox.
    const siteId = mockSessionSiteId || seedSite.id
    return delay(supportEnquiries.filter((e) => e.siteId === siteId))
  },
  async createSupportEnquiry(subject: string, attachDiagnostics: boolean): Promise<import('./types').SupportEnquiry> {
    const text = subject.trim()
    if (!text) throw new Error('400 subject is required')
    const now = Date.now()
    const siteId = mockSessionSiteId || seedSite.id
    const enquiry: import('./types').SupportEnquiry = {
      id: `enq-${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      siteId,
      subject: text.slice(0, 200),
      status: 'open',
      attachDiagnostics,
      messages: [
        { id: `msg-${now}`, from: 'user', body: text, atMs: now },
        { id: `msg-${now + 1}`, from: 'support', body: attachDiagnostics
          ? 'Thanks — we received your enquiry. You allowed Support to review diagnostic context for this home (status, last contact, faults). An agent will reply here. This channel cannot pair or unpair a device.'
          : 'Thanks — we received your enquiry without diagnostic context. An agent will reply here. This channel cannot pair or unpair a device.', atMs: now + 1 },
      ],
      createdAtMs: now,
      updatedAtMs: now + 1,
    }
    supportEnquiries = [enquiry, ...supportEnquiries]
    return delay(enquiry)
  },
  async postSupportMessage(enquiryId: string, body: string): Promise<import('./types').SupportEnquiry> {
    const text = body.trim()
    if (!text) throw new Error('400 body is required')
    const siteId = mockSessionSiteId || seedSite.id
    const idx = supportEnquiries.findIndex((e) => e.id === enquiryId && e.siteId === siteId)
    if (idx < 0) throw new Error('404 Enquiry not found')
    const now = Date.now()
    const next = {
      ...supportEnquiries[idx],
      status: 'waiting' as const,
      messages: [...supportEnquiries[idx].messages, { id: `msg-${now}`, from: 'user' as const, body: text.slice(0, 2000), atMs: now }],
      updatedAtMs: now,
    }
    supportEnquiries = supportEnquiries.map((e, i) => (i === idx ? next : e))
    return delay(next)
  },
  async requestSupportCallback(enquiryId: string, phone?: string): Promise<import('./types').SupportEnquiry> {
    const siteId = mockSessionSiteId || seedSite.id
    const idx = supportEnquiries.findIndex((e) => e.id === enquiryId && e.siteId === siteId)
    if (idx < 0) throw new Error('404 Enquiry not found')
    const now = Date.now()
    const next = {
      ...supportEnquiries[idx],
      callback: {
        status: 'requested' as const,
        windowLabel: 'Next business day, 9 am – 5 pm AEST (estimate)',
        phone: phone?.trim() || undefined,
        requestedAtMs: now,
      },
      updatedAtMs: now,
    }
    supportEnquiries = supportEnquiries.map((e, i) => (i === idx ? next : e))
    return delay(next)
  },
  async manageHouseholdMember(action: 'invite' | 'remove', email: string, access?: 'Family' | 'Viewer', _deviceId?: string): Promise<PairingStatus> {
    const siteId = mockSessionSiteId || seedSite.id
    if (siteId !== seedSite.id && pairing.residenceId === seedSite.id) {
      throw new Error('400 Household membership for this Residence is not available in the offline demo')
    }
    const user = currentUser ?? seedUser
    if (pairing.state !== 'homeowner_paired' || pairing.primaryAccount?.id !== user.id) throw new Error('403 Primary homeowner pairing required')
    const memberEmail = email.trim().toLowerCase()
    const memberId = `u-${memberEmail}`
    pairing = { ...pairing, updatedAt: Date.now(), members: action === 'remove'
      ? pairing.members.filter((m) => m.id !== memberId && m.email !== memberEmail)
      : [...pairing.members.filter((m) => m.id !== memberId), { id: memberId, email: memberEmail, name: memberEmail, access: access ?? 'Family', status: 'active' }] }
    return delay(pairing)
  },
  async getCommissioning(_site?: string): Promise<CommissioningStep[]> {
    // The offline mock commissions against a single shared wizard; `_site` is
    // accepted for signature parity with the cloud (per-residence there).
    requireInstallerRole()
    return delay(commissioning)
  },
  async advanceCommissioning(_site?: string): Promise<CommissioningStep[]> {
    requireInstallerRole()
    commissioning = opAdvance(commissioning)
    return delay(commissioning)
  },
  async getHwsDeclaration(_site?: string): Promise<HwsDeclaration | null> {
    requireInstallerRole()
    return delay(hwsDeclaration)
  },
  async setHwsDeclaration(relayConnected: boolean, hotByHour: number | undefined, _site?: string): Promise<HwsDeclaration> {
    // APP-PAIR-16: persist the relay + comfort-deadline declaration and reflect it
    // on the compliance record (shown like other wizard data).
    requireInstallerRole()
    hwsDeclaration = opNormalizeHws(relayConnected, hotByHour)
    compliance = { ...compliance, hws: hwsDeclaration }
    return delay(hwsDeclaration)
  },
  async getDiagnostics(site?: string): Promise<DiagnosticRegister[]> {
    requireInstallerRole()
    if (!site) return delay(seedDiagnostics)
    const m = (mockMemberships[currentEmail] ?? []).find((x) => x.siteId === site)
    if (!m) return delay(seedDiagnostics)
    // Post-handoff health-only ⇒ online/fault status ONLY (APP-OWN-05B).
    if (m.access !== 'full') return delay(resStatusOnly(m.statusHint !== 'fault'))
    return delay(opRegistersFor(seedDiagnostics, ticker.snapshot().soc, m.statusHint === 'fault'))
  },
  async getCompliance(): Promise<ComplianceRecord> {
    requireInstallerRole()
    return delay(compliance)
  },
  async getFirmware(): Promise<FirmwareInfo[]> {
    requireInstallerRole()
    return delay(firmware)
  },
  async applyFirmwareUpdate(component: string): Promise<FirmwareInfo[]> {
    requireInstallerRole()
    if (firmware.some((f) => f.component === component)) {
      firmware = opApplyUpdate(firmware, component, OPS_UPDATE_TARGET)
      pushFirmwareNotif()
    }
    return delay(firmware)
  },

  // ---- firmware update lifecycle + region profile (phase 4.5) ----
  async getUpdates(site?: string): Promise<UpdatePlan> {
    assertWorkspace('/updates')
    // The ADDRESSED Residence, or the caller's own when none is given — and a 403
    // rather than a silent fallback when the caller holds no membership of it
    // (mirror of the cloud's `requireMembership`).
    const siteId = updatePlanSite(site)
    const jobs = stepUpdates(siteId)
    return delay({
      updates: jobs.map(projectUpdate),
      commissioningComplete: opIsCommissioningComplete(commissioning),
      blockingOutstanding: jobs
        .filter((job) => job.classification === 'commissioning-blocking' && job.state !== 'applied' && job.state !== 'aborted')
        .map((job) => job.jobId),
      regionProfile: mockRegionProfile(),
    })
  },
  async startUpdate(jobId: string): Promise<UpdateJobView> {
    // Homeowner-side: session Residence (cloud reads session.siteId).
    const siteId = updatePlanSite()
    const job = updatePlanFor(siteId).find((candidate) => candidate.jobId === jobId)
    if (!job) throw new Error('No such update for this Residence')
    // "Update now" overrides the WAIT, never the gate (APP-HOM-31A). A deferred
    // job on an incompletely commissioned Residence is REFUSED with the reason —
    // the same refusal `cloud/src/firmwareUpdates.startNow` produces.
    if (job.classification === 'post-commissioning' && !opIsCommissioningComplete(commissioning))
      throw new Error('Commissioning has not finished. This update is queued and starts once it does.')
    if (!projectUpdate(job).canStartNow) throw new Error(`An update in ${job.state} is not waiting to start`)
    const started: UpdateJobView = {
      ...job,
      state: 'downloading',
      progressPct: 0,
      attempts: job.attempts + 1,
      startedBy: 'homeowner',
      reason: null,
      autoStartDeadlineMs: job.autoStartDeadlineMs ?? Date.now() + UPDATE_WINDOW_MS,
      deadlineStatus: 'met',
      history: [...job.history, { atMs: Date.now(), state: 'downloading' as const, note: `dispatched (homeowner, attempt ${job.attempts + 1})` }],
    }
    updatePlans[siteId] = updatePlanFor(siteId).map((candidate) => (candidate.jobId === jobId ? started : candidate))
    return delay(projectUpdate(started))
  },
  async retryUpdate(jobId: string): Promise<UpdateJobView> {
    const siteId = updatePlanSite()
    const job = updatePlanFor(siteId).find((candidate) => candidate.jobId === jobId)
    if (!job) throw new Error('No such update for this Residence')
    if (job.state !== 'failed' && job.state !== 'rolled-back') throw new Error(`An update in ${job.state} is not awaiting a retry`)
    const retried: UpdateJobView = {
      ...job,
      state: 'scheduled',
      progressPct: null,
      autoStartDeadlineMs: Date.now() + UPDATE_WINDOW_MS,
      deadlineStatus: 'within-window',
      history: [...job.history, { atMs: Date.now(), state: 'scheduled' as const, note: `retry after ${job.state}` }],
    }
    updatePlans[siteId] = updatePlanFor(siteId).map((candidate) => (candidate.jobId === jobId ? retried : candidate))
    return delay(projectUpdate(retried))
  },
  async getFirmwareCampaigns(): Promise<FirmwareCampaignView[]> {
    requireInstallerRole()
    return delay(mockCampaigns())
  },
  // ---- Stopping a rollout (CLD-OTA-02) — a RISEN-STAFF act ----
  // Mirror of `POST /admin|support/firmware/campaigns/abort` over
  // `firmwareUpdates.abortCampaign`. Two entry points differing ONLY in the
  // workspace path they assert and the role they require, exactly as the serial
  // re-assignment pair does — the shared body takes no scope argument, because on
  // the wire the scope is the route and never a body field.
  async adminAbortFirmwareCampaign(campaignId: string, reason: string): Promise<FirmwareCampaignAbort> {
    assertWorkspace('/admin/firmware/campaigns/abort')
    // `requireRole`, NOT `requireAccountOperator`: stopping a rollout is a
    // fleet-operations action, not an account capability, so the ownership
    // approver may make it. The cloud route guards with `requireAdmin` for the
    // same reason.
    requireRole('admin', 'Back-office access required')
    return delay(mockAbortCampaign(campaignId, reason))
  },
  async supportAbortFirmwareCampaign(campaignId: string, reason: string): Promise<FirmwareCampaignAbort> {
    assertWorkspace('/support/firmware/campaigns/abort')
    requireRole('support', 'Support Portal access required')
    return delay(mockAbortCampaign(campaignId, reason))
  },
  async getFleet(): Promise<FleetSite[]> {
    requireInstallerRole()
    return delay(mockFleet())
  },
  async createResidence(label: string, address: string, nmi: string): Promise<FleetSite[]> {
    requireInstallerRole()
    const normalizedNmi = nmi.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
    if (!/^[A-Z0-9]{10,11}$/.test(normalizedNmi)) throw new Error('NMI must contain 10 or 11 letters and digits')
    if (Object.values(mockResidences).some((r) => r.nmi === normalizedNmi)) throw new Error('This NMI is already assigned to a Residence')
    const mine = mockMemberships[currentEmail] ?? (mockMemberships[currentEmail] = [])
    const siteId = newMockSiteId()
    // The offline mock is the provisional path: a stable local ID plus NMI/address
    // is usable immediately and will be reconciled by the cloud later.
    mockResidences[siteId] = { label, address, nmi: normalizedNmi, suburb: address || undefined, syncStatus: 'provisional' }
    mine.push(resNewMembership(`u-${currentEmail}`, siteId, 'installer', 'full', label, '03/07/2026', address || undefined))
    return delay(mockFleet())
  },
  // Self-contained handover (APP-OWN-05/05A/05B): the installer queues their OWN
  // device-confirmed unpair. There is no claim and nothing for anyone to accept —
  // the homeowner pairs locally afterwards, like any other homeowner. The command
  // stays PENDING until the dongle confirms it, exactly as remoteUnpairDevice does;
  // the mock has no dongle, so it stops there rather than pretending confirmation.
  // Full commissioning access therefore survives until the device confirms, and the
  // basic-status fleet row is what remains afterwards.
  async handoverResidence(site: string, ownerEmail: string): Promise<HandoverResult> {
    requireInstallerRole()
    // Contact information only, and optional: it grants no access (APP-ACC-03).
    if (ownerEmail.trim() && !/.+@.+/.test(ownerEmail.trim())) throw new Error('ownerEmail, when given, must be a valid e-mail address')
    const mine = mockMemberships[currentEmail] ?? []
    const membership = mine.find((m) => m.siteId === site && m.role === 'installer')
    if (!membership || membership.access !== 'full') throw new Error('Only a Residence in commissioning can be handed over')
    const actor = currentUser ?? seedUser
    const commandId = `handover-${actor.id}-${Date.now()}`
    // Cloud parity (handler POST /installer/handover): when there is no device
    // pairing for THIS installer on THIS site, demote the fleet row immediately.
    // Never queue installer_handover onto the seed homeowner pairing of another home.
    const installerPrimaryHere =
      pairing.residenceId === site
      && pairing.primaryAccount?.role === 'installer'
      && pairing.primaryAccount.id === actor.id
      && pairing.state === 'installer_paired'
    if (!installerPrimaryHere) {
      mockMemberships[currentEmail] = mine.map((m) =>
        m.siteId === site && m.role === 'installer' ? { ...m, access: 'health-only' as const } : m,
      )
      retainOrgFleetForCurrent(site, 'handover')
      return delay({ status: 'applied', commandId, expectedRevision: pairing.deviceRevision, fleet: mockFleet() })
    }
    if (pairing.remoteUnpair?.status === 'pending') throw new Error('409 A detach command is already pending for this dongle')
    const expectedRevision = pairing.deviceRevision
    pairing = { ...pairing,
      remoteUnpair: { commandId, status: 'pending', expectedRevision, kind: 'installer_handover', requestedBy: actor.id } }
    return delay({ status: 'pending', commandId, expectedRevision, fleet: mockFleet() })
  },
  async unpairResidence(site: string): Promise<FleetSite[]> {
    requireInstallerRole()
    // Only block on a pending detach that belongs to THIS site's installer pairing.
    if (
      pairing.remoteUnpair?.status === 'pending'
      && pairing.residenceId === site
      && pairing.primaryAccount?.role === 'installer'
    ) {
      throw new Error('409 A detach command is already pending for this dongle')
    }
    // Cloud parity: if this installer is the primary on this Residence's twin,
    // release pairing first, then demote the fleet row.
    const actor = currentUser ?? seedUser
    if (
      pairing.residenceId === site
      && pairing.primaryAccount?.role === 'installer'
      && pairing.primaryAccount.id === actor.id
      && pairing.state === 'installer_paired'
    ) {
      const revision = pairing.deviceRevision + 1
      pairing = {
        ...pairing,
        state: 'unpaired',
        primaryAccount: undefined,
        members: [],
        deviceRevision: revision,
        cloudKnownRevision: revision,
        source: 'local_app_unpair',
        updatedAt: Date.now(),
        remoteUnpair: undefined,
      }
    }
    // APP-OWN-06: demote the installer's membership to health-only (Released —
    // basic status), do not delete the fleet row. Org association is retained
    // separately; shipment inventory is never touched (APP-OWN-06A).
    const mine = mockMemberships[currentEmail] ?? []
    mockMemberships[currentEmail] = mine.map((m) =>
      m.siteId === site && m.role === 'installer' ? { ...m, access: 'health-only' as const } : m,
    )
    retainOrgFleetForCurrent(site, 'direct_unpair')
    return delay(mockFleet())
  },

  // ---- Installer Organisation, practitioners and inventory (APP-ACC-08–08L) ----

  async getInstallerOrganisation(organisationId?: string): Promise<InstallerOrganisationView> {
    const orgId = mockActingOrg(organisationId)
    const membership = activeOrgMembershipsOf(uid()).find((m) => m.organisationId === orgId) ?? null
    const isManager = membership?.role === 'manager'
    return delay({
      organisation: mockOrganisations[orgId] ?? null,
      membership,
      // The roster and its attribution are MANAGER reads (APP-ACC-08A). An
      // ordinary practitioner sees their own membership and nothing else.
      practitioners: isManager ? mockOrgMemberships.filter((m) => m.organisationId === orgId) : [],
      organisations: activeOrgMembershipsOf(uid()),
      // Invitations awaiting THIS account's acceptance — a pending row grants
      // nothing, so the subject has to be able to see it to act on it.
      invitations: mockOrgMemberships.filter((m) => m.userId === uid() && m.state === 'pending'),
    })
  },
  async linkPractitioner(userId: string, role: OrgRole, organisationId?: string): Promise<OrgMembership> {
    const orgId = mockActingOrg(organisationId)
    if (!isMockOrgManager(orgId)) throw new Error('403 Only an Installer Organisation manager may link a practitioner')
    if (!userId.trim()) throw new Error('400 An account identifier is required')
    // The cloud is given a userId; the offline mock accepts the account e-mail too
    // and derives the same `u-<email>` identifier its own accounts use. An id it
    // already minted (`u-<email>`) is passed through unchanged.
    const subject = mockUserIdFor(userId)
    // AN INVITATION, not a membership. The cloud writes `pending` and grants
    // nothing until the subject accepts (see `acceptOrgInvitation`); an active
    // membership is never demoted by a repeated invitation.
    const already = mockOrgMemberships.find((m) => m.organisationId === orgId && m.userId === subject && m.state === 'active')
    if (already) return delay(already)
    const membership: OrgMembership = { organisationId: orgId, userId: subject, role, state: 'pending', sinceMs: Date.now(), revokedAtMs: null }
    mockOrgMemberships = [...mockOrgMemberships.filter((m) => !(m.organisationId === orgId && m.userId === subject)), membership]
    pushOrgAudit({
      actorId: uid(), actorRole: 'manager', organisationId: orgId, action: 'org.practitioner.invited',
      targetType: 'practitioner', targetId: subject, serials: [], reason: '', result: 'accepted',
      detail: `invited as ${role} — pending the practitioner's acceptance`,
    })
    return delay(membership)
  },
  async acceptOrgInvitation(organisationId: string): Promise<OrgMembership> {
    requireInstallerRole()
    // Mirror of the cloud (`store.acceptOrgInvitation`): only a PENDING row can be
    // accepted, so acceptance can neither resurrect a revoked membership nor
    // invent one that was never offered — and accepting a membership that is
    // already active succeeds unchanged rather than erroring, because a second tap
    // on a stale screen is not a failure.
    const active = mockOrgMemberships.find((m) => m.organisationId === organisationId && m.userId === uid() && m.state === 'active')
    if (active) return delay(active)
    const current = mockOrgMemberships.find((m) => m.organisationId === organisationId && m.userId === uid() && m.state === 'pending')
    if (!current) throw new Error('404 You have no pending invitation from that Installer Organisation')
    const accepted: OrgMembership = { ...current, state: 'active', sinceMs: Date.now() }
    mockOrgMemberships = mockOrgMemberships.map((m) => (m === current ? accepted : m))
    pushOrgAudit({
      actorId: uid(), actorRole: 'member', organisationId, action: 'org.practitioner.accepted',
      targetType: 'practitioner', targetId: uid(), serials: [], reason: '', result: 'accepted',
      detail: `accepted the invitation as ${accepted.role}`,
    })
    return delay(accepted)
  },
  async revokePractitioner(userId: string, reason: string, organisationId?: string): Promise<{ revoked: boolean; membership: OrgMembership | null }> {
    const orgId = mockActingOrg(organisationId)
    if (!isMockOrgManager(orgId)) throw new Error('403 Only an Installer Organisation manager may revoke a practitioner')
    const subject = mockUserIdFor(userId)
    const current = mockOrgMemberships.find((m) => m.organisationId === orgId && m.userId === subject)
    if (!current) throw new Error('404 That practitioner is not linked to this organisation')
    // The roster row is RETAINED and flagged — who was ever authorized is audit
    // evidence. The organisation's INVENTORY is not mentioned here, and that
    // absence is the requirement (APP-ACC-08I): it is not touched.
    const revoked: OrgMembership = { ...current, state: 'revoked', revokedAtMs: Date.now() }
    mockOrgMemberships = mockOrgMemberships.map((m) => (m === current ? revoked : m))
    pushOrgAudit({
      actorId: uid(), actorRole: 'manager', organisationId: orgId, action: 'org.practitioner.revoked',
      targetType: 'practitioner', targetId: subject, serials: [], reason, result: 'accepted',
      detail: 'inventory and fleet access revoked; organisation inventory retained',
    })
    return delay({ revoked: true, membership: revoked })
  },
  async getOrganisationAudit(organisationId?: string): Promise<OrgAuditEvent[]> {
    const orgId = mockActingOrg(organisationId)
    return delay(mockOrgAudit.filter((e) => e.organisationId === orgId).slice(0, 50))
  },
  async getInventory(organisationId?: string): Promise<InventoryUnit[]> {
    requireInstallerRole()
    // Without a named organisation this is the union over the caller's ACTIVE
    // memberships — the cloud allows it precisely so a multi-organisation
    // practitioner can still see their units before choosing one to act for.
    if (!organisationId && !actingOrganisation) {
      if (activeOrgMembershipsOf(uid()).length === 0) ensureMockOrganisation()
      return delay(inventoryVisibleToCurrent())
    }
    const orgId = mockActingOrg(organisationId)
    return delay(Object.values(mockInventory).filter((u) => u.organisationId === orgId))
  },
  async declareInventory(serials: string[], kind?: InventoryKind, organisationId?: string): Promise<DeclarationResult> {
    const orgId = mockActingOrg(organisationId)
    const list = serials.filter((s) => typeof s === 'string' && s.trim())
    if (list.length === 0) throw new Error('400 Enter at least one serial number')
    return delay(mockDeclare(orgId, list, kind, uid()))
  },
  async withdrawInventory(serial: string, reason: string, organisationId?: string): Promise<InventoryUnit> {
    const orgId = mockActingOrg(organisationId)
    if (!isMockOrgManager(orgId)) throw new Error('403 Only an Installer Organisation manager may withdraw a declaration')
    const key = normalizeSerial(serial)
    const current = mockInventory[key]
    if (!current || current.organisationId !== orgId || current.state !== 'assigned')
      throw new Error('404 This organisation does not hold that serial')
    // The row is retained in `withdrawn` state — which is what lets another
    // organisation enter the serial afterwards.
    const next: InventoryUnit = { ...current, state: 'withdrawn', withdrawnAtMs: Date.now() }
    mockInventory[key] = next
    pushOrgAudit({
      actorId: uid(), actorRole: 'manager', organisationId: orgId, action: 'inventory.withdrawn',
      targetType: 'inventory', targetId: key, serials: [key], reason, result: 'accepted',
      detail: 'shipment-inventory association ended',
    })
    return delay(next)
  },
  async correctInventory(fromSerial: string, toSerials: string[], kind?: InventoryKind, organisationId?: string): Promise<CorrectionResult> {
    const orgId = mockActingOrg(organisationId)
    if (!isMockOrgManager(orgId)) throw new Error('403 Only an Installer Organisation manager may correct a declaration')
    const key = normalizeSerial(fromSerial)
    const current = mockInventory[key]
    if (!current || current.organisationId !== orgId || current.state !== 'assigned')
      throw new Error('404 This organisation does not hold that serial')
    const list = toSerials.filter((s) => typeof s === 'string' && s.trim())
    if (list.length === 0) throw new Error('400 Enter the corrected serial number')
    const withdrawn: InventoryUnit = { ...current, state: 'withdrawn', withdrawnAtMs: Date.now() }
    mockInventory[key] = withdrawn
    // The replacement goes through the SAME format and assignment validation, so a
    // correction can never introduce a serial an original declaration could not.
    const declared = mockDeclare(orgId, list, kind, uid())
    pushOrgAudit({
      actorId: uid(), actorRole: 'manager', organisationId: orgId, action: 'inventory.corrected',
      targetType: 'inventory', targetId: key, serials: [key, ...declared.accepted.map((a) => a.serial)],
      reason: 'correction', result: declared.rejected.length ? 'rejected' : 'accepted',
      detail: 're-validated before acceptance',
    })
    return delay({ ...declared, withdrawn })
  },
  async validateInventorySerial(serial: string, organisationId?: string): Promise<InventoryValidation> {
    const orgId = mockActingOrg(organisationId)
    const key = normalizeSerial(serial)
    if (!serialFormatValid(key)) return delay({ ok: false, reason: 'malformed', heldBy: null })
    const assignment = mockInventory[key]
    if (!assignment) return delay({ ok: false, reason: 'unknown-serial', heldBy: null })
    if (assignment.state === 'withdrawn') return delay({ ok: false, reason: 'withdrawn', heldBy: null })
    if (assignment.organisationId !== orgId) return delay({ ok: false, reason: 'other-organisation', heldBy: assignment.organisationId })
    return delay({ ok: true, assignment })
  },
  // ---- Staff serial re-assignment (SYS-FUN-17J1 / CLD-PRT-01E1) ----
  // Mirror of `POST /admin|support/inventory/reassign` over `store.reassignSerial`.
  // The two entry points differ only in their guard and in the scope stamped on
  // the audit rows — which is why the shared body takes the scope as an argument
  // instead of reading it back off the session.
  async adminReassignInventorySerial(serial: string, organisationId: string, reason: string): Promise<InventoryUnit> {
    assertWorkspace('/admin/inventory/reassign')
    // `requireRole`, NOT `requireAccountOperator`: this is an inventory correction,
    // not an account capability, so the ownership approver may make it. The cloud
    // route guards with `requireAdmin` for exactly that reason.
    requireRole('admin', 'Back-office access required')
    return delay(mockReassignSerial(serial, organisationId, reason, 'admin'))
  },
  async supportReassignInventorySerial(serial: string, organisationId: string, reason: string): Promise<InventoryUnit> {
    assertWorkspace('/support/inventory/reassign')
    requireRole('support', 'Support Portal access required')
    return delay(mockReassignSerial(serial, organisationId, reason, 'support'))
  },

  async removeFleetSite(site: string, organisationId?: string): Promise<FleetSite[]> {
    requireInstallerRole()
    const orgId = mockActingOrg(organisationId)
    const mine = mockMemberships[currentEmail] ?? []
    const membership = mine.find((m) => m.siteId === site && m.role === 'installer')
    if (membership?.access === 'full') {
      throw new Error('409 Unpair or hand off first; full commissioning rows cannot be removed this way')
    }
    const endedAssoc = endOrgFleet(orgId, site, 'organisation')
    let dropped = false
    if (membership?.access === 'health-only') {
      mockMemberships[currentEmail] = mine.filter((m) => !(m.siteId === site && m.role === 'installer'))
      dropped = true
    }
    if (!endedAssoc && !dropped) {
      throw new Error('404 This organisation holds no fleet association for that Residence')
    }
    return delay(mockFleet())
  },

  // ---- Homeowner view of retained associations (APP-OWN-18/18A) ----

  async getFleetAssociations(): Promise<FleetAssociation[]> {
    // Session Residence (multi-res), not always the seed home.
    const siteId = mockSessionSiteId || seedSite.id
    return delay(fleetAssociationsForSite(siteId))
  },
  async removeFleetAssociation(organisationId: string): Promise<FleetAssociation[]> {
    const user = currentUser ?? userFor(seedUser.email)
    if (user.role !== 'homeowner') throw new Error('403 Only the current homeowner can remove a fleet association')
    const siteId = mockSessionSiteId || seedSite.id
    if (!endOrgFleet(organisationId, siteId, 'homeowner'))
      throw new Error('404 That organisation holds no fleet association for this Residence')
    return delay(fleetAssociationsForSite(siteId))
  },

  // ---- Acting-organisation selection (APP-ACC-08L) — client-side state ----

  async getActingOrganisation(): Promise<string | null> {
    return actingOrganisation
  },
  async setActingOrganisation(organisationId: string | null): Promise<void> {
    actingOrganisation = organisationId
  },
  // ---- Phase-13b: in-app documents (APP-HOM-19 / CLD-PRT-08) ----
  async listDocuments(model?: string): Promise<DocMeta[]> {
    return delay(libListDocuments(model))
  },
  async getDocument(id: string, version?: string): Promise<DocContent> {
    const doc = libGetDocument(id, version)
    if (!doc) throw new Error('404 No such document / version')
    return delay(doc)
  },

  // ---- Phase-13b: support role — consent-gated read-only view (APP-ACC-02 / CLD-PRT-02) ----
  async supportOverview(): Promise<SupportOverview> {
    const role = (currentUser ?? userFor(seedUser.email)).role
    if (role !== 'support') throw new Error('403 Support Portal access required')
    const support = consents.find((c) => c.party === 'Support')
    if (support?.status !== 'active')
      return delay({ siteId: seedSite.id, siteName: seedSite.name, consentGranted: false })
    // Honest posture (mirror of the cloud): the UNION of the live telemetry frame's
    // faults AND the seeded OPEN faults — a healthy frame's faults:[] must never
    // mask an open seeded fault. Deduped.
    const seededOpen = seedDiagnostics.flatMap((g) => g.rows).some((r) => r.tone === 'fault') ? ['SPD end-of-life'] : []
    const faults = [...new Set([...SIM_FRAME_FAULTS, ...seededOpen])]
    return delay({
      siteId: seedSite.id, siteName: seedSite.name, consentGranted: true,
      health: {
        status: faults.length ? 'attention' : 'healthy', online: true,
        sohPct: battery.soh, warrantedSohPct: battery.warrantedSohPct, faults,
      },
    })
  },

  // ---- Stage 4.3: the onsite BLE surface (APP-INS-08) ----
  // ONE client per adapter, created lazily and kept, because the session lives on
  // it: the installer authenticates once at the start of the onsite flow and the
  // later commissioning calls ride the same session, exactly as they would over a
  // real link. The dongle behind it is the in-process scripted one — see
  // `mockDongle.ts` for what that does and does not claim.
  ble(): BleClient {
    return mockBle ?? (mockBle = createBleClient(createMockDongle().transport))
  },

}
let mockBle: BleClient | null = null

// Structural assignment intentionally exposes only the current ApiClient. Some
// legacy in-memory helpers remain below as migration fixtures, but no screen or
// client interface can call them and the corresponding server routes return 410.
export const mockApi: ApiClient = mockImplementation
